import { useState, useEffect, useRef, useCallback } from "react";
import * as Tone from "tone";
const PIXEL = 5;
const HEADER_H = 22;
const LICENSE_HEX = Math.floor(Math.random() * 0xFFFF).toString(16).toUpperCase().padStart(4,'0');
const ROUTE_COLOR_DAY   = "#d8ba74";
const ROUTE_COLOR_NIGHT = "#ffe96e";
const CARD_W = 188;
const T = { WATER: 0, LAND: 1, WILDERNESS: 2, SPARSE: 3 };
const MAX_BRIDGE = 6;
const MIN_DEP_SEP = 28; // min separation between ferry departures on same shoreline
function hash(x, y, s) {
  const n = Math.sin(x * 127.1 + y * 311.7 + s * 74.3) * 43758.545312;
  return n - Math.floor(n);
}
function smooth(x, y, scale, s) {
  const xi = Math.floor(x / scale), yi = Math.floor(y / scale);
  const xf = x / scale - xi, yf = y / scale - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  return (
    hash(xi,   yi,   s) * (1-u)*(1-v) +
    hash(xi+1, yi,   s) *  u  *(1-v) +
    hash(xi,   yi+1, s) * (1-u)* v   +
    hash(xi+1, yi+1, s) *  u  * v
  );
}
function fbm(x, y, s) {
  return (
    smooth(x, y, 28, s)       * 0.52 +
    smooth(x, y, 12, s + 100) * 0.26 +
    smooth(x, y,  4, s + 200) * 0.14 +
    smooth(x, y,  2, s + 300) * 0.08
  );
}
let melodyActive = false;
let melodyTimeout = null;
let melodyNoteHistory = [];
let melodyNodes = [];
let melodyCurrentIdx = 0;
function recordArrivalNote(midiNote) {
  melodyNoteHistory.push(midiNote);
  if (melodyNoteHistory.length > 12) melodyNoteHistory.shift();
}
function startWeatherMelody(rootMidi, isNight) {
  if (melodyActive || !audioReady) return;
  melodyActive = true;
  const dorianMidis = DORIAN.map(d => rootMidi + d + 12);
  const extDorianMidis = [...dorianMidis, ...DORIAN.map(d => rootMidi + d + 24)];
  const historyPool = melodyNoteHistory.length >= 3
    ? melodyNoteHistory.map(m => {
        return extDorianMidis.reduce((best, dm) =>
          Math.abs(dm - m) < Math.abs(best - m) ? dm : best
        , extDorianMidis[0]);
      })
    : dorianMidis;
  const melodyReverb = new Tone.Reverb({ decay: 5, wet: 0.55 });
  const melodyVol = new Tone.Volume(isNight ? -20 : -18);
  melodyReverb.generate().then(() => {
    melodyVol.connect(melodyReverb);
    melodyReverb.toDestination();
    melodyNodes.push(melodyReverb, melodyVol);
    const startMidi = historyPool[Math.floor(Math.random() * historyPool.length)];
    melodyCurrentIdx = extDorianMidis.indexOf(
      extDorianMidis.reduce((best, dm) =>
        Math.abs(dm - startMidi) < Math.abs(best - startMidi) ? dm : best
      , extDorianMidis[0])
    );
    if (melodyCurrentIdx < 0) melodyCurrentIdx = 0;
    scheduleMelodyNote(rootMidi, extDorianMidis, historyPool, melodyVol, isNight, 0);
  });
}
function scheduleMelodyNote(rootMidi, scale, historyPool, volNode, isNight, depth) {
  if (!melodyActive) return;
  const baseMs = isNight ? 2200 : 1600;
  const jitter = Math.random() * 1400;
  const interval = baseMs + jitter;
  melodyTimeout = setTimeout(() => {
    if (!melodyActive) return;
    const cadence = depth > 0 && depth % 7 === 0;
    let nextIdx;
    if (cadence) {
      const targets = [0, 4];
      const targetMidi = rootMidi + DORIAN[targets[Math.floor(Math.random() * 2)]] + 12;
      nextIdx = scale.indexOf(
        scale.reduce((best, m) => Math.abs(m - targetMidi) < Math.abs(best - targetMidi) ? m : best, scale[0])
      );
    } else {
      const steps = [];
      if (melodyCurrentIdx > 0) steps.push(...Array(5).fill(melodyCurrentIdx - 1));
      if (melodyCurrentIdx < scale.length - 1) steps.push(...Array(5).fill(melodyCurrentIdx + 1));
      if (melodyCurrentIdx > 1) steps.push(...Array(2).fill(melodyCurrentIdx - 2));
      if (melodyCurrentIdx < scale.length - 2) steps.push(...Array(2).fill(melodyCurrentIdx + 2));
      if (melodyCurrentIdx > 2) steps.push(melodyCurrentIdx - 3);
      if (melodyCurrentIdx < scale.length - 3) steps.push(melodyCurrentIdx + 3);
      if (historyPool.length > 0 && Math.random() < 0.15) {
        const borrowed = historyPool[Math.floor(Math.random() * historyPool.length)];
        nextIdx = scale.indexOf(
          scale.reduce((best, m) => Math.abs(m - borrowed) < Math.abs(best - borrowed) ? m : best, scale[0])
        );
      } else {
        nextIdx = steps.length > 0 ? steps[Math.floor(Math.random() * steps.length)] : melodyCurrentIdx;
      }
    }
    melodyCurrentIdx = Math.max(0, Math.min(scale.length - 1, nextIdx));
    const midi = scale[melodyCurrentIdx];
    if (Math.random() < 0.15) {
      scheduleMelodyNote(rootMidi, scale, historyPool, volNode, isNight, depth + 1);
      return;
    }
    try {
      const freq = midiToFreq(midi);
      const osc = new Tone.Oscillator({ frequency: freq, type: 'sine' });
      const noteVol = new Tone.Volume(-3 + (Math.random() * 2 - 1));
      const env = new Tone.AmplitudeEnvelope({
        attack: 0.18,
        decay: 0.4,
        sustain: 0.3,
        release: 1.8,
      });
      osc.chain(env, noteVol, volNode);
      osc.start();
      env.triggerAttack();
      const noteDur = interval * 0.55;
      setTimeout(() => {
        try { if (!env.disposed) env.triggerRelease(); } catch(e) {}
        setTimeout(() => {
          [osc, env, noteVol].forEach(n => {
            try { if (!n.disposed) { n.stop?.(); n.dispose(); } } catch(e) {}
          });
        }, 2500);
      }, noteDur);
      melodyNodes.push(osc, env, noteVol);
    } catch(e) {}
    scheduleMelodyNote(rootMidi, scale, historyPool, volNode, isNight, depth + 1);
  }, interval);
}
function stopWeatherMelody() {
  melodyActive = false;
  if (melodyTimeout) { clearTimeout(melodyTimeout); melodyTimeout = null; }
  melodyNodes.forEach(n => {
    try { if (!n.disposed) { n.stop?.(); n.dispose(); } } catch(e) {}
  });
  melodyNodes = [];
}
const DORIAN = [0, 2, 3, 5, 7, 9, 10, 12];
const TERRAIN_DEGREES = {
  0: [4, 6, 7],
  1: [0, 2, 4],
  2: [1, 3, 5],
  3: [6, 7, 3],
};
function mapRootNote(seed) {
  const roots = [38, 40, 41, 43, 45, 46, 48];
  return roots[Math.abs(seed | 0) % roots.length];
}
function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}
function pickArrivalNote(terrain, rootMidi) {
  const preferred = TERRAIN_DEGREES[terrain] || TERRAIN_DEGREES[1];
  const pool = Math.random() < 0.30
    ? DORIAN.filter((_, i) => !preferred.includes(i))
    : preferred.map(i => DORIAN[i]);
  const semitones = pool[Math.floor(Math.random() * pool.length)];
  const octaveShift = Math.random() < 0.5 ? 12 : 24;
  return midiToFreq(rootMidi + semitones + octaveShift);
}
let audioReady = false;
let droneNodes = null;
async function initAudio(seed, isNight) {
  if (audioReady) return;
  await Tone.start();
  audioReady = true;
  buildDrone(seed, isNight);
}
let droneFilter = null;
let droneMasterGain = null;
let droneBreathLfo = null;
let droneGrit = null;
function buildDrone(seed, isNight) {
  if (droneNodes) {
    droneNodes.forEach(n => {
      try { if (!n.disposed) n.dispose(); } catch(e) {}
    });
    droneNodes = null;
    droneFilter = null;
    droneMasterGain = null;
    droneBreathLfo = null;
    droneGrit = null;
  }
  const root = mapRootNote(seed);
  const fifth = root + 7;
  const octave = root + 12;
  const reverb = new Tone.Reverb({ decay: 8, wet: 0.7 }).toDestination();
  const breathGain = new Tone.Gain(1).connect(reverb);
  const masterVol = new Tone.Volume(-22).connect(breathGain);
  const grit = new Tone.BitCrusher({ bits: 8 });
  const gritWet = new Tone.CrossFade(0);
  gritWet.connect(masterVol);
  grit.connect(gritWet.b);
  droneGrit = gritWet;
  const initFreq = isNight ? 600 : 1200;
  const filter = new Tone.Filter({ frequency: initFreq, type: "lowpass", rolloff: -12 });
  filter.connect(gritWet.a);
  filter.connect(grit);
  const filterLfo = new Tone.LFO({
    frequency: 0.05,
    min: isNight ? 400 : 800,
    max: isNight ? 900 : 1800,
  });
  filterLfo.connect(filter.frequency);
  filterLfo.start();
  let breathLfo = null;
  if (isNight) {
    breathLfo = new Tone.LFO({ frequency: 0.08, min: 0.5, max: 1.0 });
    breathLfo.connect(breathGain.gain);
    breathLfo.start();
  }
  const oscs = [
    { freq: midiToFreq(root),   detune:  0,  vol: -6  },
    { freq: midiToFreq(fifth),  detune:  4,  vol: -9  },
    { freq: midiToFreq(octave), detune: -3,  vol: -12 },
  ].map(({ freq, detune, vol }) => {
    const osc = new Tone.Oscillator({ frequency: freq, type: "sine", detune });
    const gain = new Tone.Volume(vol).connect(filter);
    osc.connect(gain);
    osc.start();
    return osc;
  });
  droneFilter = filter;
  droneBreathLfo = breathLfo;
  droneNodes = [reverb, breathGain, masterVol, grit, gritWet, filter, filterLfo, breathLfo, ...oscs].filter(Boolean);
}
function updateDroneNight(isNight) {
  if (!audioReady) return;
  try {
    if (droneFilter && !droneFilter.disposed) {
      droneFilter.frequency.rampTo(isNight ? 600 : 1200, 60);
    }
  } catch(e) {}
}
function updateDroneGrit(blend) {
  if (!audioReady || !droneGrit || droneGrit.disposed) return;
  try {
    const gritAmt = blend * 0.06;
    droneGrit.fade.rampTo(gritAmt, 30);
  } catch(e) {}
}
let activeTones = [];
function playArrival(terrain, rootMidi, gx, gy) {
  if (!audioReady) return;
  const freq = pickArrivalNote(terrain, rootMidi);
  try {
    const layerDb = -14 - Math.min(activeTones.length * 2, 12);
    const entry = { osc: null, gx: gx ?? -1, gy: gy ?? -1, baseFreq: freq };
    activeTones.push(entry);
    const env = new Tone.AmplitudeEnvelope({
      attack: 0.08,
      decay: 20.0,
      sustain: 0.04,
      release: 3.5,
    });
    const vol = new Tone.Volume(layerDb);
    const reverb = new Tone.Reverb({ decay: 6, wet: 0.6 });
    const osc = new Tone.Oscillator({ frequency: freq, type: "sine" });
    entry.osc = osc;
    reverb.generate().then(() => {
      if (osc.disposed) return;
      osc.chain(env, vol, reverb, Tone.Destination);
      osc.start();
      env.triggerAttack();
      setTimeout(() => {
        try { if (!env.disposed) env.triggerRelease(); } catch(e) {}
      }, 20500);
      setTimeout(() => {
        activeTones = activeTones.filter(t => t !== entry);
        [osc, env, vol, reverb].forEach(n => {
          try { if (!n.disposed) { n.stop?.(); n.dispose(); } } catch(e) {}
        });
      }, 24500);
    });
  } catch(e) { activeTones = activeTones.filter(t => t.osc !== null || t.gx !== -1); }
}
function detuneNearby(cx, cy) {
  if (!audioReady) return;
  activeTones.forEach(({ osc, gx, gy, baseFreq }) => {
    if (!osc || osc.disposed) return;
    const dx = (gx - cx) * 5;
    const dy = (gy - cy) * 5;
    const dist = Math.sqrt(dx*dx + dy*dy);
    const maxDist = 450;
    const intensity = Math.max(0, 1 - dist / maxDist);
    if (intensity < 0.02) return;
    try {
      const centsShift = -(intensity * 38);
      osc.detune.rampTo(centsShift, 0.3);
      setTimeout(() => {
        try { if (!osc.disposed) osc.detune.rampTo(0, 3.5); } catch(e) {}
      }, 400);
      const wobbleDepth = intensity * 6;
      const wobbleRate  = 3 + intensity * 5;
      const wobbleDur   = 1.5 + intensity * 2.5;
      const lfo = new Tone.LFO({
        frequency: wobbleRate,
        min: -wobbleDepth,
        max:  wobbleDepth,
        type: 'sine',
      });
      const lfoGain = new Tone.Gain(1);
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      lfo.start();
      lfoGain.gain.rampTo(0, wobbleDur);
      setTimeout(() => {
        try { lfo.stop(); lfo.dispose(); lfoGain.dispose(); } catch(e) {}
      }, (wobbleDur + 0.5) * 1000);
    } catch(e) {}
  });
}
function chorusNearby(_cx, _cy) {  }
const REPORT_EVENTS = [
  {
    id: "stalled_vehicle",
    icon: "⊡", label: "Stalled vehicle",
    q1: { prompt: "hazard lights", options: ["on", "off"] },
    q2: { prompt: "position", options: ["travel lane", "shoulder", "intersection", "unknown"] },
  },
  {
    id: "road_closure",
    icon: "╫", label: "Road closure",
    q1: { prompt: "passable", options: ["blocked", "single lane"] },
    q2: { prompt: "cause", options: ["construction", "incident", "flooding", "no signage"] },
  },
  {
    id: "object_in_road",
    icon: "◈", label: "Object in road",
    q1: { prompt: "hazard level", options: ["minor", "significant"] },
    q2: { prompt: "position", options: ["full block", "partial", "shoulder", "unclear"] },
  },
  {
    id: "reduced_visibility",
    icon: "░", label: "Reduced visibility",
    q1: { prompt: "severity", options: ["moderate", "severe"] },
    q2: { prompt: "apparent cause", options: ["fog", "smoke", "glare", "precipitation", "unknown"] },
  },
  {
    id: "unusual_animal",
    icon: "◬", label: "Unusual animal behavior",
    q1: { prompt: "still present", options: ["yes", "no"] },
    q2: { prompt: "scale", options: ["single", "group", "many", "indeterminate"] },
  },
  {
    id: "structure_absent",
    icon: "▣", label: "Structure no longer present",
    q1: { prompt: "absence", options: ["partial", "total"] },
    q2: { prompt: "last verified", options: ["today", "recently", "some time ago", "unknown"] },
  },
  {
    id: "sound_no_source",
    icon: "⊕", label: "Sound without visible source",
    q1: { prompt: "ongoing", options: ["yes", "no"] },
    q2: { prompt: "character", options: ["periodic", "continuous", "irregular", "subsiding"] },
  },
  {
    id: "incorrect_signage",
    icon: "⊠", label: "Incorrect signage",
    q1: { prompt: "type", options: ["directional", "regulatory"] },
    q2: { prompt: "discrepancy", options: ["wrong destination", "wrong distance", "wrong restriction", "illegible"] },
  },
  {
    id: "condition_new",
    icon: "⌖", label: "Condition not previously here",
    q1: { prompt: "severity", options: ["notable", "serious"] },
    q2: { prompt: "first noticed", options: ["just now", "earlier today", "since last visit", "unknown"] },
  },
];
async function callAPIReport(eventType, notes, isNight) {
  const notesText = (notes?.trim() || '').replace(/[`]/g, "'").slice(0, 200) || 'no additional notes';
  const timeNote = isNight ? ' Filed at an odd hour.' : '';
  const sys = `You write a single fragment for a fictional atlas. Output only the fragment, nothing else — no quotes, no labels, no punctuation beyond what the fragment needs.`;
  const usr = `Event: ${eventType}
Notes: ${notesText}
Write one fragment, 3–14 words. Voice: incident log, dispatch record, field note. Terse. Specific. Strangeness through precision, not statement. Some events this system was not designed to classify. File them anyway.${timeNote}`;
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6', max_tokens: 60,
      system: sys,
      messages: [{ role: 'user', content: usr }],
    }),
  });
  return await safeParseAPI(res);
}
function buildMap(seed, cols, rows) {
  const raw = Array.from({ length: rows }, (_, y) =>
    Array.from({ length: cols }, (_, x) => {
      const v = fbm(x, y, seed);
      if (v < 0.41) return T.WATER;
      if (v < 0.62) return T.LAND;
      if (v < 0.78) return T.WILDERNESS;
      return T.SPARSE;
    })
  );
  const isCoastalWater = new Uint8Array(cols * rows);
  { const q = [];
    for (let y=0;y<rows;y++) for (let x=0;x<cols;x++) {
      if (raw[y][x]===T.WATER&&(x===0||x===cols-1||y===0||y===rows-1)) {
        isCoastalWater[y*cols+x]=1; q.push(y*cols+x);
      }
    }
    for (let qi=0;qi<q.length;qi++) {
      const idx=q[qi],cx=idx%cols,cy=(idx/cols)|0;
      for (let d=0;d<4;d++) {
        const nx2=cx+[-1,1,0,0][d],ny2=cy+[0,0,-1,1][d];
        if (nx2<0||nx2>=cols||ny2<0||ny2>=rows) continue;
        const ni=ny2*cols+nx2;
        if (raw[ny2][nx2]===T.WATER&&!isCoastalWater[ni]) { isCoastalWater[ni]=1; q.push(ni); }
      }
    }
  }
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (raw[y][x] !== T.LAND) continue;
      let distCoast = 99;
      for (let dy=-9;dy<=9;dy++) for (let dx=-9;dx<=9;dx++) {
        const cx=x+dx,cy=y+dy;
        if (cx<0||cx>=cols||cy<0||cy>=rows) continue;
        if (isCoastalWater[cy*cols+cx]) distCoast=Math.min(distCoast,Math.abs(dx)+Math.abs(dy));
      }
      if (distCoast > 8) continue;
      const threshold = 0.39 + distCoast * 0.03;
      const rx = x*0.866 - y*0.5, ry = x*0.5 + y*0.866;
      if (fbm(rx, ry, seed+31) <= threshold) continue;
      let nearWild = false;
      for (let dy=-9;dy<=9;dy++) for (let dx=-9;dx<=9;dx++) {
        const cx=x+dx,cy=y+dy;
        if (cx<0||cx>=cols||cy<0||cy>=rows) continue;
        if (raw[cy][cx]===T.WILDERNESS) { nearWild=true; break; }
      }
      if (nearWild) raw[y][x]=T.WILDERNESS;
    }
  }
  return raw;
}
function buildDistField(cells, cols, rows) {
  const dist = new Float32Array(cols * rows).fill(Infinity);
  const queue = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      if (cells[y][x] === T.WATER) {
        dist[y * cols + x] = 0;
        queue.push(y * cols + x);
      }
  const dx8 = [1,-1,0,0,1,1,-1,-1];
  const dy8 = [0,0,1,-1,1,-1,1,-1];
  const c8  = [1,1,1,1,1.414,1.414,1.414,1.414];
  let head = 0;
  while (head < queue.length) {
    const idx = queue[head++];
    const cx = idx % cols, cy = (idx / cols) | 0;
    const cd = dist[idx];
    for (let d = 0; d < 8; d++) {
      const nx = cx + dx8[d], ny = cy + dy8[d];
      if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue;
      const ni = ny * cols + nx, nd = cd + c8[d];
      if (nd < dist[ni]) { dist[ni] = nd; queue.push(ni); }
    }
  }
  return dist;
}
function distGradient(x, y, dist, cols, rows) {
  const get = (cx, cy) => {
    if (cx < 0 || cx >= cols || cy < 0 || cy >= rows) return dist[y * cols + x];
    return dist[cy * cols + cx];
  };
  const gx = get(x+1,y) - get(x-1,y);
  const gy = get(x,y+1) - get(x,y-1);
  const len = Math.sqrt(gx*gx + gy*gy);
  if (len < 0.001) return { gx: 0, gy: 0 };
  return { gx: -gx/len, gy: -gy/len };
}
function flowWalk(start, end, cells, dist, cols, rows, seed, goalPullMax, maxSteps, noiseWeight = 0.35, stopDist = 3) {
  const pts = [{ ...start, bridge: false }];
  let x = start.x, y = start.y;
  let angle = Math.atan2(end.y - start.y, end.x - start.x);
  let bridgeCount = 0;
  let lastDx = 0, lastDy = 0;
  const EDGE_MARGIN = 0;
  const blendAngle = (base, target, w) => {
    let d = target - base;
    while (d >  Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return base + d * w;
  };
  let totalBridgeCells = 0;
  const MAX_TOTAL_BRIDGE = MAX_BRIDGE * 2;
  for (let step = 0; step < maxSteps; step++) {
    const distToGoal = Math.hypot(x - end.x, y - end.y);
    if (distToGoal < stopDist) break;
    if (x <= EDGE_MARGIN || x >= cols - 1 - EDGE_MARGIN ||
        y <= EDGE_MARGIN || y >= rows - 1 - EDGE_MARGIN) break;
    const nx1 = smooth(x, y, 1.5, seed + 500) - 0.5;
    const ny1 = smooth(x, y, 1.5, seed + 700) - 0.5;
    const nx2 = smooth(x, y, 0.8, seed + 900) - 0.5;
    const ny2 = smooth(x, y, 0.8, seed + 1100) - 0.5;
    const nxc = nx1 * 0.7 + nx2 * 0.3;
    const nyc = ny1 * 0.7 + ny2 * 0.3;
    const noiseAngle = (nxc === 0 && nyc === 0) ? angle : Math.atan2(nyc, nxc);
    const goalAngle = Math.atan2(end.y - y, end.x - x);
    const goalPull  = goalPullMax * 0.5 + (1 - Math.min(distToGoal, 60) / 60) * goalPullMax * 0.5;
    let a = angle;
    a = blendAngle(a, noiseAngle, noiseWeight);
    a = blendAngle(a, goalAngle,  goalPull);
    angle = a;
    const neighbors = [
      {nx:x+1,ny:y  },{nx:x-1,ny:y  },{nx:x,  ny:y+1},{nx:x,  ny:y-1},
      {nx:x+1,ny:y+1},{nx:x+1,ny:y-1},{nx:x-1,ny:y+1},{nx:x-1,ny:y-1},
    ].filter(n => n.nx >= 0 && n.nx < cols && n.ny >= 0 && n.ny < rows);
    const landCands = neighbors.filter(n => cells[n.ny][n.nx] !== T.WATER);
    const onWater   = cells[y][x] === T.WATER;
    let pool = landCands;
    if (landCands.length === 0 && bridgeCount < MAX_BRIDGE && totalBridgeCells < MAX_TOTAL_BRIDGE) pool = neighbors;
    else if (onWater && bridgeCount < MAX_BRIDGE && totalBridgeCells < MAX_TOTAL_BRIDGE) pool = neighbors;
    if (pool.length === 0) break;
    const scored = pool
      .map(n => {
        let diff = Math.abs(Math.atan2(n.ny - y, n.nx - x) - angle);
        if (diff > Math.PI) diff = 2 * Math.PI - diff;
        if (n.nx - x === lastDx && n.ny - y === lastDy) diff += 0.6;
        if (onWater && cells[n.ny][n.nx] !== T.WATER) diff -= 1.2;
        return { ...n, diff };
      })
      .sort((a, b) => a.diff - b.diff);
    const pick = !onWater && scored.length > 1 && hash(x + step, y, seed * 1.3) < noiseWeight * 0.6
      ? scored[1] : scored[0];
    const prevX = x, prevY = y;
    x = pick.nx; y = pick.ny;
    lastDx = x - prevX; lastDy = y - prevY;
    const isBridge = cells[y][x] === T.WATER;
    bridgeCount = isBridge ? bridgeCount + 1 : 0;
    if (isBridge) totalBridgeCells++;
    if (bridgeCount > MAX_BRIDGE || totalBridgeCells > MAX_TOTAL_BRIDGE) break;
    pts.push({ x, y, bridge: isBridge });
  }
  while (pts.length > 1 && pts[pts.length - 1].bridge) pts.pop();
  return pts;
}
function nearestLand(tx, ty, cells, cols, rows) {
  for (let r = 0; r < Math.max(cols, rows); r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const cx = Math.max(0, Math.min(cols-1, tx+dx));
        const cy = Math.max(0, Math.min(rows-1, ty+dy));
        if (cells[cy][cx] !== T.WATER) return { x: cx, y: cy };
      }
    }
  }
  return null;
}
function buildRoads(seed, cells, dist, cols, rows) {
  const roads = [];
  let maxDist = 1;
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      if (cells[y][x] !== T.WATER && dist[y*cols+x] < Infinity)
        maxDist = Math.max(maxDist, dist[y*cols+x]);
  const landCells = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      if (cells[y][x] !== T.WATER) landCells.push({ x, y });
  landCells.sort((a, b) => hash(a.x, a.y, seed*2.1) - hash(b.x, b.y, seed*2.1));
  const waypoints = [];
  const wpSeen = new Set();
  const addWP = (x, y) => {
    const key = `${x},${y}`;
    if (wpSeen.has(key) || cells[y][x] === T.WATER) return;
    wpSeen.add(key); waypoints.push({ x, y });
  };
  let coastN = 0, ridgeN = 0;
  for (const { x, y } of landCells) {
    const norm = dist[y*cols+x] / maxDist;
    if (coastN < 12 && norm < 0.4 && cells[y][x] === T.LAND) {
      if (hash(x, y, seed+77) < Math.pow(1 - norm/0.4, 2) * 0.5) { addWP(x, y); coastN++; }
    }
    if (ridgeN < 4 && norm > 0.6) {
      if (hash(x, y, seed+133) < Math.pow((norm-0.6)/0.4, 2) * 0.2) { addWP(x, y); ridgeN++; }
    }
    if (coastN >= 12 && ridgeN >= 4) break;
  }
  const snapLand = (tx, ty) => {
    for (let r = 0; r <= 15; r++)
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          const cx = Math.max(0, Math.min(cols-1, tx+dx));
          const cy = Math.max(0, Math.min(rows-1, ty+dy));
          if (cells[cy][cx] !== T.WATER) return { x:cx, y:cy };
        }
    return null;
  };
  const rng = (a, b) => hash(a, b, seed*3.7+11);
  for (const pt of [
    snapLand(0, Math.floor(rng(10,0)*rows)),
    snapLand(cols-1, Math.floor(rng(10,1)*rows)),
    snapLand(Math.floor(rng(10,2)*cols), 0),
    snapLand(Math.floor(rng(10,3)*cols), rows-1),
  ]) { if (pt) addWP(pt.x, pt.y); }
  if (waypoints.length < 3) return roads;
  const makeSpine = (wps, spineIdx) => {
    if (wps.length < 2) return;
    for (let i = 0; i < wps.length - 1; i++) {
      const s = wps[i], e = wps[i+1];
      const d = Math.hypot(e.x-s.x, e.y-s.y);
      if (d < 20) continue;
      const steps = Math.min(Math.ceil(d * 4), 500);
      const pts = flowWalk(s, e, cells, dist, cols, rows,
        seed + spineIdx*500 + i*37, 0.45, steps);
      if (pts.length > 3) roads.push(pts);
    }
  };
  const sorted = [...waypoints].sort((a,b) => (a.x + a.y*1.3) - (b.x + b.y*1.3));
  const groupA = sorted.filter((_,i) => i % 3 === 0);
  const groupB = sorted.filter((_,i) => i % 3 === 1);
  const groupC = sorted.filter((_,i) => i % 3 === 2);
  const closestCross = (src, dst) => {
    let best = null, bestD = Infinity;
    for (const a of src) for (const b of dst) {
      const d = Math.hypot(a.x-b.x, a.y-b.y);
      if (d < bestD) { bestD = d; best = a; }
    }
    return best;
  };
  const sharedAB = closestCross(groupA, groupB);
  const sharedBC = closestCross(groupB, groupC);
  const sharedCA = closestCross(groupC, groupA);
  if (sharedAB) groupB.push(sharedAB);
  if (sharedBC) groupC.push(sharedBC);
  if (sharedCA) groupA.push(sharedCA);
  makeSpine(groupA.sort((a,b) => a.x - b.x),            0);
  makeSpine(groupB.sort((a,b) => a.y - b.y),            1);
  makeSpine(groupC.sort((a,b) => (a.x+a.y)-(b.x+b.y)), 2);
  const GAP_MAX = 40;
  const endpoints = [];
  for (let ri = 0; ri < roads.length; ri++) {
    const pts = roads[ri];
    if (pts.length < 2) continue;
    endpoints.push({ pt: pts[0],            ri });
    endpoints.push({ pt: pts[pts.length-1], ri });
  }
  const used = new Set();
  const pairs = [];
  for (let i = 0; i < endpoints.length; i++)
    for (let j = i + 1; j < endpoints.length; j++) {
      if (endpoints[i].ri === endpoints[j].ri) continue;
      const d = Math.hypot(endpoints[i].pt.x - endpoints[j].pt.x,
                           endpoints[i].pt.y - endpoints[j].pt.y);
      if (d >= 4 && d <= GAP_MAX) pairs.push({ i, j, d });
    }
  pairs.sort((a, b) => a.d - b.d);
  const gapCloserStart = roads.length;
  const waterFrac = (a, b) => {
    const steps = Math.max(4, Math.ceil(Math.hypot(a.x - b.x, a.y - b.y)));
    let water = 0;
    for (let s = 0; s <= steps; s++) {
      const x = Math.round(a.x + (b.x - a.x) * s / steps);
      const y = Math.round(a.y + (b.y - a.y) * s / steps);
      if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
      if (cells[y][x] === T.WATER) water++;
    }
    return water / (steps + 1);
  };
  for (const { i, j, d } of pairs) {
    if (used.has(i) || used.has(j)) continue;
    const ea = endpoints[i].pt, eb = endpoints[j].pt;
    if (cells[ea.y]?.[ea.x] === T.WATER || cells[eb.y]?.[eb.x] === T.WATER) continue;
    if (waterFrac(ea, eb) > 0.3) continue;
    used.add(i); used.add(j);
    const noiseW = Math.min(0.08 + (d / GAP_MAX) * 0.10, 0.18);
    const steps = Math.min(Math.ceil(d * 5), 250);
    const tgt = endpoints[j].pt;
    const raw = flowWalk(endpoints[i].pt, tgt, cells, dist, cols, rows,
      seed + i*73 + j*31, 0.75, steps, noiseW, 1);
    if (raw.length < 2) continue;
    let gcIdx = 0, gcD = Infinity;
    for (let k = 0; k < raw.length; k++) {
      const dd = Math.hypot(raw[k].x - tgt.x, raw[k].y - tgt.y);
      if (dd < gcD) { gcD = dd; gcIdx = k; }
    }
    raw.length = gcIdx + 1;
    if (gcD > 1) raw.push({ x: tgt.x, y: tgt.y, bridge: false });
    if (raw.length > 2) roads.push(raw);
  }
  return { roads, gapCloserStart };
}
function realStartTime() {
  const now = new Date();
  const raw = now.getHours() * 60 + now.getMinutes();
  return Math.round(raw / 5) * 5 % 1440;
}
function formatTime(totalMins) {
  const m = ((totalMins % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60), min = m % 60;
  return { str: `${String(h).padStart(2,"0")}:${String(min).padStart(2,"0")}`, h, min };
}
const PAL_DAY = {
  water:"#8ab4b8", land:"#d4ddd4", coast:"#a4c4c0",
  veg:"#6a9890", wildBase:"#adc8bc",
  sparse:"#c0ccc0", sparseAlt:"#b8c8b8",
  road:"#41684c", bridge:"#799a79",
  bg:"#d4ddd4", timeBox:"rgba(20,32,28,0.75)", isNight:false,
};
const PAL_NIGHT = {
  water:"#304c50", land:"#1e2a1e", coast:"#3a5450",
  veg:"#1e3430", wildBase:"#264038",
  sparse:"#2a342a", sparseAlt:"#283028",
  road:"#7ab0a0", bridge:"#51765e",
  bg:"#1e2a1e", timeBox:"rgba(0,0,0,0.72)", isNight:true,
};
const ROAD_FRESH_DAY   = '#88ad43';
const ROAD_FRESH_NIGHT = '#a8d38d';
const ROAD_FADE_MS     = 300000;
function spurColor(birthTime, palRoad, isNight, now) {
  const age = Math.min((now - birthTime) / ROAD_FADE_MS, 1);
  const fresh = isNight ? ROAD_FRESH_NIGHT : ROAD_FRESH_DAY;
  return lerpHex(fresh, palRoad, age);
}
function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerpHex(a, b, t) {
  const [ar,ag,ab] = hexToRgb(a), [br,bg,bb] = hexToRgb(b);
  const r = Math.round(ar + (br-ar)*t);
  const g = Math.round(ag + (bg-ag)*t);
  const bl = Math.round(ab + (bb-ab)*t);
  return '#' + [r,g,bl].map(v => v.toString(16).padStart(2,'0')).join('');
}
function blendFactor(totalMins) {
  const m = ((totalMins % 1440) + 1440) % 1440;
  if (m >= 19*60 || m < 5*60) return 1;
  if (m >= 6*60 && m < 18*60) return 0;
  if (m >= 5*60 && m < 6*60) return 1 - (m - 5*60) / 60;
  return (m - 18*60) / 60;
}
function getPalette(totalMins) {
  const t = blendFactor(totalMins);
  if (t === 0) return PAL_DAY;
  if (t === 1) return PAL_NIGHT;
  const lerp = k => lerpHex(PAL_DAY[k], PAL_NIGHT[k], t);
  return {
    water:lerp('water'), land:lerp('land'), coast:lerp('coast'),
    veg:lerp('veg'), wildBase:lerp('wildBase'),
    sparse:lerp('sparse'), sparseAlt:lerp('sparseAlt'),
    road:lerp('road'), bridge:lerp('bridge'),
    bg:lerp('bg'),
    timeBox: t > 0.5 ? PAL_NIGHT.timeBox : PAL_DAY.timeBox,
    isNight: t > 0.5,
  };
}
function isNightTime(totalMins) {
  return blendFactor(totalMins) > 0.5;
}
function drawMap(ctx, cells, roads, grownSegs, ferrySegs, pal, cols, rows, route, roadPulse, now, deepWildGrid, gcDecay, gcStart, abandonedRids, spineGrid) {
  ctx.imageSmoothingEnabled = false;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const t = cells[y][x];
      const px = x*PIXEL, py = y*PIXEL;
      if      (t === T.WATER)      ctx.fillStyle = pal.water;
      else if (t === T.LAND)       ctx.fillStyle = pal.land;
      else if (t === T.WILDERNESS) ctx.fillStyle = pal.wildBase;
      else ctx.fillStyle = (x+y)%2===0 ? pal.sparse : pal.sparseAlt;
      ctx.fillRect(px, py, PIXEL, PIXEL);
      if (t === T.WILDERNESS && (x+y)%2===0) {
        ctx.fillStyle = pal.veg;
        const d = Math.max(1, (PIXEL*0.5)|0);
        ctx.fillRect(px+((PIXEL*0.25)|0), py+((PIXEL*0.25)|0), d, d);
      }
    }
  }
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (cells[y][x] === T.WATER) continue;
      if (
        (y>0      && cells[y-1][x]===T.WATER) ||
        (y<rows-1 && cells[y+1][x]===T.WATER) ||
        (x>0      && cells[y][x-1]===T.WATER) ||
        (x<cols-1 && cells[y][x+1]===T.WATER)
      ) { ctx.fillStyle = pal.coast; ctx.fillRect(x*PIXEL, y*PIXEL, PIXEL, PIXEL); }
    }
  }
  roads.forEach((pts, si) => {
    if (!Array.isArray(pts)) return;
    const isPulse = roadPulse && si === roadPulse.segIdx;
    for (const pt of pts) {
      let roadColor = isPulse
        ? (pal?.isNight ? '#c8d8c0' : '#a0c8a0')
        : pt.bridge ? pal.bridge : pal.road;
      if (!isPulse && !pt.bridge && gcDecay && gcStart >= 0 && si >= gcStart) {
        const dec = gcDecay[si - gcStart] || 0;
        if (dec > 0 && !(spineGrid && spineGrid[pt.y * cols + pt.x])) {
          const t = cells[pt.y]?.[pt.x];
          const terrainColor = t === T.WATER ? pal.water
            : t === T.WILDERNESS ? pal.wildBase
            : t === T.SPARSE ? ((pt.x+pt.y)%2===0 ? pal.sparse : pal.sparseAlt)
            : pal.land;
          roadColor = lerpHex(roadColor, terrainColor, Math.min(1, dec));
        }
      }
      ctx.fillStyle = roadColor;
      ctx.fillRect(pt.x*PIXEL, pt.y*PIXEL, PIXEL, PIXEL);
    }
  });
  if (grownSegs && now) {
    for (const seg of grownSegs) {
      const base = spurColor(seg.birthTime, pal.road, pal.isNight, now);
      for (const pt of seg.pts) {
        let color = base;
        if (seg.decay > 0 && !(spineGrid && spineGrid[pt.y * cols + pt.x])) {
          const t = cells[pt.y]?.[pt.x];
          const terrainColor = t === T.WATER ? pal.water
            : t === T.WILDERNESS ? pal.wildBase
            : t === T.SPARSE ? ((pt.x+pt.y)%2===0 ? pal.sparse : pal.sparseAlt)
            : pal.land;
          color = lerpHex(base, terrainColor, Math.min(1, seg.decay));
        }
        ctx.fillStyle = color;
        ctx.fillRect(pt.x*PIXEL, pt.y*PIXEL, PIXEL, PIXEL);
      }
    }
  }
  if (ferrySegs) {
    for (const ferry of ferrySegs) {
      if (!ferry.revealed || ferry.revealed === 0) continue;
      const baseFerryColor = pal.isNight ? '#4a7880' : '#5a8484';
      for (let di = 0; di < ferry.revealed && di < ferry.pts.length; di++) {
        if (di % 2 === 0) {
          const pt = ferry.pts[di];
          let ferryColor = baseFerryColor;
          if ((ferry.decay || 0) > 0 && !(spineGrid && spineGrid[pt.y * cols + pt.x])) {
            const t2 = cells[pt.y]?.[pt.x];
            const tc = t2 === T.WATER ? pal.water : t2 === T.WILDERNESS ? pal.wildBase
              : t2 === T.SPARSE ? ((pt.x+pt.y)%2===0 ? pal.sparse : pal.sparseAlt) : pal.land;
            ferryColor = lerpHex(baseFerryColor, tc, Math.min(1, ferry.decay));
          }
          ctx.fillStyle = ferryColor;
          ctx.fillRect(pt.x*PIXEL, pt.y*PIXEL, PIXEL, PIXEL);
        }
      }
    }
  }
  if (deepWildGrid) {
    for (let i = 0; i < deepWildGrid.length; i++) {
      if (!deepWildGrid[i]) continue;
      const gx = i % cols, gy = (i / cols) | 0;
      ctx.fillStyle = 'rgba(255,80,80,0.45)';
      ctx.fillRect(gx * PIXEL, gy * PIXEL, PIXEL, PIXEL);
    }
  }
  if (route) {
    ctx.fillStyle = pal?.isNight ? ROUTE_COLOR_NIGHT : ROUTE_COLOR_DAY;
    for (const pt of route.path)
      ctx.fillRect(pt.x*PIXEL, pt.y*PIXEL, PIXEL, PIXEL);
  }
}
const DATA_LOSS_FRAGMENTS = [
  "—could not deter",       "the rema",                "as far as the",
  "—tion of the orig",      "prior to the",            "no further rec",
  "see also: [miss",        "est. 18—",                "formerly kno",
  "ref. doc. 7, pg.",       "per the last surv",       "—ontinued on",
  "source unclear",         "cf. adj. terr",           "—oundary as of",
  "appr. 40 mi. n",         "once called",             "filed under",
  "—he western exte",       "last updated: [",
  "— kshhh —",              "— static —",              "krrsh— krrsh—",
  "— [carrier tone] —",     "— ffffff —",              "kssh kssh kssh",
  "— [signal lost] —",      "whrrrrr—",                "— [no signal] —",
  "tkt tkt tkt tkt",        "— [end of band] —",       "hssssss—",
  "— [scan] —",             "zzzt zzzt —",             "— [reacquiring] —",
  "[ILLEGIBLE]",            "[ILLEGIBLE x3]",          "[PARTIALLY ILLEGIBLE]",
  "[INAUDIBLE]",            "[INAUDIBLE — approx. 4 sec.]",
  "[TEXT DAMAGED]",         "[PAGE MISSING]",          "[REDACTED]",
  "[ENTRY INCOMPLETE]",     "[RECORD CORRUPTED]",      "[TRANSMISSION INTERRUPTED]",
  "[SIGNAL DEGRADED]",      "[DATA EXPUNGED]",         "[REMAINDER LOST]",
  "[OVERWRITTEN]",
  "—ern settl— [ILLEGIBLE]",  "pop. [DATA EXPUNGED]",
  "coord. [ILLEGIBLE]",       "name orig— [TEXT DAMAGED]",
  "filed [ILLEGIBLE]",        "— [no carrier] — tkt tkt —",
];
const randomDataLoss = () => DATA_LOSS_FRAGMENTS[(Math.random() * DATA_LOSS_FRAGMENTS.length) | 0];
function buildRoadGrid(roads, cols, rows) {
  const grid = new Uint8Array(cols * rows);
  for (const pts of roads)
    for (const pt of pts)
      grid[pt.y * cols + pt.x] = 1;
  return grid;
}
function buildRegions(seed, cells, cols, rows) {
  const T_WATER = 0;
  const rng = (a, b) => {
    let x = Math.sin(seed * 9301 + a * 49297 + b * 233) * 93847;
    return x - Math.floor(x);
  };
  const seeds = [];
  let attempts = 0;
  while (seeds.length < 52 && attempts < 10000) {
    attempts++;
    const x = (rng(attempts, 1) * cols) | 0;
    const y = (rng(attempts, 2) * rows) | 0;
    if (cells[y]?.[x] === T_WATER) continue;
    const minDist = Math.min(cols, rows) * 0.055;
    if (seeds.some(s => Math.hypot(s.x - x, s.y - y) < minDist)) continue;
    seeds.push({ x, y, id: seeds.length });
  }
  const regionOf = new Int16Array(cols * rows).fill(-1);
  const visited  = new Uint8Array(cols * rows);
  const queue = [];
  const DIRS = [{dx:1,dy:0},{dx:-1,dy:0},{dx:0,dy:1},{dx:0,dy:-1},
                {dx:1,dy:1},{dx:1,dy:-1},{dx:-1,dy:1},{dx:-1,dy:-1}];
  for (const s of seeds) {
    if (cells[s.y]?.[s.x] === T_WATER) continue;
    const idx = s.y * cols + s.x;
    regionOf[idx] = s.id;
    visited[idx]  = 1;
    queue.push({ x: s.x, y: s.y, id: s.id });
  }
  let head = 0;
  while (head < queue.length) {
    const { x, y, id } = queue[head++];
    for (const { dx, dy } of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      if (cells[ny][nx] === T_WATER) continue;
      const ni = ny * cols + nx;
      if (visited[ni]) continue;
      visited[ni]  = 1;
      regionOf[ni] = id;
      queue.push({ x: nx, y: ny, id });
    }
  }
  const islandVisited = new Uint8Array(cols * rows);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const idx = y * cols + x;
      if (cells[y][x] === T_WATER) continue;
      if (regionOf[idx] !== -1) continue;
      if (islandVisited[idx]) continue;
      const component = [];
      const cq = [{ x, y }];
      islandVisited[idx] = 1;
      let cHead = 0;
      while (cHead < cq.length) {
        const { x: cx2, y: cy2 } = cq[cHead++];
        component.push({ x: cx2, y: cy2 });
        for (const { dx, dy } of DIRS) {
          const nx2 = cx2 + dx, ny2 = cy2 + dy;
          if (nx2 < 0 || ny2 < 0 || nx2 >= cols || ny2 >= rows) continue;
          if (cells[ny2][nx2] === T_WATER) continue;
          const ni2 = ny2 * cols + nx2;
          if (regionOf[ni2] !== -1 || islandVisited[ni2]) continue;
          islandVisited[ni2] = 1;
          cq.push({ x: nx2, y: ny2 });
        }
      }
      const avgX = component.reduce((s, c) => s + c.x, 0) / component.length;
      const avgY = component.reduce((s, c) => s + c.y, 0) / component.length;
      let bestCell = component[0], bestD = Infinity;
      for (const c of component) {
        const d = Math.hypot(c.x - avgX, c.y - avgY);
        if (d < bestD) { bestD = d; bestCell = c; }
      }
      const newId = seeds.length;
      seeds.push({ x: bestCell.x, y: bestCell.y, id: newId });
      const si = bestCell.y * cols + bestCell.x;
      regionOf[si] = newId;
      visited[si]  = 1;
      const iq = [{ x: bestCell.x, y: bestCell.y, id: newId }];
      let iHead = 0;
      while (iHead < iq.length) {
        const { x: ix, y: iy, id: iid } = iq[iHead++];
        for (const { dx, dy } of DIRS) {
          const nx2 = ix + dx, ny2 = iy + dy;
          if (nx2 < 0 || ny2 < 0 || nx2 >= cols || ny2 >= rows) continue;
          if (cells[ny2][nx2] === T_WATER) continue;
          const ni2 = ny2 * cols + nx2;
          if (regionOf[ni2] !== -1) continue;
          regionOf[ni2] = iid;
          iq.push({ x: nx2, y: ny2, id: iid });
        }
      }
    }
  }
  const regionData = seeds.map(s => ({
    id: s.id, cells: [], cx: 0, cy: 0, terrainCounts: {}
  }));
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const rid = regionOf[y * cols + x];
      if (rid < 0) continue;
      const rd = regionData[rid];
      rd.cells.push({ x, y });
      rd.terrainCounts[cells[y][x]] = (rd.terrainCounts[cells[y][x]] || 0) + 1;
    }
  }
  for (const rd of regionData) {
    if (rd.cells.length === 0) continue;
    rd.cx = rd.cells.reduce((s, c) => s + c.x, 0) / rd.cells.length;
    rd.cy = rd.cells.reduce((s, c) => s + c.y, 0) / rd.cells.length;
    rd.dominantTerrain = Object.entries(rd.terrainCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0] | 0;
  }
  const nRegions = regionData.length;
  const k = Math.max(2, Math.ceil(nRegions / 4));
  let macroSeeds = Array.from({length: k}, (_, i) => {
    const r = regionData[Math.floor(i * nRegions / k)];
    return { cx: r ? r.cx : 0, cy: r ? r.cy : 0 };
  });
  const macroOf = new Int16Array(nRegions);
  for (let iter = 0; iter < 8; iter++) {
    for (let i = 0; i < nRegions; i++) {
      const r = regionData[i];
      if (!r || r.cells.length === 0) { macroOf[i] = 0; continue; }
      let best = Infinity, bestM = 0;
      for (let m = 0; m < k; m++) {
        const d = Math.hypot(r.cx - macroSeeds[m].cx, r.cy - macroSeeds[m].cy);
        if (d < best) { best = d; bestM = m; }
      }
      macroOf[i] = bestM;
    }
    const sums = Array.from({length: k}, () => ({sx:0,sy:0,n:0}));
    for (let i = 0; i < nRegions; i++) {
      const r = regionData[i];
      if (!r || r.cells.length === 0) continue;
      sums[macroOf[i]].sx += r.cx;
      sums[macroOf[i]].sy += r.cy;
      sums[macroOf[i]].n++;
    }
    macroSeeds = sums.map((s, i) => s.n > 0
      ? { cx: s.sx/s.n, cy: s.sy/s.n }
      : macroSeeds[i]);
  }
  const macroMembers = Array.from({length: k}, () => []);
  for (let i = 0; i < nRegions; i++) macroMembers[macroOf[i]].push(i);
  return { regionOf, regionData, macroOf, macroMembers };
}
const HYDRO_MIN_CELLS = 20;
const HYDRO_INFLUENCE = 8;
function buildHydroRegions(seed, cells, cols, rows) {
  const rng = (a, b) => {
    let x = Math.sin(seed * 7321 + a * 31337 + b * 1049) * 49871;
    return x - Math.floor(x);
  };
  const waterCells = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      if (cells[y][x] === 0) waterCells.push({ x, y });
  if (waterCells.length === 0) return { hydroOf: new Int16Array(cols * rows).fill(-1), hydroData: [] };
  const seeds = [];
  let attempts = 0;
  const minDist = Math.min(cols, rows) * 0.22;
  while (seeds.length < 10 && attempts < 3000) {
    attempts++;
    const idx = (rng(attempts, 3) * waterCells.length) | 0;
    const { x, y } = waterCells[idx];
    if (seeds.some(s => Math.hypot(s.x - x, s.y - y) < minDist)) continue;
    seeds.push({ x, y, id: seeds.length });
  }
  const hydroOf = new Int16Array(cols * rows).fill(-1);
  for (const { x, y } of waterCells) {
    let best = Infinity, bestId = 0;
    for (const s of seeds) {
      const d = Math.hypot(x - s.x, y - s.y);
      if (d < best) { best = d; bestId = s.id; }
    }
    hydroOf[y * cols + x] = bestId;
  }
  const hydroData = seeds.map(s => ({
    id: s.id, cells: [], cx: 0, cy: 0, isCoastal: false, size: 0,
  }));
  for (const { x, y } of waterCells) {
    const rid = hydroOf[y * cols + x];
    if (rid < 0) continue;
    hydroData[rid].cells.push({ x, y });
  }
  for (const rd of hydroData) {
    rd.size = rd.cells.length;
    if (rd.size === 0) continue;
    rd.cx = rd.cells.reduce((s, c) => s + c.x, 0) / rd.size;
    rd.cy = rd.cells.reduce((s, c) => s + c.y, 0) / rd.size;
    rd.isCoastal = false;
  }
  const SEA_GAP = 4;
  const visited = new Uint8Array(cols * rows);
  const queue = [];
  for (let x = 0; x < cols; x++) {
    if (cells[0][x] === T.WATER)       { const i = 0 * cols + x;       if (!visited[i]) { visited[i] = 1; queue.push(i); } }
    if (cells[rows-1][x] === T.WATER)  { const i = (rows-1)*cols + x;  if (!visited[i]) { visited[i] = 1; queue.push(i); } }
  }
  for (let y = 1; y < rows-1; y++) {
    if (cells[y][0] === T.WATER)       { const i = y*cols;              if (!visited[i]) { visited[i] = 1; queue.push(i); } }
    if (cells[y][cols-1] === T.WATER)  { const i = y*cols + cols-1;     if (!visited[i]) { visited[i] = 1; queue.push(i); } }
  }
  const dx8 = [-1,1,0,0,-1,-1,1,1];
  const dy8 = [0,0,-1,1,-1,1,-1,1];
  let head = 0;
  while (head < queue.length) {
    const idx = queue[head++];
    const cx = idx % cols, cy = (idx / cols) | 0;
    const hrid = hydroOf[idx];
    if (hrid >= 0) hydroData[hrid].isCoastal = true;
    for (let d = 0; d < 8; d++) {
      const nx = cx + dx8[d], ny = cy + dy8[d];
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const ni = ny * cols + nx;
      if (visited[ni]) continue;
      if (cells[ny][nx] === T.WATER) { visited[ni] = 1; queue.push(ni); }
    }
  }
  const coastalOf = visited;
  return { hydroOf, hydroData, coastalOf };
}
function nearestHydroRegion(x, y, hydroOf, cols, rows) {
  if (x >= 0 && y >= 0 && x < cols && y < rows) {
    const self = hydroOf[y * cols + x];
    if (self >= 0) return self;
  }
  for (let r = 1; r <= HYDRO_INFLUENCE; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        const rid = hydroOf[ny * cols + nx];
        if (rid >= 0) return rid;
      }
    }
  }
  return -1;
}
async function callAPIHydronym(fragments, isCoastal, isNight) {
  const context = isCoastal ? 'coastal or tidal water' : 'inland lake or open water';
  const sys = `You coin water feature names for a fictional atlas.
Given fragments generated at or near a body of ${context}, derive a single hydronym.
Rules:
- 1 to 3 words maximum
- Draw from the full range of how real water names form — these categories are starting points, not a complete inventory; names that don't fit any category are often the most alive:
  * Shape or character: "The Narrows", "The Bight", "Long Sound", "The Flat"
  * What flows into or drains from it: "Millrace", "Tanner's Run", "Sawdust Slough"
  * Navigational or hazard character: "Foul Ground", "Shoal Passage", "The Bar"
  * Administrative or colonial naming: "Queen's Harbour", "Survey Basin", "Commission Flat"
  * The people who worked it: possessive or commemorative forms
  * A physical fact stated bluntly: "Cold Deep", "Black Bottom", "The Flat"
  * Something that doesn't fit a category but sounds like it should exist — favor this
- ${isCoastal ? 'Coastal/tidal water: names tend toward navigational, commercial, administrative registers.' : 'Inland lake/open water: names tend toward practical, ecological, or historical registers.'}
- Do NOT default to "Lake ___", "___ Bay", "___ River", "___ Creek" — these are overused
- Do NOT quote fragments directly — name crystallizes FROM them
- Slightly off — plausible but not quite right
- No fantasy, no whimsy — cartographic register only
- Output only the name, nothing else.`;
  const usr = `Fragments:
1. "${fragments[0]}"
2. "${fragments[1]}"
3. "${fragments[2]}"
Water feature name:`;
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6', max_tokens: 20,
      system: sys,
      messages: [
        { role: 'user', content: usr },
        { role: 'assistant', content: '' },
      ],
    }),
  });
  const name = cleanNameOutput(await safeParseAPI(res));
  return name.toUpperCase();
}
async function callAPIHydronymDrift(currentName, fragments, isCoastal) {
  const mode = Math.random() < 0.5 ? 'mutate' : 'rename';
  const register = isCoastal ? 'coastal, tidal, or navigational' : 'lacustrine or open-water';
  const mutateSys = `You are a hydrographic chart editor making a small correction or variant to a water feature name. Produce a subtly different version — a spelling shift, compression, local usage variant, or minor qualifier change. Still recognizably related. 1–3 words, ${register} register. Do NOT use "Lake ___", "___ Bay", "___ River". Output only the name.`;
  const renameSys = `You are renaming a water feature — for a new survey authority, a political change, a navigational reclassification, or because the old name caused confusion with another feature. The new name should feel like it came from a different era or institution. Draw on the fragments for context. 1–3 words, ${register} register. Do NOT use "Lake ___", "___ Bay", "___ River". Output only the name.`;
  const usr = `Current name: "${currentName}"
Fragments:
1. "${fragments[0]}"
2. "${fragments[1]}"
3. "${fragments[2]}"
New name:`;
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6', max_tokens: 20,
      system: mode === 'mutate' ? mutateSys : renameSys,
      messages: [
        { role: 'user', content: usr },
        { role: 'assistant', content: '' },
      ],
    }),
  });
  const name = cleanNameOutput(await safeParseAPI(res));
  return name.toUpperCase();
}
async function safeParseAPI(res) {
  const raw = await res.text();
  if (!res.ok) throw new Error(`API ${res.status}: ${raw.slice(0, 120)}`);
  if (!raw || !raw.trim()) throw new Error('truncated:');
  let data;
  try { data = JSON.parse(raw); }
  catch (e) {
    throw new Error(`truncated: ${raw.slice(0, 40)}`);
  }
  const text = data.content?.[0]?.text?.trim();
  if (!text) throw new Error(`Empty response (stop_reason: ${data.stop_reason || 'unknown'})`);
  return text;
}
function cleanNameOutput(raw) {
  const firstLine = raw.split('\n').map(l => l.trim()).find(l => l.length > 0) || raw;
  const stripped = firstLine.replace(/[*"'().,!?;:]/g, '').trim();
  const words = stripped.split(/\s+/).filter(Boolean).slice(0, 3);
  return words.join(' ');
}
async function safeParseAPIWithRetry(fetchFn) {
  try {
    const res = await fetchFn();
    return await safeParseAPI(res);
  } catch (e) {
    if (e.message.startsWith('truncated:')) {
      const res = await fetchFn();
      return await safeParseAPI(res);
    }
    throw e;
  }
}
async function callAPIToponym(fragments, dominantTerrain, waterChar, isNight) {
  const terrainNames = { 1: 'flat settled land', 2: 'wilderness or forest', 3: 'high sparse terrain' };
  const context = terrainNames[dominantTerrain] || 'mixed terrain';
  const waterCtx = waterChar !== 'none' ? `, near ${waterChar === 'coast' ? 'the coast' : 'a lake'}` : '';
  const sys = `You coin place names for a fictional atlas. Location: ${context}${waterCtx}.
Three fragments from this place are your raw material. The name should feel like it came from the place — absorbed something from the fragments — without quoting them.
Real place names are mostly just names. The majority are surnames, given names, or phonetic relics of older words: Tioga, Hartford, Duluth, Fresno, Waco, Ypsilanti, Ukiah. Many are a single opaque word that gives nothing away. A smaller number describe something — a feature, a condition, a duration — but even those tend toward compression and accident rather than description: No Business, Nameless, Ordinary, Uncertain, Drip Rock, Ermine, Dilworth. These are illustrations of the range, not a menu to draw from.
The name you produce should be 1–3 words. It should sound like it has been on a map for at least forty years. It should not sound assembled from parts. A name that is just a surname, just a given name, just a word that used to mean something else, is usually better. The "[adjective] [geographic noun]" format can work when the modifier is strange or specific enough that it earns the geography: Difficult Run, Troublesome Creek, Toad Suck, Dismal Swamp, Burnt Ordinary. It fails when the modifier is merely descriptive: Blue Hill, Misty Point, Pleasant Valley.
Slightly off. Plausible but not quite right. No fantasy, no whimsy.
Output only the place name, nothing else.`;
  const usr = `Fragments:
1. "${fragments[0]}"
2. "${fragments[1]}"
3. "${fragments[2]}"
Place name:`;
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6', max_tokens: 20,
      system: sys,
      messages: [
        { role: 'user', content: usr },
        { role: 'assistant', content: '' },
      ],
    }),
  });
  const name = cleanNameOutput(await safeParseAPI(res));
  return name.toUpperCase();
}
async function callAPIToponymDrift(currentName, fragments) {
  const mode = Math.random() < 0.5 ? 'mutate' : 'rename';
  const mutateSys = `You are a cartographic editor correcting or adjusting a place name. Produce a subtly different version of the given name — a spelling shift, a compression, a transcription variant, a local abbreviation. It should still be recognizably related. 1–3 words. Output only the name.`;
  const renameSys = `A place has been renamed — new administration, a commemorative decision, or because the old name caused confusion. The new name should feel like it came from a different hand or era. It can be a surname, a given name, a word that used to mean something else, a compressed description, a number, an event. Real renames often produce stranger results than the original: "Terminus" became "Atlanta", "Mud Lake" became something people actually wanted to live near, or didn't. 1–3 words. Output only the name.`;
  const usr = `Current name: "${currentName}"
Fragments:
1. "${fragments[0]}"
2. "${fragments[1]}"
3. "${fragments[2]}"
New name:`;
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6', max_tokens: 20,
      system: mode === 'mutate' ? mutateSys : renameSys,
      messages: [
        { role: 'user', content: usr },
        { role: 'assistant', content: '' },
      ],
    }),
  });
  const name = cleanNameOutput(await safeParseAPI(res));
  return name.toUpperCase();
}
function getSpatialContext(x, y, cells, roadGrid, cols, rows, hydroOf, hydroData, coastalOf) {
  const minDim = Math.min(cols, rows);
  const R_WATER = 8, R_ROAD = 6, R_BORDER = 10;
  const R_WILD_LAND = Math.max(3, Math.round(minDim * 0.04));
  const R_DEEP = Math.max(5, Math.round(minDim * 0.07));
  let waterCount = 0, coastalCells = 0, lakeCells = 0;
  for (let dy = -R_WATER; dy <= R_WATER; dy++) {
    for (let dx = -R_WATER; dx <= R_WATER; dx++) {
      const cx = x+dx, cy = y+dy;
      if (cx < 0 || cx >= cols || cy < 0 || cy >= rows) continue;
      if (cells[cy][cx] !== T.WATER) continue;
      waterCount++;
      if (coastalOf) {
        if (coastalOf[cy * cols + cx]) coastalCells++;
        else lakeCells++;
      }
    }
  }
  const waterChar = waterCount === 0 ? "none"
    : coastalCells > lakeCells ? "coast"
    : lakeCells > 0 ? "lake" : "none";
  let roadCount = 0, blockCount = 0;
  for (let dy = -R_ROAD; dy <= R_ROAD; dy++) {
    for (let dx = -R_ROAD; dx <= R_ROAD; dx++) {
      const cx = x+dx, cy = y+dy;
      if (cx < 0 || cx >= cols || cy < 0 || cy >= rows) continue;
      if (roadGrid[cy * cols + cx]) {
        roadCount++;
        if (cx+1 < cols && cy+1 < rows
          && roadGrid[cy * cols + cx+1]
          && roadGrid[(cy+1) * cols + cx]
          && roadGrid[(cy+1) * cols + cx+1]) blockCount++;
      }
    }
  }
  const roadDensity = roadCount === 0 ? "none"
    : (blockCount > 0 || roadCount >= 20) ? "dense"
    : "rural";
  const thisTerrain = cells[y]?.[x];
  let borderZone = false, deepWild = false;
  if (thisTerrain !== T.WATER) {
    let hasLand = false, hasWild = false, nearRoad = false;
    for (let dy = -R_BORDER; dy <= R_BORDER; dy++) {
      for (let dx = -R_BORDER; dx <= R_BORDER; dx++) {
        const cx = x+dx, cy = y+dy;
        if (cx < 0 || cx >= cols || cy < 0 || cy >= rows) continue;
        const t = cells[cy][cx];
        if (t === T.LAND) hasLand = true;
        if (t === T.WILDERNESS || t === T.SPARSE) hasWild = true;
        if (roadGrid[cy * cols + cx]) nearRoad = true;
      }
    }
    borderZone = hasLand && hasWild;
    if (thisTerrain === T.WILDERNESS && !nearRoad) {
      // no land within 4 cells (not just adjacent)
      let nearLand=false;
      outer2: for (let dy=-4;dy<=4;dy++) for (let dx=-4;dx<=4;dx++) {
        const cx=x+dx,cy=y+dy;
        if (cx<0||cx>=cols||cy<0||cy>=rows) continue;
        if (cells[cy][cx]===T.LAND) { nearLand=true; break outer2; }
      }
      if (!nearLand) {
        // no road within R_DEEP * 1.8
        const R_DEEP2 = R_DEEP;
        let dR=false;
        outer: for (let dy=-R_DEEP2;dy<=R_DEEP2;dy++) for (let dx=-R_DEEP2;dx<=R_DEEP2;dx++) {
          const cx=x+dx,cy=y+dy;
          if (cx<0||cx>=cols||cy<0||cy>=rows) continue;
          if (roadGrid[cy*cols+cx]) { dR=true; break outer; }
        }
        deepWild=!dR;
      }
    }
  }
  return { waterChar, roadDensity, roadCount, borderZone, deepWild };
}
function getRegister(terrain, waterChar, roadDensity, isNight, borderZone, deepWild) {
  let brief = "";
  if (terrain === T.WATER) {
    if (isNight) {
      if (waterChar === "lake") {
        brief = "A lake interior at night. The surface is a membrane — sound carries across it in ways it doesn\'t during the day, the far shore audible, the water against a dock piling rhythmic and continuous. The thermocline is still there. The organic processes the county monitors during business hours are running on their own schedule now; the quarterly testing has nothing to do with this. The something that moves at depth is still moving. The county\'s jurisdiction ends at five and resumes at eight and in between the lake is operating under its own protocols, which it was always operating under — the monitoring schedule was a convenient fiction imposed on a system that doesn\'t observe it. The smell is the same. The opacity is the same. The difference is that no one is recording conditions.";
      } else if (roadDensity === "dense") {
        brief = "Coastal water adjacent to working port infrastructure, at night. The channel markers are where the chart says they are. The fog signal fires on a timer. The light sweeps whether or not anyone is watching it sweep. The AIS transponder pings position into a network that logs it and acts on nothing. A Coast Guard frequency is open; transmissions happen; they are not addressed to anyone present. The buoy rings because the swells are there, not because anyone needs warning. Somewhere there is a watch crew but they are inside and the instruments are outside and the instruments are the ones doing the work. The precision is total. The intention is absent. Everything is functioning correctly. Nothing is failing. No one is required.";
      } else if (roadDensity === "none") {
        brief = "Open coastal water at night. The light sweeps. The buoy bell rings in the swells. The AIS pings. None of this is for anyone present — the automated infrastructure operates on its own schedule, indifferent to observation. Something is visible that might be a vessel — a light that could be a running light or could be a buoy, a wake that is either recent or just the water\'s movement. The water doesn\'t distinguish. The horizon is there or the fog has taken it. Either way the water continues doing what it does.";
      } else {
        brief = "Coastal water at night. The navigation infrastructure operates without observers — the light, the bell, the frequency open on the radio, the chart accurate to its last survey date. The automated world does not require witnesses. The water has salinity and fetch and current. The tidal range is what it is. This water does work and the work continues after dark on the same terms.";
      }
    } else if (roadDensity === "dense") {
      brief = "The water side of a working port district — the dock, the slip, the gap between the hull and the piling. Quieter than the street twenty yards inland. More mechanical. The pressure of the open water is real here in a way the street doesn\'t feel — the port manages it, routes it, prices it, but at the dock edge the sea\'s weight is still present underneath the infrastructure. The fenders absorb it. The lines take the load. The bollards have been doing this for decades. Bilge pump running. The smell of a boat\'s exhaust mixed with salt. A line not properly cleated. The horizon visible between hulls. This is the periphery of the organized port — less managed, more exposed, closer to what the port is actually in relationship with.";
    } else if (roadDensity === "none") {
      brief = "Open water with no roads in range — no infrastructure visible, no fixed reference point. Something may be on this water or nothing may be — the water doesn\'t distinguish. A light that could be a running light or could be a buoy. A sound that could be a hull working in swells or could be the water\'s own movement. A wake that is either recent or just the condition of the surface here. No orientation. The nearest chart datum is somewhere. Drift is real regardless of what is drifting. The water as subject: it has depth and temperature and current and bottom composition, and none of these properties require a vessel to be true.";
    } else {
      brief = waterChar === "lake"
        ? "Lacustrine: a lake interior, away from shore. The water is opaque or dark below a certain depth. In summer the surface layer is warm; thermocline a few feet down. This is not recreational water — no beach access here, no dock visible. The bottom composition is silt or clay. The smell is faintly organic, not unpleasant. No motor boats are supposed to operate in this zone. Water quality is tested quarterly by the county. Something moves at depth occasionally."
        : "Marine or large open water — coastal, tidal, or navigable. There is fetch here; waves form. The water has salinity and current. Depth is significant and charted. Commercial and recreational vessels both use this area. There are regulations about navigation, anchoring, speed. The bottom may be rock, sand, or mud depending on the area. Tidal range affects access. The horizon is visible from here. This water does work — it is not incidental.";
    }
  } else if (terrain === T.LAND) {
    if (waterChar === "coast" && roadDensity === "dense")
      brief = isNight
        ? "The port at night belongs to a different economy than the port during the day. The sailors on leave have a window — the ship is in, the ship goes out, in between is this. The bar that takes any currency. The gas station with the cold wind coming in off the water every time the door opens. Someone\'s car in the lot, engine running, the interior filling slowly with smoke, a cheap lighter\'s flame doing something specific in that haze. The transience is the subject: these are people who are between places in every sense, and the port at night has an economy built entirely around that interval. What things cost here. What\'s open that wouldn\'t be open anywhere else at this hour. The specific pleasures available to someone with cash and no fixed address for the next six hours. The longshoremen\'s bar is closed or the longshoremen went home. This is the other bar."
        : "A working port district: the smell of low tide near a parking structure, diesel from the trucks staging before dawn, the way the marine layer sits on the cranes until 10am. Warehouses that have been rezoned three times. The longshoremen\'s bar hasn\'t changed its name. Corrosion is a maintenance budget line item.";
    else if (waterChar === "coast" && roadDensity === "rural")
      brief = "A small coastal town where most of the money is seasonal. Boats registered in other states. Storm shutters that go up in October and sometimes don\'t come down until May. The bar closes at nine because there\'s nowhere to go after. Someone\'s uncle didn\'t come back from a crossing years ago — it\'s not talked about directly.";
    else if (waterChar === "coast" && roadDensity === "none")
      brief = "Coast with no town. A structure or two that doesn\'t appear on county maps. The access is technically public but effectively isn\'t. Cliff erosion is measurable and ongoing. The smell of rotting wrack at low tide. No cell service and no reason to expect any.";
    else if (waterChar === "lake" && roadDensity !== "none")
      brief = "Lakefront property with a homeowners association. Noise ordinances and dock setback rules. The neighbor\'s boat is technically within the property line. Sunscreen and the particular anxiety of property values. A realtor who sends a card every December. The water association tests for bacteria in July.";
    else if (waterChar === "lake" && roadDensity === "none")
      brief = "A lake property that didn\'t survive whatever the plan was. A FOR SALE sign face-down in the weeds. One boat still tied up. The camp closed mid-season one year and nobody\'s explained it clearly since. No wake zone signage still standing.";
    else if (roadDensity === "dense")
      brief = isNight
        ? "The shift-change city and the bar-spill city are both out. Street sweeper, hazard lights on the double-parked delivery truck, the 24-hour laundromat with one person in it. But also: the group that\'s been drinking since 9 and has a specific momentum now, someone laughing at something that doesn\'t sound funny. The contemplative texture is: you can be invisible here. Nobody is tracking you. The laundromat person and the guy outside the bar having a quiet cry and the delivery worker and the people stumbling home are all in the same city and none of them are in contact with each other. Urban 3am is less lonely than rural 3am but it\'s a different kind of alone. The office light that\'s been on for three weeks. The bodega."
        : "Urban. The physical texture of a city block: scaffolding that\'s been up eighteen months, a bar on its third name in six years, a permit dispute holding up the new building across the street. The sound of a jackhammer through a window at 7am. Coffee cup on someone\'s car roof — it\'s been there since they parked.";
    else if (roadDensity === "rural")
      brief = isNight
        ? "The CB is still going but you don\'t have a radio. Truck stop lot at 2am — engines running, no one visible, curtains drawn on the sleeper cabs. A dog that won\'t stop. A porch light a quarter mile off that is on every single night and tonight isn\'t. Someone took the double shift and is still there. That part is real. The other part is also real: if something happened to you out here right now, the question is not whether anyone would help. The question is how long before anyone would know."
        : "Exurban highway corridor. The last gas is a real distance away — it\'s on the sign. Something is crossed out on the laminated menu. The weight station is open irregularly. CB radio is still used here. Lodging is $28/night and the ice machine is down the hall.";
    else
      brief = "Rural land with no services. A property dispute filed decades ago still clouds the title — invent a specific decade-appropriate filing year each time, never default to a stock year. No trespassing signs on fence posts. There was a fire in one of the outbuildings — the frame is still there. The well is hand-dug and has been tested twice. What\'s in the field is hard to identify from the road.";
  } else if (terrain === T.WILDERNESS) {
    if (borderZone && !deepWild) {
      brief = "The edge of a wilderness designation — which is a line on a map that the vegetation on both sides ignores. On one side the land is managed; on the other it is protected; the practical difference is a budget line and a set of use restrictions that someone enforces irregularly. What the land holds is older than the designation. Old growth stumps at a specific height: that\'s a particular era of logging, a particular equipment, a decision made at a particular board meeting that no longer exists. A road that went in for extraction and stayed after the extraction ended, which means it goes somewhere that no longer exists as a destination. Slash piles decomposing at their own pace, forty years into it. The species composition tells you what was here before and what came in after the canopy opened. Boundary or survey infrastructure showing signs of tampering — invent a specific instance each time (a relocated marker, a pulled stake, a repainted blaze), not always the same one. The designation came too late for some things and early enough for others and the land holds both of those facts in the same acre.";
    } else if (waterChar === "coast") {
      brief = isNight
        ? "King tide. The wet-rock glint, barnacles, the path the moon makes when the clouds move. The roar of something enormous you have to trust without seeing — pinned to the cliff face, navigating by sound. Oil platform lights coming on offshore one by one as the forager makes camp in the understory. Shutters clattering on a closed roadside restaurant — the only building for miles besides the farmhouse next door, closed since a specific year decades back — invent one each time, do not default to a stock year. This is the edge of the continent and at 3am the continent feels very far away. Logbooks that end mid-sentence. A coordinates notation with no corresponding record. The redwoods come down close and the road between them and the cliff is narrower than the map suggests."
        : "A coastal strip that has been here longer than anyone\'s plans for it. Victorian inn with a hand-lettered breakfast menu. Lighthouse decommissioned decades ago (invent a specific year each time, do not default to a stock year), now a hostel. Roadside stand: live dungeness, honor system. Mushroom foragers working the redwood understory — the trees come down closer to the cliff edge than seems right. Oil platforms three miles offshore, visible and unaddressed. A restaurant closed since a specific year decades back (invent one each time, do not default to a stock year) still appears on county maps; the farmhouse next door is still occupied. Tide table posted at the trailhead. Nesting closure with a date that has passed. Erosion rate filed quarterly. Survey stakes with faded flagging tape. King tide advisory. Buoy positions may not reflect current conditions.";
    } else if (waterChar === "lake" && roadDensity !== "none") {
      brief = "Mountain lake with a small service economy. The general store sells fishing licenses, propane, and things you forgot to pack. The guide has been doing this for thirty years and charges accordingly. Mail used to come by floatplane; sometimes still does. The pass closes in October — the date varies by year, which creates logistical problems for everyone.";
    } else if (waterChar === "lake" && roadDensity === "none") {
      brief = "Wilderness lake with no services and no easy access. The water is colder than expected at any depth. No motorized boats — the sign says so, and there\'s no one to enforce it. The lake has a name in a language that predates the survey. Something large lives in the drainage basin. The approach takes longer than the map suggests.";
    } else if (roadDensity === "dense") {
      brief = isNight
        ? "Wilderness-adjacent urban development at night. The bar is open, the mountain is still there, and the two facts are in conversation. Someone just got off a shift that doesn\'t exist in most cities. The trauma bay is staffed for the kind of night this could become. Avalanche path cuts a scar through the lights on the slope above town — you can see where it went in the dark. The real estate here is priced against the wilderness, not the amenities. The road in or out has a condition advisory posted; the locals already knew before it posted."
        : "Wilderness-adjacent urban development. A city or substantial town that exists in genuine logistical tension with its surroundings — not scenery, operating condition. The pass road has a name for the kind of closure it gets and a separate name for the worse kind. Two hardware stores with a non-arbitrary division of inventory between them. The person at the bar next to you is either a guide, a contractor, or someone whose operation doesn\'t have a public-facing description — the conversation is the same either way. Hospital trauma capacity that makes no sense until a specific kind of Saturday in November. Avalanche advisory as daily social fact. Real estate priced against elevation and access, not square footage. The wilderness enforces its own schedule on the people who chose to be adjacent to it, which is the main thing everyone here has in common.";
    } else if (roadDensity === "rural") {
      brief = "A road corridor pushed into wilderness. Scenic overlook with a pull-out. A memorial cross where the guardrail ends. The ranger station log has handwriting going back decades. Fire rings at the designated campsites and also at the non-designated ones. Someone carved initials and a date in the picnic table decades ago — invent a specific year each time, do not default to a stock year. The infrastructure is recent. What it sits on is not.";
    } else if (deepWild) {
      brief = isNight
        ? "Same land, different predators active. The drainage sounds different because of what\'s moving through it. No human scale to orient by — the dark isn\'t dramatic, it\'s just the condition something crossed here recently. The body registers it before the mind does. No trail, no survey, no name that stuck. The processes here were running before nightfall and will be running at dawn and the interval does not matter to them."
        : "Land that has not agreed to be terrain. No trail, no survey, no name that stuck. The drainage goes where it goes. This place is not waiting to be found — it is already fully occupied by processes that predate the map and will outlast it. Something large moves through here. The evidence is physical and recent. The light comes through at a wrong angle for the time of day.";
    } else {
      brief = "Deep wilderness with no roads and no recent human infrastructure. The drainage patterns determine where you can walk. There\'s a blaze on a tree that doesn\'t correspond to any trail. Survey stakes from an expedition that didn\'t publish its findings. Something used this area as winter range — the signs are physical and recent.";
    }
  } else {
    if (isNight) {
      if (waterChar === "lake") {
        brief = "High alpine basin at night. The lake is still there and still cold enough to kill. The cairns are shapes in the dark — route, memorial, habit, they don\'t announce themselves differently at this hour. The body at altitude in the dark is doing specific work: processing thinner air, registering cold that has no relation to temperature in the lowlands, recalibrating continuously. The wind chill is not a forecast abstraction here, it is a physical condition the body is currently inside. The color of the water is gone. The surface is audible. The elevation does something to the dark that the dark at lower altitudes doesn\'t do. The body is where all of this registers first.";
      } else if (roadDensity !== "none") {
        brief = "High pass at night. The road is still there. The body at altitude in the dark is running different calculations than it runs below: air thinner, cold more specific, the distance to anything heated a real number. The avalanche advisory is posted; the date on it may not be current. The shortwave frequency still works — someone noted it somewhere around here. The ranger station is dark or has one light. What the elevation does to perception it does equally in the dark; the body is still where it registers, and at night there is less to triangulate by and more for the body to fill in on its own.";
      } else {
        brief = "High exposed plateau at night. No roads, no services, no cell signal, and now no light except what the sky provides, which at this elevation is considerable and still not enough to navigate by with confidence. The plane went down here in the 1970s — the cairn is somewhere in this dark. Someone has been maintaining a cache; finding it is a different problem now. The elevation does something to perception and the dark compounds it: the body is processing cold and thin air and the specific quality of high-altitude darkness simultaneously, and the body is where all of this registers, not the mind. The route is not marked. The radio frequency scratched into the rock near the high point is still a valid frequency.";
      }
    } else {
      if (waterChar === "lake")
        brief = isNight
          ? "High alpine lake at night. The cold has no relationship to the temperature at lower elevations — it is a different condition. The surface is audible. The cairns are shapes in the dark; they do not distinguish between route markers and memorials. The elevation does something to the dark that the dark at sea level doesn\'t do. No fish above this altitude. The lake has a name in a language that predates the survey and may predates the concept of this particular summit being named at all. If something went wrong here tonight the timeline would be measured in hours, not minutes."
          : "High alpine basin. A lake that stays cold enough to be genuinely dangerous at any depth, in any month. No fish above this elevation — that\'s a hard line, not an approximation. The cairns mark something: route, memorial, or habit, and they don\'t announce which. Color of the water shifts with cloud cover in a way that feels like information. Wind chill is a load-bearing number here, not a forecast abstraction. The last people through signed a register at the trailhead. The entries thin out after mid-October.";
      else if (roadDensity === "dense")
        brief = isNight
          ? "A city that knows the mountain is there even at night. The hospital\'s trauma capacity makes sense at 2am in a way it doesn\'t at noon. The bar\'s still open; the person next to you is either a guide or a contractor or someone who runs the kind of operation that doesn\'t get discussed. The avalanche advisory updates at midnight. The road in is passable until it isn\'t, and the people here know that date is coming before the forecast says so. Real estate prices that make no sense from outside this elevation. Something large moved through the edge of town last week — it\'s on the news app, not the front page, but it\'s there."
          : "A city or substantial town pressed against the wilderness boundary, which it does not treat as scenery. The road in has a name referencing something that no longer exists as a destination. Two hardware stores, the division of inventory between them not arbitrary. The hospital has trauma capacity that seems disproportionate until a specific kind of day in October. Avalanche advisory is a daily social fact — people mention it the way people elsewhere mention traffic. Real estate listings include square footage and elevation. A contractor class arrived for one winter and did not leave, which produced a specific kind of house: built correctly, finished wrong, on a lot that cost more than the structure. The mountain doesn\'t care about your timeline, which everyone here has internalized at varying cost. The wilderness is the operating condition, not the view.";
      else if (roadDensity === "rural")
        brief = isNight
          ? "A road in the mountains at night, which is different from a road at night in other places. The distance to the next light is a real number. The pass advisory is posted but the date may not be current. Something crosses the road at elevation that wouldn\'t cross it below. The ranger station has one light on or none. The body at altitude in the dark is running different calculations: thinner air, cold more specific, the gap to anything heated a real distance with real consequences."
          : "A road corridor through high terrain. Scenic pull-out with a view that doesn\'t appear on any postcard because the angle is wrong. Memorial cross at the place the guardrail ends. The ranger station log has handwriting going back decades, different pens and different concerns accumulating. Fire rings at the designated sites and at the ones that aren\'t. Someone carved initials and a date in the picnic table decades ago — invent a specific year each time, do not default to a stock year. The infrastructure is recent. What it sits on is not.";
      else
        brief = isNight
          ? "High exposed terrain with no road, no infrastructure, no signal. Same land as the day version, different predators active. The drainage sounds different at night because of what\'s moving through it. No human scale to orient by — the dark here isn\'t dramatic, it\'s the condition. The route isn\'t marked. The plane went down in the 1970s; the cairn is somewhere near here but the cairn is also just a dark shape. A radio frequency scratched into rock near the high point, no guarantee it still works."
          : "High exposed terrain: thin soil, exposed rock, no road, no services, no cell signal. The plane went down here in the 1970s — there\'s a cairn, which is also just a cairn. Someone has been maintaining a cache; the contents change between visits. A radio frequency scratched into rock near the high point. The route is not marked and not obvious. The evidence of what used this area for winter range is physical and recent.";
    }
  }
  const briefHandlesNight = terrain === T.WATER
    || terrain === T.SPARSE
    || terrain === T.WILDERNESS
    || (terrain === T.LAND && roadDensity === "dense")
    || (terrain === T.LAND && waterChar === "coast" && roadDensity === "dense")
    || (terrain === T.LAND && roadDensity === "rural" && waterChar === "none");
  const nightAddendum = (isNight && !briefHandlesNight)
    ? " It is the middle of the night. The place has a different quality now — not dramatic, just the specific way familiar things become slightly unrecognizable at 3am. What is open shouldn\'t be. What is closed should have opened an hour ago."
    : "";
  return brief + nightAddendum;
}

const TOURIST_SUFFIXES = new Set(['HISTORIC SITE MARKER', 'STATE HERITAGE AREA', 'SCENIC BYWAY', 'INTERPRETIVE AREA', 'HERITAGE CORRIDOR', 'CONSERVATION AREA', 'POINTS OF INTEREST']);

function getAbandonedRegister(terrain, waterChar, roadDensity, isNight, suffix) {
  const isTourist = TOURIST_SUFFIXES.has(suffix);
  let brief = '';

  if (isTourist) {
    // Tourist phase: the apparatus of managed ruins
    if (terrain === T.LAND) {
      if (waterChar === 'coast' && roadDensity === 'dense') {
        brief = isNight
          ? "A decommissioned industrial waterfront heritage site after hours. The interpretive fencing still there, the stabilized ruins behind it darker than the street. The archival photograph panels catching the streetlight — the photographs showing the cranes in operation, the cranes behind the photographs in various states of not being in operation. The resurfaced heritage corridor ending at the designation boundary, the streets on the other side expressing their actual condition. A person moving through the district not on the heritage route, using the streets the way streets are used, the interpretive apparatus not designed for this use and not preventing it."
          : "A decommissioned container port, heritage designation, the apparatus in place. Interpretive panels at intervals along the seawall explaining what the cranes were for — the language of the panels present-tense in places where it shouldn't be, past-tense in places where the present is still visible. A parking lot where the secondary dock was, the lot surface new, the dock pilings still in the water beside it. The crane housings repainted to a color close to the original but not the original — the research was done, the color was found, the paint mixed, the result slightly wrong in a way that required more research to identify than it would to accept. The smell of the basin still there underneath the interpretive panels. Admission free, donations appreciated, the donation box at the original foreman's station.";
      } else if (waterChar === 'coast' && roadDensity === 'rural') {
        brief = isNight
          ? "A small coastal heritage settlement after hours. The dock light still on — the same dock light that was here before the designation, now heritage infrastructure. The kayak rentals chained, the interpretive panels still legible in the dock light — the lamination catching the light, the text available to nobody. The fuel pump behind its rope barrier. A car in the lot that wasn't there at closing — someone watching the water, or using the lot, the heritage apparatus irrelevant to whatever they're doing. The panel about the gear types faces the water. The water doesn't read it."
          : "A small coastal settlement, heritage designation, the seasonal economy replaced by a different seasonal economy. The fishing infrastructure still there, interpreted: a panel at the dock explaining the gear types, the panel laminated against the weather, the lamination beginning to fail at the corners. The fuel pump at the marine supply behind a rope barrier, the price still on it from whenever, the price now part of the exhibit. Kayak rentals where the working boats were. The smell of low tide still arriving on schedule, not mentioned in the interpretive material. A family reading the panel about the gear types while a cormorant works the water behind them. The panel doesn't mention the cormorant either.";
      } else if (waterChar === 'coast' && roadDensity === 'none') {
        brief = isNight
          ? "Coast with a boardwalk, after hours. The parking area empty or nearly. The boardwalk lit at intervals by solar fixtures, the fixtures doing their job, the light falling on the composite decking and the interpretive panels and nothing else. The concrete pad labeled in the daytime — the label still there, facing the dark. The wrack line at whatever level the tide has brought it to. A solar fixture at the end of the boardwalk cycling on and off, the sensor going, the light finding nothing each time it trips."
          : "Coast with a boardwalk now, the boardwalk recent, the material composite and resistant to the salt air in ways the original structures weren't. Interpretive panels at the viewpoints explaining the coastal ecology, the panels accurate, the coastal ecology proceeding regardless. A parking area at the access point, the access point formalized — a path that was worn into the ground now graveled and signed, the signing indicating the thing the path always went to. The concrete pad where the structure was is visible from the boardwalk, labeled, the label describing its former use in two sentences. The wrack line higher than the boardwalk designers accounted for.";
      } else if (waterChar === 'lake' && roadDensity === 'dense') {
        brief = isNight
          ? "A failed lakefront development after hours, heritage site. The parks department signage still legible, the boat launch gate locked. The stabilized incomplete buildings fenced, the fencing casting regular shadows in the dock light. The interpretive panel about the development history faces the lake. The lake at night is the same lake — the thermocline still organizing itself, the water quality nobody is testing since the monitoring contract lapsed. A car in the access lot, the occupants looking at the lake or at the buildings or at the panel about the development history. The panel explains the ambition of the original project. The lake doesn't confirm or deny."
          : "A failed lakefront development, the failure now the attraction. The FOR SALE signs gone, replaced by interpretive panels explaining the development history — the ambition of it, the timeline, the point at which the market moved. The boat launch still functional, now a public access point, the HOA signage replaced by a parks department sign using the same font at a different size. The buildings in various states of completion stabilized rather than finished — the open ones fenced, the fencing explaining that the open ones are structurally assessed and safe to view from here. The lake still pretty from the right angle. The panel about the development history says so, in different words.";
      } else if (waterChar === 'lake' && roadDensity === 'rural') {
        brief = isNight
          ? "A lake heritage site after hours. The main building locked, the tour windows dark. The paddle boats still chained to the dock, the rope across the cleat, the interpretive panel about the paddle boats facing the water. The dock light on. The lake at night doing what it does — the species active at this hour not the species the interpretive material describes. Something large moving in the shallows, the same something as before the designation, the designation not having addressed it. The panel about the paddle boats doesn't mention it. The panel faces the water."
          : "A lake property — camp, fishing lodge, small resort — heritage designated, the designation arriving after the closure rather than preventing it. The rental signage replaced by an interpretive panel using a photograph of the rental signage. The paddle boats still there, now part of the exhibit, a rope across the dock cleat preventing use. The fire ring with an explanatory placard. The main building open for tours on weekends, the interior preserved at the point of closure — the calendar, the posted rates, the coffee cups. The lake visible through the tour windows, the lake not preserved, the lake continuing.";
      } else if (waterChar === 'lake' && roadDensity === 'none') {
        brief = isNight
          ? "Failed lake heritage site after hours. The marker lit by a ground fixture, the explanatory panel readable, the foundation pour in the fence behind it in darkness. The FOR SALE sign behind glass catching the fixture light. The survey stake with its new orange flag. The lake beyond the end of the interpretive material, the lake dark, the lake not part of the heritage designation. The fixture illuminates the panel explaining what was going to be built here. The lake exists in the space where the building would have been, and at night this is more legible than during the day."
          : "Failed lake property, heritage marker installed, the marker the most substantial structure now on the site. The foundation pour stabilized — rebar cut, edges rounded, a low fence around the perimeter, an explanatory panel describing what was going to be here. The FOR SALE sign that was face-down in the grass mounted upright behind glass as an artifact. The survey stake still in the ground, the orange flag replaced with a new orange flag by whoever maintains the site. The lake accessible from the marker, the access unimproved, the interpretive material ending at the marker and the lake beginning where the material ends.";
      } else if (roadDensity === 'dense') {
        brief = isNight
          ? "An urban heritage district after hours. The interpretive fencing still there, the stabilized ruins behind it darker than the street. The archival photograph panels catching the streetlight — the photographs showing the buildings intact, the buildings behind the photographs in various states of not being intact. The sumac in the lots moving when the wind is enough. The heritage corridor resurfacing ending at the boundary, the streets on the other side expressing their actual condition. A person moving through the district not on the heritage route, using the streets the way streets are used, the interpretive apparatus not designed for this use and not preventing it."
          : "An urban district, heritage designation, the decay managed into legibility. Stabilized ruins with explanatory fencing, the fencing new, the ruins behind it continuing to do what ruins do at a rate the stabilization has slowed but not stopped. Interpretive panels at the significant structures explaining their former use, the panels using archival photographs, the archival photographs showing the structures at a point that now requires imagination to connect to what's visible. The sumac and ailanthus still in the lots, now described in one panel as examples of urban succession ecology. The street grid still intact, the streets themselves resurfaced for the heritage corridor, the resurfacing ending exactly at the designation boundary.";
      } else if (roadDensity === 'rural') {
        brief = isNight
          ? "A heritage exurban settlement after hours. The diner dark, the reproduction menu visible through the filmed window. The gas station canopy lit by a period-appropriate fixture installed during the restoration, the fixture illuminating the preserved rust streaks. The motel tour rooms locked. The highway still going through — headlights sweeping the canopy, catching the clear-coated rust, gone. The heritage apparatus closed. The settlement in the dark is closer to what it was before the designation than it is during the day, which the designation did not intend and does not acknowledge."
          : "An exurban settlement, heritage designation, the bypass that killed it now the route visitors take to reach it. The gas station canopy stabilized, the rust streaks preserved under a clear coating, the pumps behind interpretive fencing explaining the fuel grades of the relevant decade. The diner open again as a heritage diner, the menu a reproduction of the original menu, the prices the original prices with a small sign explaining the prices are historical and meals are complimentary with admission. The motel rooms that still have roofs open for tours, the rooms preserved at closure, the rooms that don't have roofs fenced and explained. The highway still going through. Trucks still not stopping.";
      } else {
        brief = isNight
          ? "Rural heritage marker after hours. The pullout empty or a single car. The standing wall lit by a ground fixture, the explanatory panel readable. The archival photograph on the panel showing the property with its additional structures, the landscape behind the panel dark and showing none of them. The heritage cap on the well casing. The planted border outside the fenced area moving in whatever wind there is, still flowering in season, the interpretive material ending at the fence and the border continuing beyond it. The ground fixture illuminates the wall. The wall casts a shadow that reaches the fence line and stops."
          : "Rural land, heritage marker at the road, the marker the reason for the pullout. The burned outbuilding wall still standing — the standing wall is why there's a marker, the marker explaining the wall, the wall the only thing left that required explanation. An interpretive panel with an archival photograph showing the property at an earlier point, the photograph showing more than the wall, the additional structures in the photograph not present in the landscape behind the panel. The well casing with a heritage cap. The planted border along the south side still conducting its own business, not mentioned in the interpretive material, not within the fenced heritage area.";
      }
    } else if (terrain === T.WILDERNESS) {
      // Erasure register — the gap between trace and misreading, held without comment
      if (waterChar === 'coast') {
        brief = isNight
          ? "Coastal trail after hours, the trailhead gate closed. The trail still there, the grade still the grade of the logging road, the canopy filtering whatever light the sky provides. The viewpoint platform on its foundation, the interpretive panel facing the water, the water doing what it does. The cable rigging in the undergrowth off the trail catching no light, present as mass and as the specific way the undergrowth grows differently around it. The platform foundation the same temperature as the surrounding rock at night, which is not the same as the surrounding soil, which is how foundations differ from geology and why the difference is still legible after this many years."
          : "A trailhead with a parking lot, the lot recently resurfaced. A kiosk with a trail map, the map showing the route in as a dotted line through old growth. The old growth is second growth, the original stand gone before anyone now living was born, the second growth old enough now that the distinction requires knowing what to look for — the spacing of the trees, the uniformity of the diameter, the absence of snags. The trail following the grade of the extraction road exactly, the grade too consistent for a natural feature, the trail notes describing the gentle climb as a highlight. The blazes on the trees recent, well-maintained. The older blazes underneath them, a different shape, not mentioned in the trail notes.";
      } else if (waterChar === 'lake') {
        brief = isNight
          ? "Mountain lake recreation area after hours. The trailhead gate closed, the trail still going up the supply road grade in the dark. The floatplane dock lit by a solar fixture, the fishing access point empty, the interpretive panel facing the water. The ranger station locked, the log behind glass inside it, the last entry still the last entry. Something large in the drainage, the same something as always, the interpretive panel at the dock covering lake depth and elevation and fish species and not covering this."
          : "Mountain lake, the service economy replaced by recreation access. A trailhead at the pass, the trail following the supply road grade to the lake, the grade described in the trail notes as moderate with good drainage. The floatplane dock still there, now a fishing access point, the cleats still functional, the interpretive panel at the dock explaining the lake's elevation and depth and the species present. The ranger station stabilized, open for self-guided tour, the station log preserved behind glass — the entries readable, the handwriting of the personnel who logged the pass conditions and the incidents and the wildlife sightings, the log ending at a specific date. The lake cold and clear and fishable above the elevation where it isn't.";
      } else if (roadDensity === 'rural') {
        brief = isNight
          ? "Trailhead parking lot at dusk, the gate closing soon or already closed, one car still in the lot. The trail going into the trees on the grade of the extraction road, the grade consistent in the way natural grades aren't, the trail notes describing the consistency as a feature. The older blazes under the newer blazes not visible from the parking lot. The car in the lot still there after the gate closed. The trail going into the dark on its consistent grade, the grade the reason the trail is here, the reason the trail is here not on the map."
          : "A trailhead with a parking lot, the lot recently resurfaced. A kiosk with a trail map, the map showing the route in as a dotted line through old growth. The old growth is second growth, the original stand gone before anyone now living was born, the second growth old enough now that the distinction requires knowing what to look for — the spacing of the trees, the uniformity of the diameter, the absence of snags. The trail following the grade of the extraction road exactly, the grade too consistent for a natural feature, the trail notes describing the gentle climb as a highlight. The blazes on the trees recent, well-maintained. The older blazes underneath them, a different shape, not mentioned in the trail notes.";
      } else {
        brief = isNight
          ? "Road ending at a turnaround, after dark. A car at the turnaround, the occupants having gone up the skid road or still in the car. The skid road going into the trees on its grade, the trees closing over it, the canopy blocking the sky. The soil compaction of the skid road holds moisture differently than the surrounding floor — at night the difference is thermal, the skid road slightly warmer. The turnaround gravel, the gravel not native to this location, brought here for the operation, still here. The skid road going up into the dark on its consistent grade toward whatever the operation was after."
          : "No trailhead, no designation, just the road ending at a turnaround that wasn't here before the extraction and is here now. Hikers using the turnaround as a trailhead, the informal trail beginning where the road ends, the informal trail following the skid road into the trees. The skid road not on any recreational map, the recreational use not sanctioned, the use happening regardless. The soil compaction of the skid road different from the surrounding forest floor, the difference visible as a slightly different ground cover, the ground cover visible to someone looking for it. The hikers not looking for it, looking at the canopy, which is genuine second growth and genuinely beautiful.";
      }
    } else {
      // SPARSE tourist — mix of apparatus and erasure depending on road density
      if (waterChar === 'lake') {
        brief = isNight
          ? "Alpine basin after hours, backcountry permits required, the permitted campers in their tents or not yet asleep. The bear box on its rock. The cairns marking the route in the dark, each one a shape. The adit fenced, the explanatory sign not readable at night, the adit breathing its cold air regardless. The permit system tracking who is in the basin. Not tracking what else is."
          : "Alpine basin, recreation designation, the mining claim or grazing allotment that preceded it visible if you know where the infrastructure was. A backcountry permit system now, the permit kiosk at the trailhead below, the trail following the supply route to the basin. The cache box on its rock, now a bear box, the hasp replaced, the rock the same rock. The cairns still marking the route, the route now on recreational maps, the maps not noting that the cairns predate the recreation use. The adit fenced, an explanatory sign, the sign explaining the mining history accurately. The lake still dangerous in the specific ways it always was, the permit system not addressing this.";
      } else if (roadDensity === 'dense') {
        brief = isNight
          ? "A high heritage town after hours, the designated core dark, the bar still open, the trauma bay still lit. The interpretive panels on the main street readable under the streetlights, the archival photographs showing the street full. The street not full. The mountain not visible but present — the temperature, the road condition, the avalanche terrain on both sides of the pass. The heritage designation covers the core. The rest of the town is the rest of the town, still doing what it does at this elevation at night, the designation not having changed the elevation or the night or the mountain's indifference to the interpretive apparatus installed at its base."
          : "A high town, heritage designation applied to the downtown core, the trauma bay still staffed and not part of the heritage designation. Interpretive panels on the main street explaining the boom period, the panels using archival photographs showing the street at capacity, the street behind the panels at partial capacity. The open businesses doing double work — their actual function and their heritage function, the menu laminated and the history of the building on the back of the menu. The mountain still there as operating condition, the avalanche advisory still posted, the heritage designation not having addressed the mountain. The panels explain the boom. The bust is the context the panels are installed in.";
      } else if (roadDensity === 'rural') {
        brief = isNight
          ? "High pass heritage marker after hours. The pullout empty or a single vehicle. The marker lit, the panel readable, the archival photograph showing the supply wagons on the grade. The grade still the grade, the road going down both sides of the pass into the dark. The avalanche terrain on both sides present as mass, as the specific silence of it. A vehicle going through without stopping, the headlights sweeping the marker, the archival photograph briefly visible, the supply wagons briefly there, the vehicle gone."
          : "High pass, heritage marker at the summit, the marker explaining the pass's role in the regional supply network, the supply network that created the road, the road the reason anyone is here. A pullout, a panel, an archival photograph. The pass still open or closed on its own schedule — the marker doesn't affect this. Avalanche terrain on both sides still avalanche terrain, noted in the panel as historical context, still operative as current condition. The grade of the road still the grade the supply wagons required. The marker explaining this. The road not requiring explanation to use.";
      } else {
        brief = isNight
          ? "High exposed terrain, heritage mining site, after hours. No permitted overnight use at this elevation on this terrain. A person here anyway, the permit system not having addressed this specific location at this specific hour. The adit breathing its cold air through the fence. The stamp mill components against the sky, the interpretive panels facing them, the panels dark. The radio frequency scratched into the rock at the entrance, not readable at night. The person here reading the frequency with a light. The frequency still a frequency. Nobody monitoring it, or nobody saying they are."
          : "High exposed terrain, the mining operation heritage designated, the designation covering the adit entrance and the stamp mill components and the drill rod and the compressor housing. A trail from the nearest trailhead, the trail following the access route, the access route the only sensible grade at this elevation. Interpretive panels at the significant components explaining the operation accurately. The radio frequency still scratched into the rock at the adit entrance, the panels not mentioning the frequency. The adit fenced, the cold air still coming out of it, the fence not addressing the cold air. The stamp mill components casting the same shadows they always cast, the shadows now falling on hikers reading the panels.";
      }
    }
  } else {
    // Pre-tourist phase: material decay, sensory/atmospheric, residual function as undertone
    if (terrain === T.LAND) {
      if (waterChar === 'coast' && roadDensity === 'dense') {
        brief = isNight
          ? "Decommissioned container port. The navigation lights still cycling on the channel markers for no one. The crane housings are a different size at night — not larger, but the edges where they end are unclear. Something is moving in the warehouse complex that isn't the corrugated sheet. The dock basin perfectly still in a way working water never is. The gulls are in the structural cavities but they aren't making gull sounds. The bollards still wrapped in line, the line still holding tension, nothing on the other end."
          : "A decommissioned container port. Concrete pier surface going granular at the expansion joints, rebar showing through where the freeze-thaw has worked longest. Salt has gotten into everything it can get into. The crane housings are streaked white-orange from the top down. Gull colonies in the structural cavities — the smell is layered: guano over diesel over the specific mineral character of long-standing brackish water. Dock basin surface has the dull, unworked look of water nothing has moved recently. Where the warehouse roof has failed the interior has its own weather now. Something about the bollards still wrapped in line.";
      } else if (waterChar === 'coast' && roadDensity === 'rural') {
        brief = isNight
          ? "A small coastal settlement that stopped. The dock light still on — automated, triggered by dark. The buoy bell offshore marking a hazard for nobody. Houses in a row with the particular darkness of interiors that haven't had a light on in months. The smell of low tide. Something has gotten into at least one of the buildings and made a different use of it. The dock light catches the water and the water moves and the light moves with it and there's nothing else moving."
          : "A small coastal settlement, seasonal or permanent, that stopped. Storm shutters on every window including the ones that don't need them. The dock still sound but nothing tied to it. Fuel pump at the marine supply still has a price on it from whenever. Somewhere a buoy that served the local fleet still marks a hazard nobody is navigating around. The smell of low tide arriving on schedule. A vegetable garden gone to its own devices — what's aggressive is aggressive, what needed tending is gone. The mailboxes still there at the road.";
      } else if (waterChar === 'coast' && roadDensity === 'none') {
        brief = isNight
          ? "Coast with no settlement. The concrete pad where a structure was is the same color as everything else at night. Something is using the standing structure — sound from inside it that stops when it should continue and continues when it should stop. The tide coming in at the same intervals it always has. The fence posts without wire casting shadows that look like wire. The path to the shore still there, worn into the ground, going somewhere that isn't there anymore."
          : "Coast with no settlement, or what's left after one. A concrete pad where a structure was, the anchor bolts still in it. Wrack line higher than it should be — storm damage or just the incremental work of water with nothing to stop it anymore. A path worn to the shore from a direction that no longer has anything at the end of it. Fence posts without wire. The coast doing what it does regardless: tide in, tide out, the specific smell of exposed rock and whatever is dying in the pools. One structure still standing, use unclear, roof partially open to weather.";
      } else if (waterChar === 'lake' && roadDensity === 'dense') {
        brief = isNight
          ? "A lakefront development that stopped. The security lighting on motion sensors still working — trips for deer, for wind moving the ornamental plantings, for nothing visible. The dock empty and the dock light on. The lake at night is flat and large and doesn't care about the development at all. Something has broken a window in one of the further-along units and the interior is now connected to the exterior in ways the design didn't intend. The motion sensor light trips again. Nothing there when it does."
          : "A lakefront development that failed mid-build or stopped being viable. The HOA infrastructure still legible: the boat launch, the shared dock, the community signage with the development name still on it. Setback violations that were going to be addressed. The lake itself indifferent — water quality testing nobody is doing, the thermocline still organizing itself seasonally. Buildings in various states of completion, the ones that got furthest along worst off now, more surface area for weather to work. FOR SALE signs face-down or still up, the listings expired. The lake still pretty from the right angle.";
      } else if (waterChar === 'lake' && roadDensity === 'rural') {
        brief = isNight
          ? "A lake property that closed. The dock extending into water that's perfectly black at night. The paddle boats still chained, moving when the water moves. One outbuilding door open at an angle that suggests it's been open for a long time. The lake sounds at night are not the sounds of a lake with activity on it — different species, different intervals, something large moving in the shallows that stops when it registers the dock. The main building holds heat differently than the outside air. The windows are the same temperature as the walls."
          : "A lake property that didn't work out — camp, fishing lodge, small resort, the category doesn't matter much now. The rental signage still up. Paddle boats chained to a dock cleat, the chain sound when the water moves. The lake doing its seasonal business: surface temperature, algae, the particular quality of light on inland water in the afternoon. A fire ring with one summer's ash still in it. The main building accessible, the outbuildings less so. Whatever was in the freezer was dealt with or wasn't.";
      } else if (waterChar === 'lake' && roadDensity === 'none') {
        brief = isNight
          ? "Failed lake property. The foundation pour open to the sky, the rebar standing in it. The lake beyond it perfectly still. No light source except the lake reflecting whatever the sky has. The survey stake with its faded flag. Something in the water — not threatening exactly, just present in a way that daytime obscures. The foundation holds cold air differently than the surrounding ground. The rebar casts shadows that point toward the lake."
          : "Failed lake property, no resort infrastructure, just the land and what was attempted on it. A FOR SALE sign face-down in the grass. Foundation pour that stopped, rebar still standing. The lake accessible from here but nothing to access it with. Water quality nobody is testing. A survey stake still in the ground with the orange flag mostly faded. The lake doing exactly what it would do if none of this had been attempted.";
      } else if (roadDensity === 'dense') {
        brief = isNight
          ? "A city district at night, mostly dark. Upper floors of the taller buildings open to weather — windows gone, the interiors continuous with the outside air. The street signals still cycling for nobody. Sumac and ailanthus catching whatever light there is, the leaves moving. A building mid-collapse has been mid-collapsing for long enough that it has its own settled shape now, the rubble pile compressed, things growing on it. The signal changes. A floor gives somewhere in the interior of the open building — not falling, just settling further, the sound of it carrying in the absence of traffic. The signal changes again."
          : "A city district going back to something. Asphalt heaving where the tree roots have had time. Buildings in the middle of being reclaimed — not dramatically, just incrementally, the way water finds the lowest point. Brickwork spalling at the lintels, the mortar joints open, the interior of the wall visible as a cross-section now. A storefront with the ceiling collapsed inward, the debris pile now supporting its own ecosystem. The scaffolding on the renovation that stopped still up, the planking warped and sprouting. Sumac in the lots. The street grid intact but the street itself expressing its actual age now that maintenance has stopped. Somewhere a fire hydrant has been open long enough to erode a channel in the asphalt.";
      } else if (roadDensity === 'rural') {
        brief = isNight
          ? "An exurban settlement at night. The highway going through at intervals — headlights sweeping the gas station canopy, catching the rust streaks, gone. The motel rooms that still have roofs are one quality of dark. The rooms that don't are another. The diner glass racking in the frames when the temperature drops. A truck slows — the engine note changes — and doesn't stop, the note climbing back to highway speed and then gone. The open motel rooms have been open long enough that something has made use of them. The headlights sweep through again. The something doesn't move when they do."
          : "An exurban settlement the highway bypassed or the industry left. The gas station canopy still standing but the canopy is doing what metal does over time — seams opening, the underside bubbled with rust, one corner beginning to describe a new angle. Pumps still there, the hoses cracked and stiff. The diner windows intact but the sills rotting out from underneath, the glass starting to rack in the frames. Parking lot surface expressing every crack it ever had plus new ones, the painted lines still faintly there under the growth. A motel with the roof failing room by room — some intact, some open, the open ones darker inside than they should be in daylight.";
      } else {
        brief = isNight
          ? "Rural land at night, burned outbuilding, one wall to its full height, the rest outline. The access road goes back to the highway through dark that has no interruption. The well casing. The fence line disappearing in both directions into more dark. No light source except what the sky provides which tonight is not much. The wall casts a shadow at the wrong angle for the available light. The well casing makes a sound — resonant, like something dropped in it a long time ago still arriving at the bottom."
          : "Rural land with a burned outbuilding and what's left of what came before it. The fire did what fire does and weather has continued the work — the standing wall spalling, the mortar joints open, the interior of the wall visible as a cross-section now. The access road rutted deep enough to hold water, the ruts growing their own vegetation. Compaction where the structure was: the soil different, harder, the grass thinner over the footprint. A well casing standing, the concrete apron around it cracked and lifting. Whatever was planted along the south side of the house has been conducting its own business for years now — gone leggy, gone to seed, still flowering.";
      }
    } else if (terrain === T.WILDERNESS) {
      if (waterChar === 'coast') {
        brief = isNight
          ? "Coastal wilderness at night, extraction infrastructure still in it. The cable rigging catches whatever light comes off the water and holds it wrong — the wire rope in coils has a shine that isn't rust but isn't metal either. The logging road a darker dark than the surrounding growth, the moss on it absorbing. The yarder mass against the sky. The coast doing what it does: the fetch, the swell interval, the platform lights offshore still burning for their own operational reasons. Something large using the logging road as a corridor — the sound of it using the road, then not using the road, then the road empty and the sound of the road empty."
          : "Coastal wilderness with extraction infrastructure going back to the land. A logging road ending at a collapsed turnaround, the surface gone to moss and the ruts still legible under it. Cable rigging left where it was last used, the wire rope rusting out in coils in the undergrowth. A donkey engine or yarder, the metal work orange-brown, the organic components of it long gone, the remaining mass substantial and strange in the vegetation. The coast itself unchanged — the same fetch, the same exposure, the Victorian inn that tried to make something of the view a different kind of ruin than the industrial equipment, but ruining at the same rate.";
      } else if (waterChar === 'lake') {
        brief = isNight
          ? "Mountain lake at night, the service economy gone. The floatplane dock extending into water that reflects nothing — the altitude and the tree line keeping the sky dark enough that the lake surface is just absence. The ranger station windows intact but the interior continuous with the cold outside. Something large in the drainage — the sound of it moving through water at intervals, the intervals not matching any pattern. The pass road empty. The lake at this elevation at night is not a place that was ever fully domesticated and the night makes that legible in a way the day partially obscures."
          : "Mountain lake, the service economy that grew around it gone. The floatplane dock still sound, the cleats still there, nothing tied to it. A ranger station or lodge with the weather working on it from every angle simultaneously — the altitude accelerating what lower elevation takes longer to do. The pass road still open but the reason to take it changed. The lake itself at altitude doing what high lakes do: cold, clear, the drainage running regardless.";
      } else if (roadDensity !== 'none') {
        brief = isNight
          ? "An extraction corridor at night, the road losing to the vegetation. The gate still locked across it. The blazes on the trees at intervals — pale wounds the trees have been closing for decades, visible when the light catches them, not visible when it doesn't. Something using the road as a corridor that finds the gate irrelevant. The equipment cache collapsed, the metal components in the undergrowth. The wilderness on both sides of the road doing what wilderness does at night, which is not nothing. The road itself a seam through it, still faintly legible, the vegetation not quite closed over it yet. Not yet."
          : "An extraction corridor into wilderness that stopped being used. The road still there but the road is losing the argument with the vegetation — not quickly, the way these arguments go, but consistently. A gate across it, locked, the lock rusted to the hasp. Survey blazes on the trees at intervals, the blazes old enough that the trees have grown around them, partially absorbed them. A equipment cache or line camp, the structure collapsed inward, the metal components rusting into the ground. The wilderness conducting its business on both sides of the road regardless.";
      } else {
        brief = isNight
          ? "Failed homestead at night, the wilderness reclaiming it. The fence posts still standing in a line into the dark, some with wire, some without. The root cellar open, the interior a specific cold, something using it. The clearing not quite a clearing — the shrub layer at the edges visible against the treeline, the treeline solid. The succession happening at night the same as during the day, which is to say constantly, which is to say the clearing is smaller than it was. The fence line going into the trees and continuing. Something following it, curious about the logic of it, not finding an answer."
          : "Failed homestead or grazing range, the wilderness taking it back at its own pace. A fence line, some of it still standing, running to the treeline and continuing as posts without wire into the trees. A root cellar or cache pit, the cover collapsed, the interior now a microhabitat — cooler than the surrounding ground, its own moisture regime. The grazing pressure visible in the soil compaction, the grass composition different inside the old fence line than outside it. A clearing that is slowly becoming not a clearing — the succession visible in the vegetation layers, the shrubs taller each year at the edges.";
      }
    } else {
      // SPARSE pre-tourist
      if (waterChar === 'lake') {
        brief = isNight
          ? "High alpine basin at night, the operation gone. The lake surface at altitude holds no light — the water black, the cold coming off it measurable. The corrugated structure still standing, the fasteners going, the walls moving independently of each other when the wind is enough. The cache box open on its rock. The cairns marking the route still there in the dark, each one a shape against the sky, the route they mark going somewhere the night makes irrelevant. The temperature drop since sundown has been significant and continues. The lake does not reflect the stars — the surface too disturbed by the drainage inflow, or too cold, or both."
          : "High alpine basin, the seasonal operation that used it gone. A mining claim or grazing allotment, the infrastructure at altitude doing what infrastructure does when the altitude has full access to it — the freeze-thaw working the foundations, the corrugated walls going at the fasteners first. The lake at this elevation dangerous in the specific ways high lakes are: cold enough year-round to matter, no fish above this elevation, the surface deceptively calm in the afternoon before the weather that was building since morning arrives. A cache box on the standard rock, the hasp open, the interior empty or nearly. The cairns still there, marking a route to something that no longer requires a route.";
      } else if (roadDensity === 'dense') {
        brief = isNight
          ? "A high town at night, most of it dark. The hospital lit — the trauma bay, the overnight staff, the parking lot lights. The main street with the bar still open, the bar doing what the bar does when it's the last thing open. The mountain not visible but present — the temperature, the wind off it, the road condition advisory already known before it posts. The empty blocks between the open things. The avalanche advisory on the board outside the ranger station, the board lit by a light on a timer, the light working fine. The advisory current. The mountain not caring either way."
          : "A high town that lost its reason. The trauma bay still staffed because trauma doesn't stop when the economy does, but the hospital census is down and has been. Buildings in the middle blocks empty, the main street holding on in the way main streets hold on — a few things still open, the open ones doing the work of pretending the others aren't closed. The mountain still there as operating condition: the avalanche advisory still posted, the pass closure date still relevant, the elevation still doing what elevation does to everything built at it. The infrastructure scaled for a larger population than is here now, which shows in the maintenance, which shows in the structures.";
      } else if (roadDensity === 'rural') {
        brief = isNight
          ? "A high pass at night, the station closed or unstaffed. The road still going through, the only road through, the headlights of the occasional vehicle the only light at this elevation. The avalanche terrain on both sides present in the dark as mass, as the specific silence of terrain that absorbs sound differently than open ground. The station log inside. A vehicle goes through without stopping. The brake lights at the switchback, then gone. The pass empty again."
          : "A high pass settlement or ranger station, the operation scaled back or closed. The station log still there, the last entry from whenever, the entries before it a record of the pass in all its moods — closure dates, incident reports, wildlife sightings, the handwriting changing as personnel changed. The pass still open or closed on its own schedule regardless. Avalanche terrain on both sides still avalanche terrain. The road still going through — still the only way through — the maintenance deferred in ways that are visible at the edges, the shoulders going, the signage faded to the point of suggestion.";
      } else {
        brief = isNight
          ? "High exposed terrain at night, mining infrastructure still in it. The adit entrance open, the interior air colder than outside, the cold coming out of it. The drill rod and compressor housing against the sky — the shapes of them. The radio frequency scratched into the rock at the entrance, not visible at night, still there. No light source at this elevation except what the sky provides, which at altitude is considerable — the stars doing real illumination work, casting real shadows, the stamp mill components each with a shadow. The adit breathing: the cold air out when the wind drops, the exchange when it picks up. Something in the exchange. Not wind."
          : "High exposed terrain, a mining operation or survey station, gone. The equipment too heavy to remove or not worth removing — stamp mill components, drill rod, a compressor housing. The metal work at altitude going in the specific way altitude metal goes: the UV doing what UV does at elevation, the freeze-thaw working every joint. A maintained cache that isn't maintained anymore, the contents picked through. The radio frequency still in the rock — scratched into the stone at the entrance to the adit — a frequency nobody is monitoring. The adit itself open, the timber sets still holding or not holding, the interior air a different temperature than outside.";
      }
    }
  }
  return brief;
}

const TEXT_FORMS = [
  "overheard fragment, no speaker identified",
  "text message, no sender or recipient",
  "classified ad, partial or truncated",
  "public service announcement, tone slightly off",
  "one-line yelp review",
  "news headline, no article follows",
  "clickbait link title",
  "single line of a diary entry",
  "hand-painted sign",
  "automated voicemail transcription, partial",
  "tweet from an account with no followers",
  "line from a pamphlet whose purpose is unclear",
  "advertisement for something that may not exist",
  "warning label, context missing",
  "radio dispatch, cut off",
  "government form field filled in incorrectly",
  "one-line personals ad",
  "note left under a windshield wiper",
  "single item on a list — shopping, inventory, supplies, requisition",
  "place name with a parenthetical annotation",
];
const MOVES_DAY = [
  "treat it as a proper noun — a business name, a road name, a person's name",
  "focus on the transaction rather than the thing: cost, duration, quantity, availability",
  "describe it from the perspective of someone who does not find it remarkable",
  "give it a price, a date, or a phone number",
  "state what it is not, in plain terms",
  "describe the bureaucratic or logistical fact of it",
  "use the vocabulary of instruction manuals or warranty cards",
  "make it an incomplete action: something started, pending, or overdue",
];
const MOVES_NIGHT = [
  "treat it as a proper noun — a name, a brand, a place",
  "make it the wrong category: a feeling described as a measurement, a location described as a warning",
  "describe it from the perspective of someone who does not find it remarkable, but they should",
  "combine it with something from an entirely different register — bureaucratic + organic, technical + emotional",
  "use it as the thing that is conspicuously absent rather than present",
  "let it be factually wrong in a specific, mundane way",
  "give it a price or quantity that doesn't make sense",
  "describe the logistical fact of something that cannot have logistics",
];
function cacheKey(terrain, waterChar, roadDensity, isNight, borderZone, deepWild) {
  return `${terrain}-${waterChar}-${roadDensity}-${isNight ? 1 : 0}-${borderZone ? 1 : 0}-${deepWild ? 1 : 0}`;
}
const FORMS_INTIMATE = [
  "missed connection ad, craigslist",
  "note never sent",
  "answering machine message, left by mistake",
  "single line of a letter that was never finished",
  "text typed but not sent, screenshot taken instead",
  "dedication page of a book no one published",
  "small ad in a local paper's lost & found",
  "voicemail from a number that's been disconnected",
  "caption under a photo that wasn't kept",
  "one-line entry in a guestbook no one reads",
];
const MOVES_INTIMATE = [
  "name the thing that is no longer true in a register that assumes it still is",
  "make it about waiting — not dramatically, just the ordinary fact of it",
  "let the wrong person be addressed",
  "describe something kept longer than it should have been",
  "make it an advertisement for something that no longer exists",
  "state a small fact about a specific time that has passed",
  "let it be about a light — a window, a sign, a screen, the kind that stays on",
  "describe a return to a place as if the place will also have returned",
  "give it the specificity of a thing that was important only to one person",
  "make it the trace of a habit — something still done even though the reason is gone",
];
const FORMS_DEEP_WILD = [
  "a physical fact with no frame around it",
  "something a body registers before the mind catches up",
  "a sound or smell stated without source or context",
  "evidence of process — growth, decay, movement, pressure",
  "a condition of the ground, air, water, or light",
  "what an animal would know that a person wouldn't notice",
  "a duration or interval with no human reference",
  "the aftermath of something with no record of the thing itself",
];
const MOVES_DEEP_WILD = [
  "make the land the subject — it acts, it has states, it moves",
  "no human observer — if a person is present they are just another organism",
  "give it the timescale of the non-human: slow, ongoing, indifferent",
  "state only what is physically true — no interpretation, no atmosphere",
  "let it be continuous — not an event but a condition that was already happening",
  "make the strangeness come from scale or duration, not from mystery",
  "something that would still be true if no one were there to register it",
];
function getRealSourcePalette(terrain, waterChar, roadDensity, borderZone, deepWild, isNight) {
  const isUrban  = terrain === T.LAND && roadDensity === 'dense';
  const isRural  = terrain === T.LAND && roadDensity === 'rural';
  const isCoast  = waterChar === 'coast';
  const isLake   = waterChar === 'lake';
  const isWild   = terrain === T.WILDERNESS;
  const isSparse = terrain === T.SPARSE;
  const P = {
    coast_d:    ['NOAA marine zone forecasts','Coast Guard notices to mariners','local coastal newspaper human interest coverage','fishing community news and trade publications','coastal real estate listings','travel magazine coastal destination pieces','regional news coverage of maritime incidents','tide and buoy condition reports','port authority bulletins','classified ads in a small coastal-town paper','estate sale and boat auction listings','local historical society pages about the harbor','church bulletin or community newsletter from a coastal parish'],
    coast_n:    ['Coast Guard overnight marine safety broadcasts','NOAA overnight coastal advisory statements','local news coverage of overnight coastal incidents','maritime weather bulletins','regional news late coverage of fishing and harbor activity','missed connections posted from a coastal bar or ferry dock'],
    lake_d:     ['Army Corps of Engineers water level reports','local newspaper coverage of lake communities','regional human interest stories about lakefront life','recreational boating advisories','EPA lake water quality monitoring bulletins','outdoor recreation magazine lake guides','local lakefront real estate listings','estate sale listing for a lake cottage','classified ad selling a dock or pontoon boat','genealogy forum thread about a family lake property','lake association community newsletter'],
    lake_n:     ['EPA overnight monitoring summaries','local news overnight coverage of lake incidents','inland waterway condition notices','Army Corps operational target reports'],
    deepwild_d: ['InciWeb wildfire incident reports','USFS backcountry closure orders','adventure publication narrative coverage of wilderness incidents','NPS wilderness condition advisories','regional news coverage of search and rescue operations','conservation organization newsletters','wildlife agency field condition notes','backcountry forum trip report thread'],
    deepwild_n: ['NWS overnight fire weather statements','regional news coverage of overnight wilderness emergencies','USFS overnight incident operations summaries','adventure publication late dispatches'],
    wild_d:     ['USFS trail condition notices','local newspaper coverage of outdoor recreation and trail life','regional human interest stories about wilderness and backcountry','adventure and outdoor recreation magazine narrative pieces','NPS backcountry alerts','InciWeb wildfire incident reports','conservation organization newsletters','gear review publications','regional news coverage of search and rescue operations','trail forum or hiking community board post','unhinged one-star review of a remote campground or lodge'],
    wild_n:     ['NWS overnight wilderness weather statements','regional news overnight coverage of backcountry incidents','USFS overnight operations reports','BLM closure and restriction notices'],
    sparse_d:   ['NWS high altitude zone forecasts','USGS seismic and geological activity reports','regional newspaper coverage of remote and high-desert communities','mountaineering and alpine recreation publications','local news human interest stories from sparse and remote areas','avalanche center condition reports','BLM high desert wilderness notices','adventure publication narrative pieces on high and remote terrain','mountaineering forum condition report thread','unhinged review of a high-altitude motel or gas station'],
    sparse_n:   ['NWS overnight mountain weather advisories','regional news overnight coverage of mountain and remote incidents','high altitude observatory condition logs','alpine rescue team incident summaries'],
    urban_d:    ['celebrity and entertainment news','tabloid and gossip publications','local alt-weekly arts and culture listings','lifestyle blog city roundups','local newspaper human interest coverage','regional news feature stories','Yelp neighborhood guides','EPA urban air quality index reports','municipal planning and zoning notices','local business journal coverage','classified ads or missed connections in an alt-weekly','unhinged one-star review of a restaurant or apartment building','city neighborhood forum or community board post','consumer complaint board filing against a local business','estate sale or moving liquidation listing'],
    urban_n:    ['celebrity gossip and tabloid columns','entertainment news and late-night coverage','local news overnight city coverage','nightlife and culture publication roundups','late-night entertainment recaps','municipal overnight transit and service notices','late-night missed connections posting','consumer complaint filed at 3am'],
    rural_d:    ['local newspaper human interest and community coverage','regional news feature stories about rural life','county extension service agricultural bulletins','hunting and fishing license and regulation notices','regional lifestyle magazine rural coverage','DOT road condition advisories','county fair and agricultural event announcements','rural real estate listings','classified ads in a county weekly paper','estate sale or farm auction listing','genealogy society newsletter or local history page','church bulletin from a rural congregation','community board or grange hall meeting notice'],
    rural_n:    ['local news overnight rural coverage','regional newspaper late reporting','state highway overnight road condition reports','volunteer fire department call summaries','DOT overnight incident and closure notices'],
    border_d:   ['regional news coverage of industrial and post-industrial communities','local newspaper human interest from border and transitional areas','EPA Superfund and brownfield site notices','state environmental remediation reports','regional labor and industry publications','rural economic development bulletins','liquidation auction listing for a closed plant or mill','local historical society page about the town before the closure','genealogy forum thread tracing a family through the mill town','classified ads in a shrinking local paper','consumer complaint board filing against a utility or landlord'],
    border_n:   ['local news overnight coverage of industrial areas','overnight environmental monitoring station reports','state environmental incident logs'],
    base_d:     ['federal register environmental and land use notices','USGS water resource reports','regional news human interest coverage','classified ads in a small regional paper','local genealogy or historical society page'],
    base_n:     ['NWS overnight regional weather statements','regional news overnight coverage'],
  };
  const suf = isNight ? '_n' : '_d';
  const pool = [];
  const add = k => { if(P[k+suf]) pool.push(...P[k+suf]); };
  if (isCoast)    add('coast');
  if (isLake)     add('lake');
  if (deepWild)   add('deepwild'); else if (isWild) add('wild');
  if (isSparse)   add('sparse');
  if (isUrban)    add('urban');
  if (isRural)    add('rural');
  if (borderZone && !deepWild) add('border');
  if (!isCoast && !isLake) add('base');
  const shuffled = pool.slice().sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(6, shuffled.length)).join('; ');
}
async function fetchRealFragment(terrain, waterChar, roadDensity, isNight, borderZone, deepWild) {
  const palette = getRealSourcePalette(terrain, waterChar, roadDensity, borderZone, deepWild, isNight);
  const timeRegister = isNight
    ? 'overnight institutional or late-night public-facing — terse, skeleton-crew, or the register of entertainment and celebrity media after hours'
    : 'daytime institutional or public-facing — routine monitoring, administrative notices, lifestyle and culture media, news and human interest filed during business hours';
  const sys = `You surface fragments from real web content for a fictional atlas. The fragment should feel archaeological — like something that was already in the world, briefly visible.
You will be given a source palette appropriate to the terrain and time of day. Your job:
1. Choose one source type from the palette that feels most generative for this context — not most obvious, most alive. News, human interest, and narrative sources are as valid as institutional ones.
2. Construct a specific search query for that source type. Be concrete enough to find real current content, and varied enough that repeated calls produce different results. Avoid generic queries.
3. Search and read what you find
4. Lift 3-12 words directly from the text — a true fragment, cut at effectively arbitrary points regardless of sentence boundaries, punctuation, or capitalization. Mid-sentence, mid-clause, mid-thought. The cut is the point. Do not smooth the edges or complete the thought.
5. Strip geographic place names (cities, states, named landmarks) that would locate the fragment in the real world. Retain everything else: timestamps, codes, zone identifiers, order numbers, prices, quantities, organizational abbreviations, personal names from news and entertainment sources.
6. Timestamps, codes, and non-geographic identifiers may survive into the fragment where they appear naturally — not as a requirement, only where present.
Do not draw from law enforcement records, court documents, medical records, or incident reports involving private individuals — even where these appear in news coverage.
Output only the raw fragment — no quotes, no explanation, no punctuation added at the edges`;
  const body = JSON.stringify({
    model: 'claude-sonnet-4-6',
    max_tokens: 100,
    tools: [{ type: 'web_search_20250305', name: 'web_search' }],
    system: sys,
    messages: [{ role: 'user', content: `Source palette: ${palette}
Time register: ${timeRegister}
#${Math.floor(Math.random() * 99999)}` }],
  });
  const res = await fetch('/api/messages', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
  if (!res.ok) throw new Error('real fragment API error');
  let data;
  try { data = await res.json(); } catch(e) { throw new Error('truncated: malformed JSON'); }
  if (!data.content || !Array.isArray(data.content)) throw new Error('truncated: no content');
  const text = data.content.filter(b => b.type === 'text').map(b => b.text || '').join('').trim();
  if (!text) throw new Error('truncated: empty');
  return text;
}
async function fetchBatch(terrain, waterChar, roadDensity, isNight, borderZone, deepWild, briefOverride) {
  const brief = briefOverride || getRegister(terrain, waterChar, roadDensity, isNight, borderZone, deepWild);
  const moves = isNight ? MOVES_NIGHT : MOVES_DAY;
  const intimateFires = !isNight && !deepWild;
  const intimateSlots = new Set();
  if (intimateFires) {
    const first = Math.random() * 6 | 0;
    intimateSlots.add(first);
    let second = Math.random() * 6 | 0;
    while (second === first) second = Math.random() * 6 | 0;
    intimateSlots.add(second);
  }
  const wildSlots = deepWild ? new Set([0,1,2,3,4,5]) : new Set();
  const formIdxs = [...Array(TEXT_FORMS.length).keys()].sort(() => Math.random() - 0.5).slice(0, 6);
  const moveIdxs = [...Array(moves.length).keys()].sort(() => Math.random() - 0.5).slice(0, 6);
  const pairs = formIdxs.map((fi, i) => {
    if (intimateSlots.has(i)) {
      const intimateForm = FORMS_INTIMATE[Math.random() * FORMS_INTIMATE.length | 0];
      const intimateMove = MOVES_INTIMATE[Math.random() * MOVES_INTIMATE.length | 0];
      return `${i+1}. form: ${intimateForm} | move: ${intimateMove}`;
    }
    if (wildSlots.has(i)) {
      const wf = FORMS_DEEP_WILD[Math.random() * FORMS_DEEP_WILD.length | 0];
      const wm = MOVES_DEEP_WILD[Math.random() * MOVES_DEEP_WILD.length | 0];
      return `${i+1}. form: ${wf} | move: ${wm}`;
    }
    return `${i+1}. form: ${TEXT_FORMS[fi]} | move: ${moves[moveIdxs[i] % moves.length]}`;
  }).join("\n");
  const intimateSlotList = [...intimateSlots].sort((a,b)=>a-b).map(i => i+1).join(' and ');
  const intimateNote = intimateFires
    ? `\nTwo fragments (slots ${intimateSlotList}) use an intimate register — wistful, personal, quietly elegiac. The light that stays on in a vacant window. The neon sign where the "no" burned out years ago and no one fixed it. A missed connection. Not sentimental — specific and slightly wrong in the way memory is. Same world as the other fragments, different frequency. The two intimate fragments should not echo each other — different situations, different kinds of ache.`
    : '';
  const daySys = `You write fragments for "Provisional Pocket Atlas of Minor Worlds," a fictional atlas.
Place context — inhabit this situation before writing:
${brief}
You will produce exactly 6 fragments. Each fragment:
- is 3–12 words
- uses its assigned form and transformation move as a starting point — push past the obvious interpretation, find the unexpected angle within the constraint
- draws specific sensory, transactional, or logistical detail from the place context — not generic atmosphere; do not quote or paraphrase the brief directly
- if the place context contains a single vivid concrete detail (a date, a name, an object, an image), treat it as an illustration of the kind of specificity wanted, not a fact to reuse — invent your own comparably specific detail instead. The brief is reused across many calls; repeating its exact details produces a recognizable, repetitive fragment pool
- feels like something that actually exists in the world
- specific and grounded; the strangeness comes from precision, not poeticism
- no words: forgotten, whisper, echo, shadow, ancient, dissolve, liminal
- no construction "X (the kind that...)" or "X (the kind where...)"${intimateNote}${deepWild ? "\n\nSome slots: the land is the subject, not the object. No observer frame. No atlas voice. The processes here are continuous and do not require witness. Write from inside them — physical, specific, non-human in scale. The human if present is incidental and knows it." : ""}
The 6 fragments must contrast with each other in form, length, and approach. No two should feel like the same kind of artifact. You are generating variety, not consistency.
Return exactly this format — a numbered list, one fragment per line, nothing else:
1. [fragment]
2. [fragment]
3. [fragment]
4. [fragment]
5. [fragment]
6. [fragment]`;
  const nightSys = `You write fragments for "Provisional Pocket Atlas of Minor Worlds," a fictional atlas.
Place context — inhabit this situation before writing:
${brief}
You will produce exactly 6 fragments. Each fragment:
- is 3–12 words
- uses its assigned form and transformation move as a starting point — push past the obvious interpretation, find the unexpected angle within the constraint
- draws from the place context, but filtered through 3am: private thought, the weight of something true that shouldn't be written down; do not quote or paraphrase the brief directly
- if the place context contains a single vivid concrete detail (a date, a name, an object, an image), treat it as an illustration of the kind of specificity wanted, not a fact to reuse — invent your own comparably specific detail instead. The brief is reused across many calls; repeating its exact details produces a recognizable, repetitive fragment pool
- something confessed in the wrong format: grief in a PSA, shame in a classified ad, dread in a yelp review — these are examples of the mode, not a fixed menu
- not funny-surreal, not whimsical, not horror — dark, psychological, intimate
- strangeness from pressure on ordinary things, not invented weirdness
- no words: forgotten, whisper, echo, shadow, ancient, dissolve, liminal, haunted
- no construction "X (the kind that...)" or "X (the kind where...)"${deepWild ? "\n\nSome slots: the land at night is the same land. It does not perform darkness. Write from inside the continuous processes — drainage, growth, decomposition, animal movement. No observer. No witness. Physical and specific." : ""}
The 6 fragments must contrast with each other in form, length, and approach. No two should feel like the same kind of artifact.
Return exactly this format — a numbered list, one fragment per line, nothing else:
1. [fragment]
2. [fragment]
3. [fragment]
4. [fragment]
5. [fragment]
6. [fragment]`;
  const usr = `Form + move assignments:\n${pairs}\n\nWrite the 6 fragments now.`;
  const body = JSON.stringify({
    model: "claude-sonnet-4-6", max_tokens: 600,
    system: isNight ? nightSys : daySys,
    messages: [{ role: "user", content: usr }],
  });
  const text = await safeParseAPIWithRetry(() => fetch("/api/messages", {
    method: "POST", headers: { "Content-Type": "application/json" }, body,
  }));
  const fragments = text.split("\n")
    .map(l => l.match(/^\d+\.\s+(.+)$/))
    .filter(Boolean)
    .map(m => m[1].trim())
    .filter(f => f.length > 0);
  if (fragments.length === 0) throw new Error("batch parse failed");
  for (let i = fragments.length - 1; i > 0; i--) {
    const j = Math.random() * (i + 1) | 0;
    [fragments[i], fragments[j]] = [fragments[j], fragments[i]];
  }
  return fragments;
}
async function callAPISingle(terrainType, timeInfo, destination, spatialCtx) {
  const isNight = timeInfo.h >= 19 || timeInfo.h < 6;
  const brief = spatialCtx
    ? getRegister(terrainType, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, spatialCtx.borderZone || false, spatialCtx.deepWild || false)
    : "Indeterminate ground. No landmarks. Unclear how long you\'ve been here.";
  const moves = isNight ? MOVES_NIGHT : MOVES_DAY;
  const move = moves[Math.floor(Math.random() * moves.length)];
  const imperativeForms = [
    "a directional sign, terse",
    "a one-line instruction, no context given",
    "an imperative sentence addressed to someone moving through space",
    "a warning posted at a threshold",
    "a line from a set of directions, mid-sequence",
  ];
  const form = imperativeForms[Math.floor(Math.random() * imperativeForms.length)];
  const dayInstr = "Documentary, not literary. Specific and grounded. The strangeness comes from precision.";
  const nightInstr = "Dark, psychological, intimate. The destination shapes an undercurrent of dread or private knowledge — not horror, not whimsy. Something confessed in the wrong format.";
  const sys = `You write a single fragment for "Provisional Pocket Atlas of Minor Worlds."
Place context:
${brief}
Form: ${form}. Transformation move: ${move}. 3–12 words.
The fragment is a direction or instruction — imperative, addressed to someone in motion toward: "${destination}".
The destination shapes the tone without being named literally.
${isNight ? nightInstr : dayInstr}
Output only the fragment.`;
  const singleBody = JSON.stringify({
    model: "claude-sonnet-4-6", max_tokens: 80, system: sys,
    messages: [{ role: "user", content: "Write it now." }],
  });
  return await safeParseAPIWithRetry(() => fetch("/api/messages", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: singleBody,
  }));
}
const ROAD_COLOR = "#486860";
function Crosshair({ px, py, pal, fading, isRoute, isReport, loading }) {
  const routeCol  = pal?.isNight ? ROUTE_COLOR_NIGHT : ROUTE_COLOR_DAY;
  const reportCol = pal?.isNight ? '#c8a040' : '#8a5c10';
  const accent = isReport ? reportCol : isRoute ? routeCol : (pal?.road || ROAD_COLOR);
  return (
    <div style={{
      position:"absolute", left:px-9, top:py-9, pointerEvents:"none", zIndex:9,
      opacity: fading ? 0 : 1,
      transition: fading ? "opacity 1.5s ease-out" : "opacity 0.2s ease-in",
      animation: loading ? "xhair-pulse 1.1s ease-in-out infinite" : "none",
    }}>
      <svg width={18} height={18}>
        <line x1={9} y1={1} x2={9} y2={17} stroke={accent} strokeWidth={2.5}/>
        <line x1={1} y1={9} x2={17} y2={9} stroke={accent} strokeWidth={2.5}/>
      </svg>
    </div>
  );
}
function coordTag(gx, gy) {
  const lat = (49.8 - gy * 0.031).toFixed(1);
  const lon = (101.2 + gx * 0.028).toFixed(1);
  const prec = (0.002 + ((gx * 7 + gy * 13) % 17) * 0.0004).toFixed(3);
  return `${lat}°N · ${lon}°W ±${prec}`;
}
function Card({ px, py, gx, gy, text, loading, error, dataLoss, W, H, pal, fading, isRoute, isReport, reportLabel, forcedLeft, forcedTop, lifetime=20000, spatialCtx, debugCtx }) {
  const left = forcedLeft !== undefined ? forcedLeft : (px+14+CARD_W > W-8 ? px-CARD_W-14 : px+14);
  const top  = forcedTop  !== undefined ? forcedTop  : Math.max(8, Math.min(py-18, H-150));
  const isNight  = pal?.isNight;
  const bg       = isNight ? "#1e2a1e" : "#eaf0ea";
  const fg       = isNight ? "#a0b8b0" : "#2a3c38";
  const routeCol = pal?.isNight ? ROUTE_COLOR_NIGHT : ROUTE_COLOR_DAY;
  const reportCol = pal?.isNight ? "#c8a040" : "#8a5c10";
  const AMBER    = pal?.isNight ? "#b87820" : "#c8860a";
  const border   = dataLoss ? AMBER : error ? "#c05030" : isReport ? reportCol : isRoute ? routeCol : (pal?.road || ROAD_COLOR);
  const lifetimeSec = (lifetime / 1000).toFixed(1);
  return (
    <div style={{
      position:"absolute", left, top, width:CARD_W,
      background:bg, border:`2px solid ${border}`,
      padding:"8px 10px", fontFamily:'"Courier New",Courier,monospace',
      fontSize:13, lineHeight:1.45, whiteSpace:"pre-wrap", wordBreak:"break-word",
      pointerEvents:"none", zIndex:10, color:dataLoss ? (pal?.isNight ? "#b87820" : "#c8860a") : error?"#c84040":fg,
      opacity: fading ? 0 : 1,
      transition: fading ? "opacity 1.2s ease-in" : `opacity 0.2s ease-in`,
      animation: "none",
    }}>
      {isReport && reportLabel && !error && (
        <div style={{ fontSize:9, letterSpacing:"0.12em", color:reportCol, marginBottom:5, opacity:0.85 }}>
          {reportLabel.toUpperCase()}
        </div>
      )}
      {loading ? "···" : dataLoss ? dataLoss : (error || text)}
      {!loading && !error && !dataLoss && gx != null && gy != null && (
        <div style={{ fontSize: 8, letterSpacing: '0.08em', marginTop: 6, opacity: 0.45, color: fg }}>
          {coordTag(gx, gy)}
        </div>
      )}
      {debugCtx && spatialCtx && (
        <div style={{ fontSize: 8, letterSpacing: '0.08em', marginTop: 3, opacity: 0.55, color: fg, borderTop: `1px solid ${border}`, paddingTop: 3 }}>
          {['WATER','LAND','WILD','SPARSE'][spatialCtx.terrain ?? -1] ?? '?'} · {spatialCtx.waterChar} · {spatialCtx.roadDensity}{spatialCtx.borderZone ? ' · border' : ''}{spatialCtx.deepWild ? ' · deep' : ''}
        </div>
      )}
    </div>
  );
}
async function callAPIWeatherAlert(imageB64, placeFocus, terrainMix, timeStr, isNight, anchoredReports) {
  const placeStr = placeFocus.length ? placeFocus.join(', ') : 'unnamed region';
  const reportStr = anchoredReports.length
    ? anchoredReports.map(r => `- ${r}`).join('\n')
    : '';
  const FOCUS_ANGLES = [
    'a pressure reading at a specific named location',
    'a single named location and one thing that is wrong there',
    'a visibility condition affecting movement between named places',
    'something that has changed since the last report period',
    'a front or boundary condition — what it is, where it is, what to do',
    'an anomaly in one specific named place only',
    'conditions at the water margin',
    'what the named places are reporting to each other',
  ];
  const focusAngle = FOCUS_ANGLES[Math.floor(Math.random() * FOCUS_ANGLES.length)];
  const sys = `You write regional weather alerts in a quasi-meteorological register: fronts, visibility, pressure, advisories. Terse. Specific. One thing, fully committed.
This alert is about: ${focusAngle}.
Use the place names given. Do not survey the whole region — stay inside the one condition. No abstraction, no summary, no overview. If a field report exists, absorb it directly into the alert language.
No words: luminous, luminescent, liminal, boundary conditions, bracketed zones. Do not describe what the instrument is doing.
Write 30–50 words. End with a blank line then exactly: ---
Then one CTA: 1–3 words. The CTA is a button label that does not match the alert above it — borrowed from a different form, a different context, a different era. Not an acknowledgment of the content. Not thematically resonant. Drawn from: legal filings, cooking instructions, assembly manuals, liturgy, medical intake forms, deposition transcripts, immigration paperwork, quality control checklists, swimming lesson handouts. The wrongness is the point. Do not use any folding instruction. Examples of the register (do not reuse these): recant, emulsify until smooth, assemble, firmly grasp, testify, confirm expungement, renumerate, heed, equivocate, initial here, proceed to gate, debone carefully.`;
  const usr = `${isNight ? 'Night' : 'Day'}. ${timeStr}.
Places: ${placeStr}.${reportStr ? '\nReports:\n' + reportStr : ''}`;
  const res = await fetch('/api/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: 200,
      system: sys,
      messages: [{
        role: 'user',
        content: [{ type: 'text', text: usr }],
      }],
    }),
  });
  const raw = await safeParseAPI(res);
  const sepIdx = raw.lastIndexOf('---');
  const alertText = (sepIdx > 0 ? raw.slice(0, sepIdx) : raw).trim();
  const cta = (sepIdx > 0 ? raw.slice(sepIdx + 3) : 'acknowledge').trim().replace(/[*"]/g, '').toLowerCase();
  return { alertText, cta };
}
function WeatherModalPanel({ alertText, cta, timeStr, amber, isNight, licenseHex, onDismiss }) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    let i = 0;
    const tick = setInterval(() => {
      if (i >= alertText.length) { clearInterval(tick); setDone(true); return; }
      setDisplayed(alertText.slice(0, i + 1));
      i++;
    }, 28);
    return () => clearInterval(tick);
  }, [alertText]);
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 70,
      background: isNight ? 'rgba(8,14,10,0.88)' : 'rgba(20,30,24,0.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: isNight ? '#1a2420' : '#eaf0ea',
        border: `2px solid ${amber}`,
        padding: '28px 32px', maxWidth: 420, width: '90%',
        fontFamily: '"Courier New",Courier,monospace',
      }}>
        <div style={{ fontSize: 9, color: amber, letterSpacing: '0.15em', marginBottom: 14 }}>
          REGIONAL WEATHER ALERT — {timeStr}
        </div>
        <div style={{ fontSize: 7, color: amber, letterSpacing: '0.14em', opacity: 0.55, marginBottom: 12 }}>
          ANOMALOUS AFFECTIVE READING — TERMINAL {licenseHex}
        </div>
        <div style={{
          fontSize: 13, lineHeight: 1.65, color: isNight ? '#a0b8b0' : '#2a3c38',
          minHeight: 80, whiteSpace: 'pre-wrap', marginBottom: 20,
        }}>
          {displayed}
          {!done && <span style={{ opacity: 0.5 }}>▌</span>}
        </div>
        {done && (
          <button onClick={onDismiss} style={{
            width: '100%', padding: '10px 0',
            background: amber, border: 'none', color: '#1a1a1a',
            fontFamily: '"Courier New",Courier,monospace',
            fontSize: 13, letterSpacing: '0.12em', cursor: 'pointer',
            textTransform: 'uppercase',
          }}>
            {cta}
          </button>
        )}
      </div>
    </div>
  );
}
export default function Atlas() {
  const canvasRef = useRef(null);
  const fragCache = useRef({});
  const apiInFlight = useRef(0);
  const pendingFetches = useRef({});
  const routeTimers = useRef([]);
  const weatherPaused = useRef(false);
  const alertTriggeredRef = useRef(-1);
  const triggerWeatherAlertRef = useRef(null);
  const firstAlertFiredRef = useRef(false);
  const [weatherAlert, setWeatherAlert] = useState(null);
  const [weatherToastVisible, setWeatherToastVisible] = useState(false);
  const audioRoot   = useRef(null);
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight - HEADER_H });
  const cols = Math.ceil(dims.w / PIXEL);
  const rows = Math.ceil(dims.h / PIXEL);
  const [genSeed, setGenSeed] = useState(() => (Math.random() * 9999) | 0);
  const [cells,    setCells]    = useState(null);
  const [dist,     setDist]     = useState(null);
  const [roads,    setRoads]    = useState([]);
  const [visibleRoads, setVisibleRoads] = useState([]);
  const allRoadsRef       = useRef([]);
  const gapCloserStartRef = useRef(0);
  const spineGridRef      = useRef(null);
  const roadRevealTimer   = useRef(null);
  const roadGridRef = useRef(null);
  const [timeMins, setTimeMins] = useState(null);
  const [pal,     setPal]     = useState(null);
  const [markers, setMarkers] = useState([]);
  const [route,   setRoute]   = useState(null);
  const [routeFading, setRouteFading] = useState(false);
  const [modal,   setModal]   = useState(false);
  const [dest,    setDest]    = useState("");
  const [routeLoading, setRouteLoading] = useState(false);
  const [debugCtx, setDebugCtx] = useState(false);
  const [debugAbandon, setDebugAbandon] = useState(false);
  const debugAbandonRef = useRef(false);
  const [debugRegions, setDebugRegions] = useState(0); // 0=off 1=sub-regions 2=macro-regions
  const [debugVisible, setDebugVisible] = useState(false);
  useEffect(() => { debugAbandonRef.current = debugAbandon; }, [debugAbandon]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setDebugVisible(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [reportModal, setReportModal] = useState(false);
  const [reportStep, setReportStep]   = useState('select');
  const [reportEvent, setReportEvent] = useState(null);
  const [reportChoices, setReportChoices] = useState({});
  const [reportNotes, setReportNotes]   = useState('');
  const [reportMarkers, setReportMarkers] = useState([]);
  const [regions, setRegions] = useState(null);
  const [toponyms, setToponyms] = useState({});
  const setToponymsSync = (updater, regionCx, regionCy) => {
    setToponyms(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      toponymsRef.current = next;
      if (regionCx != null && regionCy != null) detuneNearby(regionCx, regionCy);
      return next;
    });
  };
  const regionFrags = useRef({});
  const macroClicks  = useRef({});
  const regionPins  = useRef({});
  const pendingToponyms = useRef(new Set());
  const pendingHydros   = useRef(new Set());
  const hydroPins   = useRef({});
  const driftTimer  = useRef(null);
  const toponymsRef = useRef({});
  const labelPositions = useRef({});
  const [hydroRegions, setHydroRegions] = useState(null);
  const [hydronyms, setHydronyms] = useState({});
  const hydronymsRef = useRef({});
  const hydroFrags = useRef({});
  const hydroDriftTimer = useRef(null);
  const setHydronymsSync = (updater, hydroCx, hydroCy) => {
    setHydronyms(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      hydronymsRef.current = next;
      if (hydroCx != null && hydroCy != null) chorusNearby(hydroCx, hydroCy);
      return next;
    });
  };
  useEffect(() => {
    const onResize = () => setDims({ w: window.innerWidth, h: window.innerHeight - HEADER_H });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    const c = buildMap(genSeed, cols, rows);
    const d = buildDistField(c, cols, rows);
    const { roads: allRoads, gapCloserStart } = buildRoads(genSeed, c, d, cols, rows);
    const rg = buildRoadGrid(allRoads, cols, rows);
    const start = realStartTime();
    const reg = buildRegions(genSeed, c, cols, rows);
    const hreg = buildHydroRegions(genSeed, c, cols, rows);
    const root = mapRootNote(genSeed);
    audioRoot.current = root;
    gapCloserStartRef.current = gapCloserStart;
    allRoadsRef.current = allRoads;
    roadGridRef.current = rg;
    spineGridRef.current = buildRoadGrid(allRoads.slice(0, gapCloserStart), cols, rows);
    setCells(c); setDist(d); setRoads(allRoads); setRegions(reg); setHydroRegions(hreg);
    setVisibleRoads(allRoads.slice(0, gapCloserStart));
    setTimeMins(start); setPal(getPalette(start));
    setMarkers([]); setRoute(null); setReportMarkers([]);
    setToponymsSync({}); regionFrags.current = {}; toponymsRef.current = {}; labelPositions.current = {};
    setHydronymsSync({}); hydroFrags.current = {}; hydronymsRef.current = {};
    regionPins.current = {}; hydroPins.current = {};
    pendingToponyms.current.clear(); pendingHydros.current.clear();
    fragCache.current = {}; pendingFetches.current = {}; routeTimers.current.forEach(clearTimeout);
    firstAlertFiredRef.current = false;
    attnRef.current = {}; abandonedRegions.current = new Set();
    regionSuffixes.current = {}; gapCloserDecay.current = [];
    totalInGameMins.current = 0; macroClicks.current = {};
    roadsGoneAt.current = {}; touristFired.current = new Set(); namedAt.current = {};
    if (audioReady) buildDrone(genSeed, isNightTime(start));
  }, [genSeed, cols, rows]);
  useEffect(() => {
    if (timeMins === null) return;
    const id = setInterval(() => {
      if (weatherPaused.current) return;
      setTimeMins(prev => {
        const next = (prev + 5) % 1440;
        const wasNight = isNightTime(prev);
        const nowNight = isNightTime(next);
        if (wasNight !== nowNight) {
          fragCache.current = {}; pendingFetches.current = {};
          updateDroneNight(nowNight);
        }
        setPal(getPalette(next));
        updateDroneGrit(blendFactor(next));
        totalInGameMins.current += 5;
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timeMins !== null]);
  // Post-abandonment interval — runs outside state setter so it can call state setters
  useEffect(() => {
    const id = setInterval(() => {
      if (!regions || !cells || !dist) return;
      const TOURIST_DELAY_MS = debugAbandonRef.current ? 30000 : 60000;
      const ERASURE_DELAY_MS = debugAbandonRef.current ? 60000 : 300000;
      const nowMs = Date.now();
      for (const rid of abandonedRegions.current) {
        if (touristFired.current.has(rid)) continue;
        if (!roadsGoneAt.current[rid]) { continue; }
        const elapsed = nowMs - roadsGoneAt.current[rid];
        const attn = attnRef.current[rid] || 0;
        const r = regions.regionData[rid];
        if (!r) continue;
        const interestAt = touristInterest.current[rid] || 0;
        const hasTouristInterest = interestAt > 0;
        if (elapsed >= TOURIST_DELAY_MS && hasTouristInterest) {
          const cx2 = Math.round(r.cx), cy2 = Math.round(r.cy);
          const rg2 = roadGridRef.current;
          if (!rg2) continue;
          // build live cell set: spine cells + cells from living spur/gc segments
          const liveCells = new Set();
          const spineSegs2 = allRoadsRef.current.slice(0, gapCloserStartRef.current);
          for (const seg of spineSegs2) {
            if (!Array.isArray(seg)) continue;
            for (const pt of seg) liveCells.add(pt.y * cols + pt.x);
          }
          const DEAD_T2 = 0.78;
          const gcSegs2 = allRoadsRef.current.slice(gapCloserStartRef.current).filter(Array.isArray);
          for (let gi = 0; gi < gcSegs2.length; gi++) {
            if ((gapCloserDecay.current[gi] || 0) >= DEAD_T2) continue;
            for (const pt of gcSegs2[gi]) liveCells.add(pt.y * cols + pt.x);
          }
          for (const seg of grownSegments.current) {
            if (seg.decay >= DEAD_T2) continue;
            for (const pt of seg.pts) liveCells.add(pt.y * cols + pt.x);
          }
          // find nearest live road cell at least 8 cells from centroid
          let bestSrc = null, bestD = Infinity;
          for (let dy2 = -55; dy2 <= 55; dy2++) {
            for (let dx2 = -55; dx2 <= 55; dx2++) {
              const nx2 = cx2+dx2, ny2 = cy2+dy2;
              if (nx2<0||ny2<0||nx2>=cols||ny2>=rows) continue;
              if (!liveCells.has(ny2*cols+nx2)) continue;
              const d2 = Math.hypot(dx2, dy2);
              if (d2 < 8) continue;
              if (d2 < bestD) { bestD = d2; bestSrc = {x:nx2,y:ny2}; }
            }
          }
          if (!bestSrc) continue;
          const landTgt = cells[cy2]?.[cx2] === T.WATER
            ? nearestLand(cx2, cy2, cells, cols, rows) : {x:cx2,y:cy2};
          if (Math.hypot(bestSrc.x - landTgt.x, bestSrc.y - landTgt.y) < 4) continue;
          touristFired.current.add(rid); // only mark fired once a valid spur will grow
          const capturedRid = rid, capturedSrc = bestSrc, capturedTgt = landTgt;
          setTimeout(() => {
            if (!roadGridRef.current || !cells || !dist) return;
            const rawPts2 = flowWalk(capturedSrc, capturedTgt, cells, dist, cols, rows,
              genSeed + capturedRid * 317, 0.8, 300, 0.35, 3);
            if (rawPts2.length < 2) return;
            const touristSeg = { pts: [], birthTime: Date.now(), decay: 0, rid: capturedRid };
            grownSegments.current.push(touristSeg);
            let ri2 = 0;
            const touristSuffixPool = ['HISTORIC SITE MARKER', 'STATE HERITAGE AREA', 'SCENIC BYWAY', 'INTERPRETIVE AREA', 'HERITAGE CORRIDOR', 'CONSERVATION AREA', 'POINTS OF INTEREST'];
            const tick2 = setInterval(() => {
              if (!roadGridRef.current) { clearInterval(tick2); return; }
              if (ri2 >= rawPts2.length) {
                clearInterval(tick2);
                // Reclassify suffix to tourist phase on spur completion
                regionSuffixes.current[capturedRid] = touristSuffixPool[(Math.random() * touristSuffixPool.length) | 0];
                return;
              }
              const pt2 = rawPts2[ri2++];
              touristSeg.pts.push(pt2);
              roadGridRef.current[pt2.y * cols + pt2.x] = 1;
              setGrowVersion(v => v+1);
            }, 1000);
          }, 3000);
        } else if (elapsed >= ERASURE_DELAY_MS && attn <= 0.05 && !hasTouristInterest) {
          setToponymsSync(prev => { const n = {...prev}; delete n[rid]; return n; });
          regionFrags.current[rid] = [];
          regionPins.current[rid] = [];
          const mid2 = regions.macroOf?.[rid];
          if (mid2 !== undefined) macroClicks.current[mid2] = [];
          abandonedRegions.current.delete(rid);
          delete regionSuffixes.current[rid];
          delete roadsGoneAt.current[rid];
          delete touristInterest.current[rid];
        }
      }
    }, 5000);
    return () => clearInterval(id);
  }, [cells, dist, cols, rows, genSeed, regions, dims]);

  // Abandonment tick — runs every 5s to avoid blocking the clock tick with heavy segment scans
  useEffect(() => {
    const id = setInterval(() => {
      if (!regions) return;
      const namedCount = Object.keys(toponymsRef.current).length;
      const LATE_GATE = debugAbandonRef.current || (namedCount >= 4);
      if (!LATE_GATE) return;
      const rd = regions.regionData;
      const gcSegs = allRoadsRef.current.slice(gapCloserStartRef.current).filter(Array.isArray);
      // attention drain
      for (const rid of Object.keys(attnRef.current)) {
        if (attnRef.current[rid] > 0)
          attnRef.current[rid] = Math.max(0, attnRef.current[rid] - 0.06); // 5x per tick since 5s interval
      }
      const now2 = Date.now();
      const MATURITY_MS = debugAbandonRef.current ? 15000 : 180000;
      const BASE_RATE = debugAbandonRef.current ? 0.03 : 0.012; // 5x per tick since 5s interval
      const ATTN_MULT = 1.6;
      const DEAD_T = 0.78;
      // Revalidate bridge immunity: an immune spur only stays immune while both its
      // endpoints remain anchored to live network. If either end is stranded (its
      // connecting roads have died), immunity is revoked and the spur decays normally.
      {
        const sg = spineGridRef.current;
        const liveGrown = grownSegments.current.filter(s => s.decay >= 0 && s.decay < DEAD_T && s.rid !== -1);
        const liveGC = allRoadsRef.current.slice(gapCloserStartRef.current).filter(Array.isArray)
          .filter((seg, gi) => (gapCloserDecay.current[gi] || 0) < DEAD_T);
        const ANCHOR_R = 3;
        const anchoredToLive = (pt, selfSeg) => {
          // spine anchor
          for (let dy = -ANCHOR_R; dy <= ANCHOR_R; dy++) for (let dx = -ANCHOR_R; dx <= ANCHOR_R; dx++) {
            const nx = pt.x+dx, ny = pt.y+dy;
            if (nx<0||ny<0||nx>=cols||ny>=rows) continue;
            if (sg && sg[ny*cols+nx]) return true;
          }
          // live grown spur anchor (excluding self)
          for (const s of liveGrown) {
            if (s === selfSeg) continue;
            for (const p of s.pts) if (Math.hypot(p.x-pt.x, p.y-pt.y) <= ANCHOR_R) return true;
          }
          // live gap-closer anchor
          for (const seg of liveGC) {
            for (const p of seg) if (Math.hypot(p.x-pt.x, p.y-pt.y) <= ANCHOR_R) return true;
          }
          return false;
        };
        for (const seg of grownSegments.current) {
          if (seg.decay !== -1 || !seg.pts || seg.pts.length < 2) continue;
          const startAnchored = anchoredToLive(seg.pts[0], seg);
          const endAnchored = anchoredToLive(seg.pts[seg.pts.length-1], seg);
          if (!startAnchored || !endAnchored) {
            console.log('[immunity-revoke] rid:', seg.rid, 'startAnchored:', startAnchored, 'endAnchored:', endAnchored, 'len:', seg.pts.length);
            seg.decay = 0; // revoke immunity — bridge no longer bridges live network
          }
        }
      }
      // decay spurs
      for (const seg of grownSegments.current) {
        if (seg.decay < 0 || seg.rid === -1) continue;
        if (seg.decay >= 1) continue;
        if ((now2 - seg.birthTime) < MATURITY_MS) continue;
        const attn = seg.rid !== undefined ? (attnRef.current[seg.rid] || 0) : 0;
        const protection = Math.min(1, attn * ATTN_MULT);
        seg.decay = Math.min(1, seg.decay + BASE_RATE * (1 - protection) * (1 + seg.decay * 3.0));
      }
      // decay ferries
      for (const ferry of ferrySegments.current) {
        if ((ferry.decay || 0) < 0) continue; // immune
        if ((ferry.decay || 0) >= 1) continue;
        if ((now2 - ferry.birthTime) < MATURITY_MS) continue;
        const attn = ferry.rid >= 0 ? (attnRef.current[ferry.rid] || 0) : 0;
        const protection = Math.min(1, attn * ATTN_MULT);
        const d0 = ferry.decay || 0;
        ferry.decay = Math.min(1, d0 + BASE_RATE * (1 - protection) * (1 + d0 * 3.0));
      }
      // decay gap-closers
      const gcBirth = gapCloserDecay.current.birthTime || 0;
      for (let i = 0; i < gcSegs.length; i++) {
        if ((gapCloserDecay.current[i] || 0) >= 1) continue;
        if (gcBirth && (now2 - gcBirth) < MATURITY_MS) continue;
        const seg = gcSegs[i];
        const mid = seg[seg.length >> 1];
        if (!mid) continue;
        let best = Infinity, attn = 0;
        for (const r of rd) {
          if (!toponymsRef.current[r.id]) continue;
          const d = Math.hypot(r.cx - mid.x, r.cy - mid.y);
          if (d < best) { best = d; attn = attnRef.current[r.id] || 0; }
        }
        const protection = Math.min(1, attn * ATTN_MULT);
        const d0 = gapCloserDecay.current[i] || 0;
        gapCloserDecay.current[i] = Math.min(1, d0 + BASE_RATE * (1 - protection) * (1 + d0 * 3.0));
      }
      // contagious spread
      const SPREAD_R = 3;
      const spreadChunk = 0.08;
      const spreadProb = 0.06; // 5x since 5s interval
      const cellToSegs = {};
      const addCells = (pts, type, i) => {
        for (const pt of pts) {
          const k = pt.x + pt.y * 10000;
          if (!cellToSegs[k]) cellToSegs[k] = [];
          cellToSegs[k].push({ type, i });
        }
      };
      for (let i = 0; i < gcSegs.length; i++) {
        if ((gapCloserDecay.current[i] || 0) < DEAD_T) addCells(gcSegs[i], 'gc', i);
      }
      for (let i = 0; i < grownSegments.current.length; i++) {
        const s2 = grownSegments.current[i];
        if (s2.decay < 0 || s2.rid === -1) continue;
        if (s2.decay < DEAD_T) addCells(s2.pts, 'gs', i);
      }
      const trySpread = (deadPts) => {
        if (Math.random() > spreadProb) return;
        const pt = deadPts[(Math.random() * deadPts.length) | 0];
        if (!pt) return;
        const hits = new Set();
        for (let dy = -SPREAD_R; dy <= SPREAD_R; dy++) {
          for (let dx = -SPREAD_R; dx <= SPREAD_R; dx++) {
            const k = (pt.x + dx) + (pt.y + dy) * 10000;
            for (const nb of (cellToSegs[k] || [])) hits.add(nb.type + ':' + nb.i);
          }
        }
        for (const key of hits) {
          const [type, si] = key.split(':');
          const idx = Number(si);
          if (type === 'gc') gapCloserDecay.current[idx] = Math.min(1, (gapCloserDecay.current[idx] || 0) + spreadChunk);
          else if (grownSegments.current[idx].decay >= 0 && grownSegments.current[idx].rid !== -1)
            grownSegments.current[idx].decay = Math.min(1, grownSegments.current[idx].decay + spreadChunk);
        }
      };
      for (let i = 0; i < gcSegs.length; i++) {
        if ((gapCloserDecay.current[i] || 0) >= DEAD_T) trySpread(gcSegs[i]);
      }
      for (const seg of grownSegments.current) {
        if (seg.decay >= DEAD_T) trySpread(seg.pts);
      }
      // connection check and abandonment trigger
      const CONN_R = 15;
      const SUFFIXES = ['(SITE)', '(ABANDONED)', '(GHOST TOWN)', 'TOWNSITE', '(UNINCORPORATED)', 'FORMER TOWNSITE', '(DEPOPULATED)', '(DISSOLVED)'];
      for (const [ridStr] of Object.entries(toponymsRef.current)) {
        const rid = Number(ridStr);
        const r = rd[rid];
        if (!r) continue;
        const everHadRoad = gcSegs.some(seg =>
          seg.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R)
        ) || grownSegments.current.some(seg =>
          seg.pts.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R)
        );
        if (!everHadRoad) continue;
        const namedMs = namedAt.current[rid] || 0;
        const NAMING_GRACE = debugAbandonRef.current ? 20000 : 180000;
        if (!namedMs || (now2 - namedMs) < NAMING_GRACE) continue;
        const hasLiveRoad = gcSegs.some((seg, gi) =>
          (gapCloserDecay.current[gi] || 0) < DEAD_T &&
          seg.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R)
        ) || grownSegments.current.some(seg =>
          (seg.decay === -1 || (seg.decay >= 0 && seg.decay < DEAD_T)) &&
          seg.pts.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R)
        );
        let connected = false;
        const spineSegs = allRoadsRef.current.slice(0, gapCloserStartRef.current);
        outer: for (const seg of spineSegs) {
          if (!Array.isArray(seg)) continue;
          for (const pt of seg) {
            if (Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R) { connected = true; break outer; }
          }
        }
        if (!connected && hasLiveRoad) connected = true;
        if (!abandonedRegions.current.has(rid)) {
          // what's keeping this region connected? (only log non-abandoned, road-having, past-grace regions)
          const spineNear = (() => {
            for (const seg of spineSegs) { if (!Array.isArray(seg)) continue; for (const pt of seg) if (Math.hypot(pt.x-r.cx,pt.y-r.cy)<=CONN_R) return true; } return false;
          })();
          const liveGrownNear = grownSegments.current.filter(seg =>
            (seg.decay === -1 || (seg.decay >= 0 && seg.decay < DEAD_T)) &&
            seg.pts.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R)
          );
          console.log('[abandon-check] rid:', rid, 'connected:', connected, 'hasLiveRoad:', hasLiveRoad, 'spineNear:', spineNear,
            'liveGrown:', liveGrownNear.map(s => `${s.rid}:${s.decay.toFixed(2)}:${s.pts.length}c`).join(' '));
        }
        if (!abandonedRegions.current.has(rid) && !connected && !hasLiveRoad) {
          abandonedRegions.current.add(rid);
          regionSuffixes.current[rid] = SUFFIXES[(Math.random() * SUFFIXES.length) | 0];
        }
        if (abandonedRegions.current.has(rid) && !roadsGoneAt.current[rid]) {
          const hasImmuneRoad = grownSegments.current.some(seg =>
            seg.decay < 0 && seg.pts.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R)
          );
          if (hasImmuneRoad) { /* bridge region — never stamp roadsGoneAt */ }
          else {
            const allDead = gcSegs.every((seg, gi) => {
              const near = seg.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R);
              return !near || (gapCloserDecay.current[gi] || 0) >= DEAD_T;
            }) && grownSegments.current.every(seg => {
              const near = seg.pts.some(pt => Math.hypot(pt.x - r.cx, pt.y - r.cy) <= CONN_R);
              return !near || seg.decay >= DEAD_T;
            });
            if (allDead) roadsGoneAt.current[rid] = Date.now();
          }
        }
      }
    }, 5000);
    return () => clearInterval(id);
  }, [cells, regions, cols, rows, genSeed]);

    const withPins = (frags, pins) => {
    if (!pins || pins.length === 0) return frags;
    const unique = pins.filter((p, i) => pins.indexOf(p) === i);
    const fill = frags.filter(f => !unique.includes(f));
    return [...unique, ...fill].slice(0, 3);
  };
  useEffect(() => {
    if (driftTimer.current) clearInterval(driftTimer.current);
    driftTimer.current = setInterval(() => {
      const current = toponymsRef.current;
      const rids = Object.keys(current);
      if (rids.length === 0) return;
      rids.forEach(ridStr => {
        if (Math.random() > 0.04) return;
        const rid = Number(ridStr);
        if (abandonedRegions.current.has(rid)) return;
        const frags = regionFrags.current[rid];
        if (!frags || frags.length < 3) return;
        const landPins = regionPins.current[rid] || [];
        const landSample = withPins(frags.length <= 3 ? frags : frags.slice(-6), landPins);
        const driftRd = regions?.regionData?.[rid];
        callAPIToponymDrift(current[ridStr], landSample)
          .then(name => setToponymsSync(prev => ({ ...prev, [rid]: name }), driftRd?.cx, driftRd?.cy))
          .catch(() => {});
      });
    }, 7000);
    return () => clearInterval(driftTimer.current);
  }, [genSeed]);
  useEffect(() => {
    if (hydroDriftTimer.current) clearInterval(hydroDriftTimer.current);
    hydroDriftTimer.current = setInterval(() => {
      const current = hydronymsRef.current;
      const rids = Object.keys(current);
      if (rids.length === 0) return;
      rids.forEach(ridStr => {
        const rid = Number(ridStr);
        const rd = hydroRegions?.hydroData[rid];
        const threshold = rd?.isCoastal ? 0.06 : 0.03;
        if (Math.random() > threshold) return;
        const frags = hydroFrags.current[rid];
        if (!frags || frags.length < 3) return;
        const hydroPinsArr = hydroPins.current[rid] || [];
        const hydroSample = withPins(frags.length <= 3 ? frags : frags.slice(-6), hydroPinsArr);
        callAPIHydronymDrift(current[ridStr], hydroSample, rd?.isCoastal ?? false)
          .then(name => setHydronymsSync(prev => ({ ...prev, [rid]: name }), rd?.cx, rd?.cy))
          .catch(() => {});
      });
    }, 7000);
    return () => clearInterval(hydroDriftTimer.current);
  }, [genSeed, hydroRegions]);
  useEffect(() => {
    if (roadRevealTimer.current) clearTimeout(roadRevealTimer.current);
    const capturedSeed = genSeed;
    const allSnap      = allRoadsRef.current;
    const start        = gapCloserStartRef.current;
    setVisibleRoads(allSnap.slice(0, start));
    if (start >= allSnap.length) return;
    const segments = allSnap.slice(start).filter(Array.isArray);
    if (segments.length === 0) return;
    const birthTime = Date.now();
    gapCloserDecay.current = new Array(segments.length).fill(0);
    gapCloserDecay.current.birthTime = Date.now();
    let segIdx  = 0;
    let cellIdx = 0;
    const growingEntries = segments.map(() => null);
    const tick = () => {
      if (genSeed !== capturedSeed) return;
      if (segIdx >= segments.length) return;
      if (cellIdx === 0) {
        const entry = { pts: [], birthTime, decay: 0 };
        growingEntries[segIdx] = entry;
        grownSegments.current.push(entry);
      }
      const seg   = segments[segIdx];
      const entry = growingEntries[segIdx];
      if (cellIdx < seg.length) {
        const pt = seg[cellIdx];
        entry.pts.push(pt);
        if (roadGridRef.current) roadGridRef.current[pt.y * cols + pt.x] = 1;
        cellIdx++;
        setGrowVersion(v => v + 1);
        roadRevealTimer.current = setTimeout(tick, 1000);
      } else {
        segIdx++;
        cellIdx = 0;
        if (segIdx < segments.length) {
          roadRevealTimer.current = setTimeout(tick, 3000);
        } else {
          const seg = segments[segIdx - 1];
          if (seg && seg.length >= 2) {
            const ep0 = seg[0], ep1 = seg[seg.length - 1];
            setTimeout(() => {
              if (genSeed !== capturedSeed) return;
              scheduleFerryGapClosers([ep0, ep1], -1, birthTime);
            }, 12000);
          }
        }
      }
    };
    roadRevealTimer.current = setTimeout(tick, 2000);
    return () => { if (roadRevealTimer.current) clearTimeout(roadRevealTimer.current); };
  }, [genSeed]);
  const grownSegments  = useRef([]);
  const gapCloserDecay = useRef([]);
  const attnRef        = useRef({});
  const abandonedRegions = useRef(new Set());
  const regionSuffixes = useRef({});
  const roadsGoneAt    = useRef({});  // {rid: timestamp} when all non-spine roads for region hit decay>=1
  const touristFired   = useRef(new Set()); // rids that have had tourist spur grown
  const touristInterest = useRef({}); // {rid: {since: roadsGoneAt-stamp, count: n}} post-abandonment clicks
  const namedAt        = useRef({});        // {rid: timestamp} when region was last named
  const totalInGameMins = useRef(0);
  const ferrySegments         = useRef([]);
  const pendingFerryDepartures = useRef({});
  const namedRegions   = useRef(new Set());
  const [growVersion, setGrowVersion] = useState(0);
  useEffect(() => {
    if (!regions || !cells || !dist) return;
    Object.keys(toponyms).forEach(ridStr => {
      const rid = Number(ridStr);
      if (namedRegions.current.has(rid)) return;
      namedRegions.current.add(rid);
      const rd = regions.regionData[rid];
      if (!rd) return;
      const rg = roadGridRef.current;
      if (!rg) return;
      const cx = Math.round(rd.cx), cy = Math.round(rd.cy);
      const scanR = 55;
      const MIN_SOURCE_SEP = 28;
      const roadCandidates = [];
      for (let dy = -scanR; dy <= scanR; dy++) {
        for (let dx = -scanR; dx <= scanR; dx++) {
          const nx = cx + dx, ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          if (!rg[ny * cols + nx]) continue;
          if (cells[ny]?.[nx] === T.WATER) continue; // skip bridge cells
          const d = Math.hypot(dx, dy);
          if (d < 4) return;
          roadCandidates.push({ x: nx, y: ny, d });
        }
      }
      if (roadCandidates.length === 0) return;
      roadCandidates.sort((a, b) => a.d - b.d);
      const sources = [];
      for (const cand of roadCandidates) {
        if (sources.every(s => Math.hypot(s.x - cand.x, s.y - cand.y) >= MIN_SOURCE_SEP)) {
          sources.push(cand);
          if (sources.length === 3) break;
        }
      }
      const spurEndpoints = [];
      const completedSpurSegs = []; // {seg, ferry} pairs, accumulated across all growSpurTo calls

      // Route-level bridge evaluation. Runs once when all spurs for a region are complete.
      // A spur is a bridge only if its start component is distinct from other spurs' components.
      // If all spurs come from the same component, none are bridges — all decay normally.
      const evaluateBridgeRoutes = (completedSegs, completedFerries) => {
        if (!roadGridRef.current || completedSegs.length < 2) return;
        // build ferry cells for BFS connectivity (non-sibling ferries only)
        const siblingFerrySet = new Set(completedFerries.filter(Boolean));
        const bfsFerryCells = new Set();
        for (const ferry of ferrySegments.current) {
          if (siblingFerrySet.has(ferry)) continue;
          if (!ferry.revealed || ferry.revealed === 0) continue;
          for (let di = 0; di < ferry.revealed && di < ferry.pts.length; di++) {
            const fp = ferry.pts[di];
            if (fp) bfsFerryCells.add(fp.y * cols + fp.x);
          }
        }
        // BFS from startPt excluding only this spur's own cells
        const getComponent = (startPt, excludeCells) => {
          const visited = new Uint8Array(cols * rows);
          const q = [startPt.y * cols + startPt.x];
          visited[q[0]] = 1;
          let head = 0;
          while (head < q.length) {
            const idx = q[head++];
            const cx2 = idx % cols, cy2 = (idx / cols) | 0;
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
              if (!dx && !dy) continue;
              const nx = cx2+dx, ny = cy2+dy;
              if (nx<0||ny<0||nx>=cols||ny>=rows) continue;
              const ni = ny*cols+nx;
              if (visited[ni] || excludeCells.has(ni)) continue;
              if (!roadGridRef.current[ni] && !bfsFerryCells.has(ni)) continue;
              visited[ni] = 1;
              q.push(ni);
            }
          }
          return visited;
        };
        // compute per-spur component, excluding only that spur's own cells
        const spurComponents = completedSegs.map((seg, i) => {
          const excludeCells = new Set(seg.pts.map(p => p.y * cols + p.x));
          const ferry = completedFerries[i];
          if (ferry) for (const p of ferry.pts) excludeCells.add(p.y * cols + p.x);
          return getComponent(seg.pts[0], excludeCells);
        });
        // group spurs by start-component — two spurs are in the same group if either
        // can reach the other's start point (using their respective component maps)
        const groups = []; // each group: { indices: number[] }
        for (let i = 0; i < completedSegs.length; i++) {
          const startI = completedSegs[i].pts[0];
          let placed = false;
          for (const group of groups) {
            const j = group.indices[0];
            const startJ = completedSegs[j].pts[0];
            // i and j are same component if i's BFS (excluding i's spur) reaches j's start
            if (spurComponents[i][startJ.y * cols + startJ.x]) {
              group.indices.push(i);
              placed = true;
              break;
            }
          }
          if (!placed) groups.push({ indices: [i] });
        }
        // if only one component group, no bridge exists — all decay normally
        if (groups.length < 2) return;
        // multiple components: spurs from each group are essential bridges
        // within each group, keep shortest, decay rest
        for (const group of groups) {
          const segs = group.indices.map(i => completedSegs[i]);
          const ferries = group.indices.map(i => completedFerries[i]);
          const sorted = segs.map((s, i) => ({ s, f: ferries[i] }))
            .sort((a, b) => a.s.pts.length - b.s.pts.length);
          sorted[0].s.decay = -1;
          if (sorted[0].f) sorted[0].f.decay = -1;
          // also mark the pre-ferry growing1 segment immune for ferry routes
          // (growing1 is the spur before the ferry — find it by rid+birthTime, not in completedSegs)
          // rest decay normally (already 0)
        }
      };
      const birthTime = Date.now();
      const landDetour = (a, b, maxSteps = 400) => {
        const startIdx = a.y * cols + a.x;
        const endIdx   = b.y * cols + b.x;
        if (startIdx === endIdx) return 0;
        const dist_ = new Int16Array(cols * rows).fill(-1);
        dist_[startIdx] = 0;
        const q = [startIdx];
        let qh = 0;
        const DIRS4 = [-1, 1, -cols, cols];
        while (qh < q.length) {
          const cur = q[qh++];
          const d = dist_[cur];
          if (d >= maxSteps) continue;
          const cy_ = (cur / cols) | 0, cx_ = cur % cols;
          for (const dd of DIRS4) {
            const ni = cur + dd;
            if (ni < 0 || ni >= cols * rows) continue;
            const ny_ = (ni / cols) | 0, nx_ = ni % cols;
            if (Math.abs(nx_ - cx_) > 1) continue;
            if (cells[ny_]?.[nx_] === T.WATER) continue;
            if (dist_[ni] !== -1) continue;
            dist_[ni] = d + 1;
            if (ni === endIdx) return d + 1;
            q.push(ni);
          }
        }
        return dist_[endIdx] === -1 ? Infinity : dist_[endIdx];
      };
      const findFerryCrossing = (pt, tgt, ridKey) => {
        const existingFerries = ferrySegments.current.filter(f => f.rid === ridKey);
        const ddx = tgt.x - pt.x, ddy = tgt.y - pt.y;
        const dd = Math.hypot(ddx, ddy);
        if (dd < 2) return null;
        const nx = ddx / dd, ny = ddy / dd;
        let departure = null, inWater = false, landing = null;
        for (let step = 1; step < Math.ceil(dd) + cols; step++) {
          const wx = Math.round(pt.x + nx * step), wy = Math.round(pt.y + ny * step);
          if (wx < 0 || wy < 0 || wx >= cols || wy >= rows) break;
          const isWater = cells[wy][wx] === T.WATER;
          if (!inWater && isWater) { departure = { x: wx, y: wy }; inWater = true; }
          if (inWater && !isWater) {
            const ax = Math.round(pt.x + nx*(step+1)), ay = Math.round(pt.y + ny*(step+1));
            const bx = Math.round(pt.x + nx*(step+2)), by = Math.round(pt.y + ny*(step+2));
            const solid = (
              ax>=0&&ay>=0&&ax<cols&&ay<rows&&cells[ay][ax]!==T.WATER &&
              bx>=0&&by>=0&&bx<cols&&by<rows&&cells[by][bx]!==T.WATER
            );
            if (solid) { landing = { x: wx, y: wy }; break; }
            inWater = true;
          }
          if (!inWater && Math.hypot(wx-tgt.x, wy-tgt.y) < 4) return null;
        }
        if (!departure || !landing) return null;
        const crossLen = Math.hypot(landing.x - departure.x, landing.y - departure.y);
        const detour   = landDetour(departure, landing);
        if (detour < crossLen * 3) return null;
        const planned = pendingFerryDepartures.current[ridKey] || [];
        for (const pd of planned) {
          if (Math.hypot(pd.x - departure.x, pd.y - departure.y) < MIN_DEP_SEP) return null;
        }
        for (const ef of existingFerries) {
          if (Math.hypot(ef.departure.x - departure.x, ef.departure.y - departure.y) < MIN_DEP_SEP) return null;
        }
        return { departure, landing };
      };
      const growSpurTo = (src, target, visited, startDelay) => {
        const seed = genSeed + rid * 137 + Math.round(src.x * 7 + src.y * 13);
        const srcToTarget = Math.hypot(src.x - target.x, src.y - target.y);
        const steps1 = Math.min(Math.ceil(srcToTarget * 4), 200);
        const rawPts = flowWalk(src, target, cells, dist, cols, rows, seed, 0.65, steps1, 0.3, 1);
        if (rawPts.length < 2) return;
        let closestIdx = 0, closestD = Infinity;
        for (let k = 0; k < rawPts.length; k++) {
          const d = Math.hypot(rawPts[k].x - target.x, rawPts[k].y - target.y);
          if (d < closestD) { closestD = d; closestIdx = k; }
        }
        rawPts.length = closestIdx + 1;
        const spurTip = rawPts[rawPts.length - 1];
        const regionFerries = ferrySegments.current.filter(f => f.rid === rid);
        let ferryData = null, phase2Pts = null, ferryEntry = null;
        let usedSnap = false;
        if (closestD > 6) {
          const directCrossing = findFerryCrossing(spurTip, target, rid);
          const directCost = directCrossing
            ? Math.hypot(spurTip.x-directCrossing.departure.x, spurTip.y-directCrossing.departure.y)
              + Math.hypot(directCrossing.departure.x-directCrossing.landing.x, directCrossing.departure.y-directCrossing.landing.y)
              + Math.hypot(directCrossing.landing.x-target.x, directCrossing.landing.y-target.y)
            : Infinity;
          const pendingDeps = pendingFerryDepartures.current[rid] || [];
          if (directCrossing) {
            const tooClose = pendingDeps.some(pd =>
              Math.hypot(pd.x - directCrossing.departure.x, pd.y - directCrossing.departure.y) < MIN_DEP_SEP
            );
            if (tooClose) {
              setTimeout(() => growSpurTo(spurTip, target, visited, 0), 6000);
              return;
            }
          }
          let snapTarget = null, bestSnapCost = directCost;
          for (const ef of regionFerries) {
            for (const ep of [ef.departure, ef.landing]) {
              if (!ep) continue;
              const key = `${ep.x},${ep.y}`;
              if (visited.has(key)) continue;
              const otherEp = (ep.x === ef.departure?.x && ep.y === ef.departure?.y) ? ef.landing : ef.departure;
              if (!otherEp) continue;
              const snapCost = Math.hypot(spurTip.x-ep.x, spurTip.y-ep.y)
                             + Math.hypot(otherEp.x-target.x, otherEp.y-target.y);
              if (snapCost < bestSnapCost) { bestSnapCost = snapCost; snapTarget = { pt: ep, key, ferry: ef }; }
            }
          }
          if (snapTarget) {
            usedSnap = true;
            const ep = snapTarget.pt;
            const ef = snapTarget.ferry;
            const newVisited = new Set(visited);
            newVisited.add(snapTarget.key);
            const otherEnd = (ep.x === ef.departure?.x && ep.y === ef.departure?.y) ? ef.landing : ef.departure;
            newVisited.add(`${otherEnd.x},${otherEnd.y}`);
            const epSteps = Math.min(Math.ceil(Math.hypot(spurTip.x-ep.x, spurTip.y-ep.y)*4), 200);
            const epRaw = flowWalk(spurTip, ep, cells, dist, cols, rows, seed+200, 0.8, epSteps, 0.25, 1);
            let ei = 0, ed = Infinity;
            for (let k = 0; k < epRaw.length; k++) {
              const d = Math.hypot(epRaw[k].x-ep.x, epRaw[k].y-ep.y);
              if (d < ed) { ed = d; ei = k; }
            }
            epRaw.length = ei + 1;
            if (ed > 1) epRaw.push({ ...ep, bridge: false });
            let ri = 0;
            const growingSnap = { pts: [], birthTime, decay: 0, rid };
            grownSegments.current.push(growingSnap);
            const snapTick = () => {
              const tick = setInterval(() => {
                if (!roadGridRef.current) { clearInterval(tick); return; }
                if (ri >= epRaw.length) {
                  clearInterval(tick);
                  const estMs = epRaw.length * 1000 + 2000;
                  setTimeout(() => growSpurTo(otherEnd, target, newVisited, 0), estMs);
                  return;
                }
                const pt = epRaw[ri];
                growingSnap.pts.push(pt);
                roadGridRef.current[pt.y*cols+pt.x] = 1;
                ri++;
                setGrowVersion(v => v+1);
              }, 1000);
            };
            if (startDelay > 0) setTimeout(snapTick, startDelay); else snapTick();
          } else if (directCrossing) {
            if (!pendingFerryDepartures.current[rid]) pendingFerryDepartures.current[rid] = [];
            pendingFerryDepartures.current[rid].push(directCrossing.departure);
            ferryData = directCrossing;
            const ld = ferryData.landing;
            const steps2 = Math.min(Math.ceil(Math.hypot(ld.x-target.x, ld.y-target.y)*4), 200);
            const raw2 = flowWalk(ld, target, cells, dist, cols, rows, seed+500, 0.7, steps2, 0.3, 1);
            if (raw2.length >= 2) {
              let ci2 = 0, cd2 = Infinity;
              for (let k = 0; k < raw2.length; k++) {
                const d = Math.hypot(raw2[k].x-target.x, raw2[k].y-target.y);
                if (d < cd2) { cd2 = d; ci2 = k; }
              }
              raw2.length = ci2 + 1;
              if (cd2 > 1) raw2.push({ ...target, bridge: false });
              phase2Pts = raw2;
            }
            const ferryDist = Math.hypot(ferryData.landing.x-ferryData.departure.x, ferryData.landing.y-ferryData.departure.y);
            const fdx = (ferryData.landing.x-ferryData.departure.x)/ferryDist;
            const fdy = (ferryData.landing.y-ferryData.departure.y)/ferryDist;
            const ferryPts = [];
            for (let s = 0; s <= Math.ceil(ferryDist); s++) {
              const fx = Math.round(ferryData.departure.x + fdx*s);
              const fy = Math.round(ferryData.departure.y + fdy*s);
              if (fx>=0&&fy>=0&&fx<cols&&fy<rows) ferryPts.push({x:fx,y:fy});
            }
            ferryEntry = { pts: ferryPts, revealed: 0, birthTime, rid, departure: ferryData.departure, landing: ferryData.landing, decay: 0 };
            ferrySegments.current.push(ferryEntry);
          }
        }
        if (!usedSnap && !ferryData && closestD > 1) rawPts.push({ ...target, bridge: false });
        if (usedSnap) return;
        const onPhase1Done = () => {
          if (!ferryEntry || !phase2Pts) {
            completedSpurSegs.push({ seg: growing1, ferry: null });
            spurEndpoints.push(rawPts[rawPts.length-1]);
            if (spurEndpoints.length === sources.length) {
              if (spurEndpoints.length > 1) {
                scheduleLoopCompletion(spurEndpoints, rid, birthTime);
                scheduleFerryGapClosers(spurEndpoints, rid, birthTime);
              }
              evaluateBridgeRoutes(
                completedSpurSegs.map(e => e.seg),
                completedSpurSegs.map(e => e.ferry)
              );
            }
            return;
          }
          let fi = 0;
          const ferryTick = setInterval(() => {
            if (!roadGridRef.current) { clearInterval(ferryTick); return; }
            if (fi >= ferryEntry.pts.length) {
              clearInterval(ferryTick);
              let j = 0;
              const growing2 = { pts: [], birthTime, decay: 0, rid };
              grownSegments.current.push(growing2);
              const tick2 = setInterval(() => {
                if (!roadGridRef.current) { clearInterval(tick2); return; }
                if (j >= phase2Pts.length) {
                  clearInterval(tick2);
                  completedSpurSegs.push({ seg: growing2, ferry: ferryEntry || null });
                  spurEndpoints.push(phase2Pts[phase2Pts.length-1]);
                  if (spurEndpoints.length === sources.length) {
                    if (spurEndpoints.length > 1) {
                      scheduleLoopCompletion(spurEndpoints, rid, birthTime);
                      scheduleFerryGapClosers(spurEndpoints, rid, birthTime);
                    }
                    evaluateBridgeRoutes(
                      completedSpurSegs.map(e => e.seg),
                      completedSpurSegs.map(e => e.ferry)
                    );
                  }
                  return;
                }
                const pt = phase2Pts[j];
                growing2.pts.push(pt);
                roadGridRef.current[pt.y*cols+pt.x] = 1;
                j++;
                setGrowVersion(v => v+1);
              }, 1000);
              return;
            }
            ferryEntry.revealed = fi + 1;
            const fpt = ferryEntry.pts[fi];
            if (fpt) roadGridRef.current[fpt.y * cols + fpt.x] = 1;
            fi++;
            setGrowVersion(v => v+1);
          }, 800);
        };
        let i = 0;
        const growing1 = { pts: [], birthTime, decay: 0, rid };
        grownSegments.current.push(growing1);
        const startTick = () => {
          const tick = setInterval(() => {
            if (!roadGridRef.current) { clearInterval(tick); return; }
            if (i >= rawPts.length) { clearInterval(tick); onPhase1Done(); return; }
            const pt = rawPts[i];
            growing1.pts.push(pt);
            roadGridRef.current[pt.y*cols+pt.x] = 1;
            i++;
            setGrowVersion(v => v+1);
          }, 1000);
        };
        if (startDelay > 0) setTimeout(startTick, startDelay);
        else startTick();
      };
      const landTarget = cells[cy]?.[cx] === T.WATER
        ? nearestLand(cx, cy, cells, cols, rows)
        : { x: cx, y: cy };
      sources.forEach((src, si) => growSpurTo(src, landTarget, new Set(), si * 8000));
    });
  }, [toponyms]);
  const scheduleLoopCompletion = useCallback((endpoints, rid, birthTime) => {
    const LOOP_MAX = 30;
    const pairs = [];
    for (let i = 0; i < endpoints.length; i++)
      for (let j = i + 1; j < endpoints.length; j++) {
        const d = Math.hypot(endpoints[i].x - endpoints[j].x, endpoints[i].y - endpoints[j].y);
        if (d <= LOOP_MAX) pairs.push({ a: endpoints[i], b: endpoints[j], d });
      }
    if (pairs.length === 0) return;
    pairs.sort((a, b) => a.d - b.d);
    pairs.forEach(({ a, b }, pi) => {
      const startDelay = pi * 4000 + 2000;
      setTimeout(() => {
        if (!roadGridRef.current || !cells || !dist) return;
        // validate both endpoints are on land and near a road cell
        const nearRoad = (pt) => {
          for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
            const nx = pt.x+dx, ny = pt.y+dy;
            if (nx<0||ny<0||nx>=cols||ny>=rows) continue;
            if (roadGridRef.current[ny*cols+nx]) return true;
          }
          return false;
        };
        if (cells[a.y]?.[a.x] === T.WATER || cells[b.y]?.[b.x] === T.WATER) return;
        if (!nearRoad(a) || !nearRoad(b)) return;
        const steps  = Math.min(Math.ceil(b.d * 4), 120);
        const seed   = genSeed + rid * 199 + pi * 53;
        const rawPts = flowWalk(a, b, cells, dist, cols, rows, seed, 0.85, steps, 0.2, 1);
        if (rawPts.length < 2) return;
        let lcIdx = 0, lcD = Infinity;
        for (let k = 0; k < rawPts.length; k++) {
          const d = Math.hypot(rawPts[k].x - b.x, rawPts[k].y - b.y);
          if (d < lcD) { lcD = d; lcIdx = k; }
        }
        rawPts.length = lcIdx + 1;
        if (lcD > 1) rawPts.push({ ...b, bridge: false });
        let i = 0;
        const growing = { pts: [], birthTime, decay: 0, rid };
        grownSegments.current.push(growing);
        const tick = setInterval(() => {
          if (!roadGridRef.current) { clearInterval(tick); return; }
          if (i >= rawPts.length) { clearInterval(tick); return; }
          const pt = rawPts[i];
          growing.pts.push(pt);
          roadGridRef.current[pt.y * cols + pt.x] = 1;
          i++;
          setGrowVersion(v => v + 1);
        }, 1000);
      }, startDelay);
    });
  }, [cells, dist, genSeed]);
  const scheduleFerryGapClosers = useCallback((endpoints, rid, birthTime) => {
    const FERRY_GAP_MAX = 12;
    const SHORE_SNAP    = 4;
    setTimeout(() => {
      if (!roadGridRef.current || !cells) return;
      const T_WATER = 0;
      // filter to land-only endpoints that are on or adjacent to a road cell
      const validEndpoints = endpoints.filter(pt => {
        if (!pt || pt.x < 0 || pt.y < 0 || pt.x >= cols || pt.y >= rows) return false;
        if (cells[pt.y]?.[pt.x] === T_WATER) return false;
        // must be on or within 2 cells of a road cell
        for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
          const nx = pt.x+dx, ny = pt.y+dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          if (roadGridRef.current[ny*cols+nx]) return true;
        }
        return false;
      });
      const used = new Set();
      for (let i = 0; i < validEndpoints.length; i++) {
        for (let j = i + 1; j < validEndpoints.length; j++) {
          if (used.has(i) || used.has(j)) continue;
          const a = validEndpoints[i], b = validEndpoints[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const dd = Math.hypot(dx, dy);
          if (dd < 2) continue;
          const nx = dx / dd, ny = dy / dd;
          let departure = null, landing = null, inWater = false;
          for (let step = 1; step <= Math.ceil(dd) + 2; step++) {
            const wx = Math.round(a.x + nx * step), wy = Math.round(a.y + ny * step);
            if (wx < 0 || wy < 0 || wx >= cols || wy >= rows) break;
            const isWater = cells[wy]?.[wx] === T_WATER;
            if (!inWater && isWater)  { departure = { x: wx, y: wy }; inWater = true; }
            if ( inWater && !isWater) { landing   = { x: wx, y: wy }; break; }
          }
          if (!departure || !landing) continue;
          const crossLen = Math.hypot(landing.x - departure.x, landing.y - departure.y);
          if (crossLen > FERRY_GAP_MAX) continue;
          const aDep = Math.hypot(a.x - departure.x, a.y - departure.y);
          const bLand = Math.hypot(b.x - landing.x, b.y - landing.y);
          if (aDep > SHORE_SNAP || bLand > SHORE_SNAP) continue;
          const tooClose = ferrySegments.current.some(ef =>
            Math.hypot(ef.departure.x - departure.x, ef.departure.y - departure.y) < MIN_DEP_SEP
          );
          if (tooClose) continue;
          used.add(i); used.add(j);
          const fdist = Math.hypot(landing.x - departure.x, landing.y - departure.y);
          const fdx = (landing.x - departure.x) / fdist;
          const fdy = (landing.y - departure.y) / fdist;
          const ferryPts = [];
          for (let s = 0; s <= Math.ceil(fdist); s++) {
            const fx = Math.round(departure.x + fdx * s);
            const fy = Math.round(departure.y + fdy * s);
            if (fx >= 0 && fy >= 0 && fx < cols && fy < rows) ferryPts.push({ x: fx, y: fy });
          }
          const ferryEntry = { pts: ferryPts, revealed: 0, birthTime, rid, decay: 0,
                               departure, landing };
          ferrySegments.current.push(ferryEntry);
          let fi = 0;
          const ferryTick = setInterval(() => {
            if (!roadGridRef.current) { clearInterval(ferryTick); return; }
            if (fi >= ferryEntry.pts.length) { clearInterval(ferryTick); return; }
            ferryEntry.revealed = fi + 1;
            const fpt = ferryEntry.pts[fi];
            if (fpt && roadGridRef.current) roadGridRef.current[fpt.y * cols + fpt.x] = 1;
            fi++;
            setGrowVersion(v => v + 1);
          }, 800);
        }
      }
    }, 8000);
  }, [cells, genSeed]);
  const periodicGapCloserRef = useRef(0);
  useEffect(() => {
    if (timeMins === null || !cells || !dist || !roadGridRef.current) return;
    const slot = Math.floor(timeMins / 720);
    if (slot === periodicGapCloserRef.current) return;
    periodicGapCloserRef.current = slot;
    const birthTime = Date.now();
    const capturedSeed = genSeed;
    const endpoints = [];
    const spineGrid2 = spineGridRef.current;
    const rg = roadGridRef.current;
    // only use endpoints that are on land and whose segment starts from an established road cell
    const addEndpoint = (pt) => {
      if (!pt) return;
      if (pt.x < 0 || pt.y < 0 || pt.x >= cols || pt.y >= rows) return;
      if (cells[pt.y]?.[pt.x] === T.WATER) return;
      endpoints.push(pt);
    };
    for (const seg of allRoadsRef.current) {
      if (!Array.isArray(seg) || seg.length < 2) continue;
      addEndpoint(seg[0]);
      addEndpoint(seg[seg.length - 1]);
    }
    const DEAD_T_PGC = 0.5; // conservative — skip if corridor is starting to decay
    for (const seg of grownSegments.current) {
      if (!seg.pts || seg.pts.length < 2) continue;
      if (seg.decay >= DEAD_T_PGC || seg.decay < 0) continue; // skip dead or immune segments
      // only include if segment's first point is on an established road (spine or roadGrid)
      const first = seg.pts[0];
      if (!first || !rg || !rg[first.y * cols + first.x]) continue;
      addEndpoint(seg.pts[0]);
      addEndpoint(seg.pts[seg.pts.length - 1]);
    }
    if (endpoints.length < 2) return;
    const GAP_MAX = 40;
    const used = new Set();
    const pairs = [];
    for (let i = 0; i < endpoints.length; i++) {
      for (let j = i + 1; j < endpoints.length; j++) {
        const d = Math.hypot(endpoints[i].x - endpoints[j].x, endpoints[i].y - endpoints[j].y);
        if (d >= 4 && d <= GAP_MAX) pairs.push({ i, j, d });
      }
    }
    pairs.sort((a, b) => a.d - b.d);
    const waterFraction = (a, b) => {
      const steps = Math.max(4, Math.ceil(Math.hypot(a.x - b.x, a.y - b.y)));
      let water = 0;
      for (let s = 0; s <= steps; s++) {
        const x = Math.round(a.x + (b.x - a.x) * s / steps);
        const y = Math.round(a.y + (b.y - a.y) * s / steps);
        if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
        if (cells[y][x] === T.WATER) water++;
      }
      return water / (steps + 1);
    };
    // build decayed cell set to skip rebuilding dead corridors
    const decayedCells = new Set();
    for (const seg of grownSegments.current) {
      if (seg.decay >= DEAD_T_PGC) for (const pt of seg.pts) decayedCells.add(pt.y * cols + pt.x);
    }
    const gcSegsNow = allRoadsRef.current.slice(gapCloserStartRef.current).filter(Array.isArray);
    for (let i2 = 0; i2 < gcSegsNow.length; i2++) {
      if ((gapCloserDecay.current[i2] || 0) >= DEAD_T_PGC)
        for (const pt of gcSegsNow[i2]) decayedCells.add(pt.y * cols + pt.x);
    }
    pairs.forEach(({ i, j, d }, pi) => {
      if (used.has(i) || used.has(j)) return;
      const a = endpoints[i], b = endpoints[j];
      if (cells[a.y]?.[a.x] === T.WATER || cells[b.y]?.[b.x] === T.WATER) return;
      if (waterFraction(a, b) > 0.3) return;
      // skip if either endpoint is on or adjacent to a decayed cell
      const nearDecayed = (pt) => {
        for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
          if (decayedCells.has((pt.y+dy) * cols + (pt.x+dx))) return true;
        }
        return false;
      };
      if (nearDecayed(a) || nearDecayed(b)) return;
      if (roadGridRef.current[a.y * cols + a.x] && roadGridRef.current[b.y * cols + b.x]) {
        const maxSteps = Math.ceil(d * 1.5);
        const visited_ = new Uint8Array(cols * rows);
        const q_ = [a.y * cols + a.x];
        visited_[q_[0]] = 1;
        let qh = 0, found = false;
        const target_ = b.y * cols + b.x;
        const dirs4 = [-1, 1, -cols, cols];
        while (qh < q_.length && !found) {
          const cur = q_[qh++];
          if (cur === target_) { found = true; break; }
          if (qh > maxSteps) break;
          for (const dd of dirs4) {
            const ni = cur + dd;
            if (ni < 0 || ni >= cols * rows || visited_[ni]) continue;
            const nx_ = ni % cols, ny_ = (ni / cols) | 0;
            if (Math.abs(nx_ - (cur % cols)) > 1) continue;
            if (!roadGridRef.current[ni]) continue;
            visited_[ni] = 1;
            q_.push(ni);
          }
        }
        if (found) return;
      }
      used.add(i); used.add(j);
      const startDelay = pi * 4000 + 3000;
      setTimeout(() => {
        if (!roadGridRef.current || !cells || !dist || genSeed !== capturedSeed) return;
        const noiseW = Math.min(0.15 + (d / GAP_MAX) * 0.30, 0.45);
        const steps = Math.min(Math.ceil(d * 5), 250);
        const rawPts = flowWalk(a, b, cells, dist, cols, rows,
          capturedSeed + i * 73 + j * 31 + slot * 17, 0.75, steps, noiseW, 1);
        if (rawPts.length < 2) return;
        let lcIdx = 0, lcD = Infinity;
        for (let k = 0; k < rawPts.length; k++) {
          const dd = Math.hypot(rawPts[k].x - b.x, rawPts[k].y - b.y);
          if (dd < lcD) { lcD = dd; lcIdx = k; }
        }
        rawPts.length = lcIdx + 1;
        if (lcD > 1) rawPts.push({ ...b, bridge: false });
        if (rawPts.length < 2) return;
        let ri = 0;
        const growing = { pts: [], birthTime, decay: 0, rid: -1 };
        grownSegments.current.push(growing);
        const tick = setInterval(() => {
          if (!roadGridRef.current) { clearInterval(tick); return; }
          if (ri >= rawPts.length) { clearInterval(tick); return; }
          const pt = rawPts[ri];
          growing.pts.push(pt);
          roadGridRef.current[pt.y * cols + pt.x] = 1;
          ri++;
          setGrowVersion(v => v + 1);
        }, 1000);
      }, startDelay);
    });
    setTimeout(() => {
      if (genSeed !== capturedSeed) return;
      scheduleFerryGapClosers(endpoints, -1, birthTime);
    }, pairs.length * 4000 + 10000);
  }, [timeMins]);
  useEffect(() => {
    grownSegments.current = [];
    ferrySegments.current = [];
    pendingFerryDepartures.current = {};
    namedRegions.current = new Set();
    setGrowVersion(0);
  }, [genSeed]);
  useEffect(() => {
    const id = setInterval(() => {
      if (grownSegments.current.length > 0)
        setGrowVersion(v => v + 1);
    }, 15000);
    return () => clearInterval(id);
  }, []);
  const [roadPulse, setRoadPulse] = useState(null);
  const [showDeepWild, setShowDeepWild] = useState(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !cells || !pal) return;
    canvas.width  = cols * PIXEL;
    canvas.height = rows * PIXEL;
    const dwGrid = showDeepWild ? (() => {
      const minDim2 = Math.min(cols, rows);
      const R_D2 = Math.max(5, Math.round(minDim2 * 0.07));
      const grid = new Uint8Array(cols * rows);
      for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
        if (cells[y]?.[x] !== T.WILDERNESS) continue;
        if (roadGridRef.current[y*cols+x]) continue;
        let adjLand = false;
        for (let dy=-1;dy<=1;dy++) for (let dx=-1;dx<=1;dx++) {
          const cx=x+dx,cy=y+dy;
          if (cx<0||cx>=cols||cy<0||cy>=rows) continue;
          if (cells[cy][cx]===T.LAND){adjLand=true;break;}
        }
        if (adjLand) continue;
        let nearRoad = false;
        rd: for (let dy=-R_D2;dy<=R_D2;dy++) for (let dx=-R_D2;dx<=R_D2;dx++) {
          const cx=x+dx,cy=y+dy;
          if (cx<0||cx>=cols||cy<0||cy>=rows) continue;
          if (roadGridRef.current[cy*cols+cx]){nearRoad=true;break rd;}
        }
        if (!nearRoad) grid[y*cols+x]=1;
      }
      return grid;
    })() : null;
    drawMap(canvas.getContext("2d"), cells, visibleRoads, grownSegments.current, ferrySegments.current, pal, cols, rows, route, roadPulse, Date.now(), dwGrid, gapCloserDecay.current, gapCloserStartRef.current, abandonedRegions.current, spineGridRef.current);
  }, [cells, visibleRoads, pal, cols, rows, route, roadPulse, growVersion, showDeepWild]);
  const currentTime = timeMins !== null ? formatTime(timeMins) : null;
  const cpx = gx => gx * PIXEL + PIXEL / 2;
  const cpy = gy => gy * PIXEL + PIXEL / 2;
  const handleClick = useCallback(async e => {
    if (!cells || !currentTime || modal || routeLoading || reportModal) return;
    if (reportStep !== 'placing' && markers.filter(m => !m.fading).length >= 8) return;
    if (reportStep === 'placing' && reportEvent) {
      const rect = canvasRef.current.getBoundingClientRect();
      const gx = (e.clientX - rect.left) / PIXEL | 0;
      const gy = (e.clientY - rect.top)  / PIXEL | 0;
      if (gx < 0 || gx >= cols || gy < 0 || gy >= rows) return;
      const rid = `report-${Date.now()}`;
      setReportMarkers(prev => [...prev, { id: rid, gx, gy, loading: true, fragment: null, eventLabel: reportEvent.label }]);
      setReportStep('select');
      const spatialCtxR = roadGridRef.current ? getSpatialContext(gx, gy, cells, roadGridRef.current, cols, rows, hydroRegions?.hydroOf, hydroRegions?.hydroData, hydroRegions?.coastalOf) : { waterChar:'none', roadDensity:'none' };
      const terrainR = cells[gy][gx];
      const isNightR = isNightTime(timeMins);
      callAPIReport(reportEvent.label, reportNotes, isNightR)
        .then(fragment => {
          setReportMarkers(prev => prev.map(m => m.id === rid ? { ...m, fragment, loading: false } : m));
          setTimeout(() => setReportMarkers(prev => prev.map(m => m.id === rid ? { ...m, fading: true } : m)), 30000);
          setTimeout(() => setReportMarkers(prev => prev.filter(m => m.id !== rid)), 31500);
          const rawNotes = reportNotes.trim();
          if (regions) {
            const rrid = regions.regionOf[gy * cols + gx];
            if (rrid >= 0) {
              if (rawNotes) {
                if (!regionPins.current[rrid]) regionPins.current[rrid] = [];
                if (!regionPins.current[rrid].includes(rawNotes)) regionPins.current[rrid].push(rawNotes);
              }
              if (!regionFrags.current[rrid]) regionFrags.current[rrid] = [];
              const rf = regionFrags.current[rrid];
              if (!rf.includes(fragment)) { rf.push(fragment); if (rf.length > 12) rf.shift(); }
              if (!toponymsRef.current[rrid] && rf.length >= (Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length < 2 ? 2 : 3)) {
                const rd = regions.regionData[rrid];
                callAPIToponym(withPins(rf, regionPins.current[rid] || []), rd.dominantTerrain, spatialCtxR.waterChar, isNightR)
                  .then(name => { namedAt.current[rrid] = Date.now(); const repRd = regions?.regionData?.[rrid]; setToponymsSync(prev => ({ ...prev, [rrid]: name }), repRd?.cx, repRd?.cy); }).catch(() => {});
              }
            }
          }
          if (hydroRegions) {
            const hrid = terrainR === 0
              ? hydroRegions.hydroOf[gy * cols + gx]
              : nearestHydroRegion(gx, gy, hydroRegions.hydroOf, cols, rows);
            if (hrid >= 0) {
              const hd = hydroRegions.hydroData[hrid];
              if (hd && hd.size >= HYDRO_MIN_CELLS) {
                if (rawNotes) {
                  if (!hydroPins.current[hrid]) hydroPins.current[hrid] = [];
                  if (!hydroPins.current[hrid].includes(rawNotes)) hydroPins.current[hrid].push(rawNotes);
                }
                if (!hydroFrags.current[hrid]) hydroFrags.current[hrid] = [];
                const hf = hydroFrags.current[hrid];
                if (!hf.includes(fragment)) { hf.push(fragment); if (hf.length > 12) hf.shift(); }
                if (!hydronymsRef.current[hrid] && hf.length >= (Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length < 2 ? 2 : 3)) {
                  callAPIHydronym(withPins(hf, hydroPins.current[hrid] || []), hd.isCoastal, isNightR)
                    .then(name => setHydronymsSync(prev => ({ ...prev, [hrid]: name }), hd.cx, hd.cy)).catch(() => {});
                }
              }
            }
          }
        })
        .catch(err => {
          setReportMarkers(prev => prev.map(m => m.id === rid ? { ...m, fragment: err.message, loading: false } : m));
          setTimeout(() => setReportMarkers(prev => prev.map(m => m.id === rid ? { ...m, fading: true } : m)), 15000);
          setTimeout(() => setReportMarkers(prev => prev.filter(m => m.id !== rid)), 16500);
        });
      return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    const gx = (e.clientX - rect.left) / PIXEL | 0;
    const gy = (e.clientY - rect.top)  / PIXEL | 0;
    if (gx < 0 || gx >= cols || gy < 0 || gy >= rows) return;
    const id = Date.now() + Math.random();
    setMarkers(prev => [...prev, { id, gx, gy, loading:true, fragment:null, error:null, spatialCtx:null }]);
    setTimeout(() => setMarkers(prev => prev.filter(m => m.id !== id || !m.loading)), 20000);
    try {
      if (!audioReady && audioRoot.current !== null) {
        initAudio(genSeed, isNightTime(timeMins));
      }
      const spatialCtx = roadGridRef.current ? getSpatialContext(gx, gy, cells, roadGridRef.current, cols, rows, hydroRegions?.hydroOf, hydroRegions?.hydroData, hydroRegions?.coastalOf) : { waterChar:"none", roadDensity:"none", borderZone:false, deepWild:false };
      const isNight = isNightTime(timeMins);
      const terrain = cells[gy][gx];
      setMarkers(prev => prev.map(m => m.id === id ? { ...m, spatialCtx: { ...spatialCtx, terrain } } : m));
      if (Math.random() < 0.20) {
        apiInFlight.current++;
        try {
          const realFrag = await fetchRealFragment(terrain, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, spatialCtx.borderZone || false, spatialCtx.deepWild || false);
          apiInFlight.current = Math.max(0, apiInFlight.current - 1);
          setMarkers(prev => prev.map(m => m.id===id ? { ...m, fragment: realFrag, loading:false } : m));
          setTimeout(() => setMarkers(prev => prev.map(m => m.id===id ? { ...m, fading:true } : m)), 20000);
          setTimeout(() => setMarkers(prev => prev.filter(m => m.id!==id)), 21500);
          const root = audioRoot.current ?? mapRootNote(genSeed);
          playArrival(terrain, root, gx, gy);
          return;
        } catch(e) {
          apiInFlight.current = Math.max(0, apiInFlight.current - 1);
          setMarkers(prev => prev.map(m => m.id===id ? { ...m, fragment: randomDataLoss(), loading:false, dataLoss:true } : m));
          setTimeout(() => setMarkers(prev => prev.map(m => m.id===id ? { ...m, fading:true } : m)), 15000);
          setTimeout(() => setMarkers(prev => prev.filter(m => m.id!==id)), 16500);
          return;
        }
      }
      // Abandoned region: bypass cache, use dedicated brief
      const clickedRid = regions ? regions.regionOf[gy * cols + gx] : -1;
      if (clickedRid >= 0 && abandonedRegions.current.has(clickedRid)) {
        // register post-abandonment interest (drives tourist-spur preservation)
        touristInterest.current[clickedRid] = Date.now();
        attnRef.current[clickedRid] = Math.min(1.0, (attnRef.current[clickedRid] || 0) + 0.18);
        const abandonedSuffix = regionSuffixes.current[clickedRid] || '(ABANDONED)';
        const abandonedBrief = getAbandonedRegister(terrain, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, abandonedSuffix);
        apiInFlight.current++;
        try {
          const batch = await fetchBatch(terrain, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, spatialCtx.borderZone || false, spatialCtx.deepWild || false, abandonedBrief);
          apiInFlight.current = Math.max(0, apiInFlight.current - 1);
          const fragment = batch[0];
          setMarkers(prev => prev.map(m => m.id===id ? { ...m, fragment, loading:false } : m));
          setTimeout(() => setMarkers(prev => prev.map(m => m.id===id ? { ...m, fading:true } : m)), 20000);
          setTimeout(() => setMarkers(prev => prev.filter(m => m.id!==id)), 21500);
          initAudio(genSeed, isNight).then(() => { playArrival(terrain, audioRoot.current ?? mapRootNote(genSeed), gx, gy); });
        } catch(e) {
          apiInFlight.current = Math.max(0, apiInFlight.current - 1);
          setMarkers(prev => prev.map(m => m.id===id ? { ...m, fragment: randomDataLoss(), loading:false, dataLoss:true } : m));
          setTimeout(() => setMarkers(prev => prev.map(m => m.id===id ? { ...m, fading:true } : m)), 15000);
          setTimeout(() => setMarkers(prev => prev.filter(m => m.id!==id)), 16500);
        }
        return;
      }
      const key = cacheKey(terrain, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, spatialCtx.borderZone || false, spatialCtx.deepWild || false);
      const slot = isNight ? "night" : "day";
      if (!fragCache.current[key]) fragCache.current[key] = { day: [], night: [] };
      const bucket = fragCache.current[key][slot];
      let fragment;
      if (bucket.length > 0) {
        fragment = bucket.shift();
      } else {
        const pendingKey = key + ':' + slot;
        if (!pendingFetches.current[pendingKey]) {
          apiInFlight.current++;
          const promise = fetchBatch(terrain, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, spatialCtx.borderZone || false, spatialCtx.deepWild || false)
            .then(batch => {
              if (!fragCache.current[key]) fragCache.current[key] = { day: [], night: [] };
              fragCache.current[key][slot].push(...batch);
              return batch;
            })
            .finally(() => { delete pendingFetches.current[pendingKey]; apiInFlight.current = Math.max(0, apiInFlight.current - 1); });
          pendingFetches.current[pendingKey] = promise;
        }
        const batch = await pendingFetches.current[pendingKey];
        if (fragCache.current[key]?.[slot]?.length > 0) {
          fragment = fragCache.current[key][slot].shift();
        } else {
          fragment = batch[batch.length - 1];
        }
      }
      setMarkers(prev => prev.map(m => m.id===id ? { ...m, fragment, loading:false } : m));
      setTimeout(() => setMarkers(prev => prev.map(m => m.id===id ? { ...m, fading:true } : m)), 20000);
      setTimeout(() => setMarkers(prev => prev.filter(m => m.id!==id)), 21500);
      initAudio(genSeed, isNight).then(() => {
        const root = audioRoot.current ?? mapRootNote(genSeed);
        const arrivalFreq = pickArrivalNote(terrain, root);
        const preferred = TERRAIN_DEGREES[terrain] || TERRAIN_DEGREES[1];
        const semitones = preferred[Math.floor(Math.random() * preferred.length)];
        const octaveShift = Math.random() < 0.5 ? 12 : 24;
        recordArrivalNote(root + DORIAN[semitones % DORIAN.length] + octaveShift);
        playArrival(terrain, root, gx, gy);
      });
      const pendingKey = key + ':' + slot;
      if (fragCache.current[key][slot].length < 2 && !pendingFetches.current[pendingKey]) {
        apiInFlight.current++;
        const refillPromise = fetchBatch(terrain, spatialCtx.waterChar, spatialCtx.roadDensity, isNight, spatialCtx.borderZone || false, spatialCtx.deepWild || false)
          .then(batch => {
            if (!fragCache.current[key]) fragCache.current[key] = { day: [], night: [] };
            fragCache.current[key][slot].push(...batch);
            return batch;
          })
          .catch(() => {})
          .finally(() => { delete pendingFetches.current[pendingKey]; apiInFlight.current = Math.max(0, apiInFlight.current - 1); });
        pendingFetches.current[pendingKey] = refillPromise;
      }
      if (regions && fragment) {
        const rid = regions.regionOf[gy * cols + gx];
        if (rid >= 0) {
          attnRef.current[rid] = Math.min(1.0, (attnRef.current[rid] || 0) + 0.18);
          if (abandonedRegions.current.has(rid)) touristInterest.current[rid] = Date.now();
          // slightly reverse decay on early-stage roads near this region
          const REPAIR_AMOUNT = 0.04;
          const REPAIR_CAP = 0.5; // only repair roads below this decay level
          for (const seg of grownSegments.current) {
            if (seg.rid !== rid) continue;
            if (seg.decay > 0 && seg.decay < REPAIR_CAP)
              seg.decay = Math.max(0, seg.decay - REPAIR_AMOUNT);
          }
          const rdClick = regions.regionData[rid];
          if (rdClick) {
            const gcSegsClick = allRoadsRef.current.slice(gapCloserStartRef.current).filter(Array.isArray);
            for (let gi = 0; gi < gcSegsClick.length; gi++) {
              const d0 = gapCloserDecay.current[gi] || 0;
              if (d0 <= 0 || d0 >= REPAIR_CAP) continue;
              const seg = gcSegsClick[gi];
              if (seg.some(pt => Math.hypot(pt.x - rdClick.cx, pt.y - rdClick.cy) <= 15))
                gapCloserDecay.current[gi] = Math.max(0, d0 - REPAIR_AMOUNT);
            }
          }
          if (!regionFrags.current[rid]) regionFrags.current[rid] = [];
          const rf = regionFrags.current[rid];
          if (!rf.includes(fragment)) {
            rf.push(fragment);
            if (rf.length > 12) rf.shift();
          }
          if (regions.macroOf) {
            const mid = regions.macroOf[rid];
            if (!macroClicks.current[mid]) macroClicks.current[mid] = [];
            macroClicks.current[mid].push({ x: gx, y: gy });
            const clicks = macroClicks.current[mid];
            const MACRO_THRESH = Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length < 2 ? 2 : 3;
            if (clicks.length >= MACRO_THRESH) {
              const recentClicks = clicks.slice(-MACRO_THRESH);
              const cx = Math.round(recentClicks.reduce((s, c) => s + c.x, 0) / recentClicks.length);
              const cy = Math.round(recentClicks.reduce((s, c) => s + c.y, 0) / recentClicks.length);
              let targetRid = regions.regionOf[cy * cols + cx];
              if (targetRid < 0) {
                const mRids = new Set(regions.macroMembers[mid]);
                let best = Infinity;
                for (const mrid of mRids) {
                  const r = regions.regionData[mrid];
                  if (!r) continue;
                  const d = Math.hypot(r.cx - cx, r.cy - cy);
                  if (d < best) { best = d; targetRid = mrid; }
                }
              }
              if (targetRid >= 0 && !toponymsRef.current[targetRid] && !pendingToponyms.current.has(targetRid)) {
                const rd = regions.regionData[targetRid];
                const trf = regionFrags.current[targetRid] || rf;
                pendingToponyms.current.add(targetRid);
                apiInFlight.current++;
                callAPIToponym(withPins(trf, regionPins.current[targetRid] || []), rd.dominantTerrain, spatialCtx.waterChar, isNight)
                  .then(name => { namedAt.current[targetRid] = Date.now(); setToponymsSync(prev => ({ ...prev, [targetRid]: name }), rd.cx, rd.cy); })
                  .catch(() => {})
                  .finally(() => { pendingToponyms.current.delete(targetRid); apiInFlight.current = Math.max(0, apiInFlight.current - 1); });
                macroClicks.current[mid] = [];
              }
            }
          }
        }
      }
      if (hydroRegions && fragment) {
        let hrid = -1;
        if (terrain === 0) {
          hrid = hydroRegions.hydroOf[gy * cols + gx];
        } else {
          hrid = nearestHydroRegion(gx, gy, hydroRegions.hydroOf, cols, rows);
        }
        if (hrid >= 0) {
          const hd = hydroRegions.hydroData[hrid];
          if (hd && hd.size >= HYDRO_MIN_CELLS) {
            if (!hydroFrags.current[hrid]) hydroFrags.current[hrid] = [];
            const hf = hydroFrags.current[hrid];
            if (!hf.includes(fragment)) {
              hf.push(fragment);
              if (hf.length > 12) hf.shift();
            }
            const hydroThresh = Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length < 2 ? 2 : 3;
            if (!hydronymsRef.current[hrid] && !pendingHydros.current.has(hrid) && hf.length >= hydroThresh) {
              pendingHydros.current.add(hrid);
              callAPIHydronym(withPins(hf, hydroPins.current[hrid] || []), hd.isCoastal, isNight)
                .then(name => setHydronymsSync(prev => ({ ...prev, [hrid]: name }), hd.cx, hd.cy))
                .catch(() => {})
                .finally(() => { pendingHydros.current.delete(hrid); });
            }
          }
        }
      }
    } catch(err) {
      const dataLoss = randomDataLoss();
      setMarkers(prev => prev.map(m => m.id===id ? { ...m, dataLoss, loading:false } : m));
      setTimeout(() => setMarkers(prev => prev.map(m => m.id===id ? { ...m, fading:true } : m)), 20000);
      setTimeout(() => setMarkers(prev => prev.filter(m => m.id!==id)), 21500);
    }
  }, [cells, currentTime, cols, rows, modal, routeLoading, regions, toponyms, reportStep, reportEvent, reportChoices, reportNotes, hydroRegions]);
  const triggerWeatherAlert = useCallback(async () => {
    if (!cells || !canvasRef.current || !currentTime) return;
    try {
    const canvas = canvasRef.current;
    const thumbCanvas = document.createElement('canvas');
    const thumbScale = 0.4;
    thumbCanvas.width = Math.round(canvas.width * thumbScale);
    thumbCanvas.height = Math.round(canvas.height * thumbScale);
    const thumbCtx = thumbCanvas.getContext('2d');
    thumbCtx.drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);
    const imageB64 = thumbCanvas.toDataURL('image/jpeg', 0.6).split(',')[1];
    const allNames = [
      ...Object.values(toponymsRef.current),
      ...Object.values(hydronymsRef.current),
    ].filter(Boolean);
    const shuffle = arr => arr.slice().sort(() => Math.random() - 0.5);
    const placeFocus = shuffle(allNames).slice(0, 2 + Math.floor(Math.random() * 3));
    const counts = { water: 0, settled: 0, wilderness: 0, sparse: 0 };
    let total = 0;
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < cols; x++) {
        const t = cells[y][x];
        if (t === T.WATER)           counts.water++;
        else if (t === T.LAND)       counts.settled++;
        else if (t === T.WILDERNESS) counts.wilderness++;
        else                         counts.sparse++;
        total++;
      }
    const terrainMix = Object.fromEntries(
      Object.entries(counts).map(([k,v]) => [k, Math.round(v / total * 100)])
    );
    const DIRS = ['N','NE','E','SE','S','SW','W','NW'];
    const dirStr = (dx, dy) => DIRS[Math.round((Math.atan2(dy, dx) / Math.PI * 4 + 8)) % 8];
    const anchoredReports = [];
    const seenNotes = new Set();
    const noteToRaw = new Map();
    if (regions) {
      Object.entries(regionPins.current).forEach(([ridStr, pins]) => {
        const rid = Number(ridStr);
        pins.forEach(note => {
          if (!note || seenNotes.has(note)) return;
          seenNotes.add(note);
          const cleanNote = note.split('').filter(c => c !== '`' && c !== '\n').join('').slice(0, 80);
          let nearestName = null, nearestDist = Infinity, nearestDx = 0, nearestDy = 0;
          const srcRd = regions.regionData[rid];
          Object.entries(toponymsRef.current).forEach(([nridStr, name]) => {
            if (!name) return;
            const nrd = regions.regionData[Number(nridStr)];
            if (!nrd) return;
            const dx = nrd.cx - srcRd.cx, dy = nrd.cy - srcRd.cy;
            const d = Math.sqrt(dx*dx + dy*dy);
            if (d < nearestDist) { nearestDist = d; nearestName = name; nearestDx = dx; nearestDy = dy; }
          });
          const formatted = nearestName
            ? `${cleanNote} — ${dirStr(nearestDx, nearestDy)} of ${nearestName}`
            : cleanNote;
          anchoredReports.push(formatted);
          noteToRaw.set(formatted, note);
        });
      });
    }
    const currentNotes = reportNotes.trim();
    if (currentNotes && !seenNotes.has(currentNotes)) {
      const formatted = currentNotes.replace(/[\n]/g, ' ').slice(0, 80);
      anchoredReports.push(formatted);
      noteToRaw.set(formatted, currentNotes);
    }
    const finalReports = shuffle(anchoredReports).slice(0, 3);
    const consumedRaw = new Set(finalReports.map(f => noteToRaw.get(f)).filter(Boolean));
    try {
      const { alertText, cta } = await callAPIWeatherAlert(
        imageB64, placeFocus, terrainMix,
        currentTime.str, isNightTime(timeMins), finalReports
      );
      if (consumedRaw.size > 0) {
        Object.keys(regionPins.current).forEach(rid => {
          regionPins.current[rid] = (regionPins.current[rid] || []).filter(n => !consumedRaw.has(n));
        });
        Object.keys(hydroPins.current).forEach(hid => {
          hydroPins.current[hid] = (hydroPins.current[hid] || []).filter(n => !consumedRaw.has(n));
        });
      }
      setWeatherAlert({ alertText, cta, status: 'toast' });
      setWeatherToastVisible(true);
      setTimeout(() => setWeatherToastVisible(false), 8000);
    } catch(e) {
      console.warn('Weather alert inner failed:', e.message);
    }
    } catch(e) {
      console.warn('Weather alert outer failed:', e.message);
    }
  }, [cells, currentTime, timeMins, toponyms, hydronyms, reportNotes, rows, cols, regions]);
  triggerWeatherAlertRef.current = triggerWeatherAlert;
  useEffect(() => {
    if (firstAlertFiredRef.current) return;
    const totalNames = Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length;
    if (totalNames < 3) return;
    firstAlertFiredRef.current = true;
    const delay = 20000 + Math.random() * 20000;
    setTimeout(() => { if (triggerWeatherAlertRef.current) triggerWeatherAlertRef.current(); }, delay);
  }, [Object.keys(toponyms).length + Object.keys(hydronyms).length]);
  useEffect(() => {
    if (timeMins === null || !cells) return;
    const slot = Math.floor(totalInGameMins.current / 1440);
    if (slot === alertTriggeredRef.current) return;
    if (slot < 1) { alertTriggeredRef.current = slot; return; } // day 0 covered by guaranteed first alert
    const totalNames = Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length;
    if (totalNames < 2) return;
    // count unused field reports (pins not yet consumed by an alert)
    let pendingReports = 0;
    Object.values(regionPins.current).forEach(arr => { pendingReports += (arr?.length || 0); });
    Object.values(hydroPins.current).forEach(arr => { pendingReports += (arr?.length || 0); });
    // base 70% fire chance, raised toward certainty as unused reports accumulate
    const fireChance = Math.min(0.95, 0.70 + pendingReports * 0.12);
    if (Math.random() > fireChance) { alertTriggeredRef.current = slot; return; }
    alertTriggeredRef.current = slot;
    setTimeout(() => { if (triggerWeatherAlertRef.current) triggerWeatherAlertRef.current(); }, 3000);
  }, [timeMins]);
  const handleFindRoute = useCallback(async () => {
    if (!cells || !dist || !dest.trim() || routeLoading || !currentTime || !roadGridRef.current) return;
    setModal(false); setRouteLoading(true);
    const roadCells = [];
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < cols; x++)
        if (roadGridRef.current[y * cols + x]) roadCells.push({ x, y });
    if (roadCells.length < 2) { setRouteLoading(false); return; }
    const start = roadCells[(Math.random() * roadCells.length) | 0];
    const prev = new Int32Array(cols * rows).fill(-1);
    const visited = new Uint8Array(cols * rows);
    const bfsQueue = [start.y * cols + start.x];
    visited[start.y * cols + start.x] = 1;
    const dx8 = [1,-1,0,0,1,1,-1,-1], dy8 = [0,0,1,-1,1,-1,1,-1];
    let head = 0;
    while (head < bfsQueue.length) {
      const idx = bfsQueue[head++];
      const cx = idx % cols, cy = (idx / cols) | 0;
      for (let d = 0; d < 8; d++) {
        const nx = cx + dx8[d], ny = cy + dy8[d];
        if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue;
        const ni = ny * cols + nx;
        if (visited[ni] || !roadGridRef.current[ni]) continue;
        visited[ni] = 1; prev[ni] = bfsQueue[head-1]; bfsQueue.push(ni);
      }
    }
    const ranked = bfsQueue
      .map(idx => ({ idx, d: Math.hypot(idx % cols - start.x, ((idx / cols) | 0) - start.y) }))
      .sort((a, b) => b.d - a.d);
    const poolSize = Math.max(1, Math.floor(ranked.length * 0.20));
    const picked = ranked[Math.floor(Math.random() * poolSize)];
    const endIdx = picked.idx;
    const end = { x: endIdx % cols, y: (endIdx / cols) | 0 };
    const path = [];
    let cur = endIdx;
    while (cur !== -1) {
      path.push({ x: cur % cols, y: (cur / cols) | 0 });
      cur = prev[cur];
    }
    path.reverse();
    try {
      const sCtx = getSpatialContext(start.x, start.y, cells, roadGridRef.current, cols, rows, hydroRegions?.hydroOf, hydroRegions?.hydroData, hydroRegions?.coastalOf);
      const eCtx = getSpatialContext(end.x, end.y, cells, roadGridRef.current, cols, rows, hydroRegions?.hydroOf, hydroRegions?.hydroData, hydroRegions?.coastalOf);
      const [sf, ef] = await Promise.all([
        callAPISingle(cells[start.y][start.x], currentTime, dest, sCtx),
        callAPISingle(cells[end.y][end.x],     currentTime, dest, eCtx),
      ]);
      routeTimers.current.forEach(clearTimeout);
      setRoute({ start:{...start,fragment:sf}, end:{...end,fragment:ef}, path });
      setRouteFading(false);
      setMarkers([]);
      routeTimers.current = [
        setTimeout(() => setRouteFading(true), 30000),
        setTimeout(() => { setRoute(null); setRouteFading(false); }, 31500),
      ];
      const isNightNow = isNightTime(timeMins);
      [[start, sf], [end, ef]].forEach(([pt, frag]) => {
        if (!frag) return;
        if (regions) {
          const rid = regions.regionOf[pt.y * cols + pt.x];
          if (rid >= 0) {
            if (!regionFrags.current[rid]) regionFrags.current[rid] = [];
            const rf = regionFrags.current[rid];
            if (!rf.includes(frag)) { rf.push(frag); if (rf.length > 12) rf.shift(); }
            if (!toponymsRef.current[rid] && rf.length >= (Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length < 2 ? 2 : 3)) {
              const rd = regions.regionData[rid];
              const sCtxLocal = getSpatialContext(pt.x, pt.y, cells, roadGridRef.current, cols, rows, hydroRegions?.hydroOf, hydroRegions?.hydroData, hydroRegions?.coastalOf);
              callAPIToponym(withPins(rf, regionPins.current[rid] || []), rd.dominantTerrain, sCtxLocal.waterChar, isNightNow)
                .then(name => setToponymsSync(prev => ({ ...prev, [rid]: name }), rd.cx, rd.cy))
                .catch(() => {});
            }
          }
        }
        if (hydroRegions) {
          const terrain = cells[pt.y][pt.x];
          const hrid = terrain === 0
            ? hydroRegions.hydroOf[pt.y * cols + pt.x]
            : nearestHydroRegion(pt.x, pt.y, hydroRegions.hydroOf, cols, rows);
          if (hrid >= 0) {
            const hd = hydroRegions.hydroData[hrid];
            if (hd && hd.size >= HYDRO_MIN_CELLS) {
              if (!hydroFrags.current[hrid]) hydroFrags.current[hrid] = [];
              const hf = hydroFrags.current[hrid];
              if (!hf.includes(frag)) { hf.push(frag); if (hf.length > 12) hf.shift(); }
              if (!hydronymsRef.current[hrid] && hf.length >= (Object.keys(toponymsRef.current).length + Object.keys(hydronymsRef.current).length < 2 ? 2 : 3)) {
                callAPIHydronym(withPins(hf, hydroPins.current[hrid] || []), hd.isCoastal, isNightNow)
                  .then(name => setHydronymsSync(prev => ({ ...prev, [hrid]: name }), hd.cx, hd.cy))
                  .catch(() => {});
              }
            }
          }
        }
      });
    } catch(err) {
      routeTimers.current.forEach(clearTimeout);
      setRoute({ start:{...start,fragment:err.message}, end:{...end,fragment:err.message}, path });
      setRouteFading(false);
      setMarkers([]);
      routeTimers.current = [
        setTimeout(() => setRouteFading(true), 30000),
        setTimeout(() => { setRoute(null); setRouteFading(false); }, 31500),
      ];
    }
    setDest(""); setRouteLoading(false);
  }, [cells, dist, dest, currentTime, cols, rows, routeLoading, genSeed, regions, hydroRegions, timeMins]);
  const isNight = pal?.isNight ?? false;
  const sessionHex = genSeed.toString(16).toUpperCase().padStart(6,'0');
  const uiBg      = isNight ? "#1e2a1e" : "#eaf0ea";
  const uiFg      = isNight ? "#a0b8b0" : "#2a3c38";
  const uiAccent  = pal?.road || ROAD_COLOR;
  const uiBorder  = isNight ? "#3a5450" : "#9ab8b0";
  const uiSubtle  = isNight ? "#2a3a50" : "#e8e0cc";
  return (
    <div style={{ position:"fixed", inset:0, overflow:"hidden",
      cursor:modal||reportModal?"default":reportStep==="placing"?"cell":"crosshair", background:pal?.bg||"#162030",
      display:"flex", flexDirection:"column" }}>
      <style>{`@keyframes xhair-pulse { 0%,100% { opacity:1; } 50% { opacity:0.25; } }`}</style>
      {}
      <div style={{
        flexShrink: 0, height: HEADER_H, display: 'flex', alignItems: 'center',
        background: isNight ? 'rgba(14,20,16,0.97)' : 'rgba(225,235,225,0.97)',
        borderBottom: `1px solid ${isNight ? '#2a3a30' : '#b0c8b8'}`,
        fontFamily: '"Courier New",Courier,monospace',
        zIndex: 30, pointerEvents: 'none',
        position: 'relative',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', width: '100%',
          fontSize: 8, letterSpacing: '0.18em',
          color: isNight ? '#4a6054' : '#7a9888',
          padding: '0 10px', boxSizing: 'border-box',
        }}>
          <span>PROVISIONAL ATLAS OF MINOR WORLDS</span>
        </div>
      </div>
      <div style={{ position:"relative", flex:1, overflow:"hidden" }}>
      <canvas ref={canvasRef} style={{ display:"block", imageRendering:"pixelated", boxSizing:"border-box", outline: `1px solid ${isNight ? '#2a3a30' : '#b0c8b8'}` }} onClick={handleClick}/>
      {debugRegions === 1 && regions && (() => {
        const REGION_COLORS = [
          '#e05050','#e08030','#d0b020','#80c030','#30b060',
          '#20c0a0','#2090c0','#4060d0','#8040c0','#c030a0',
          '#e07070','#e0a060','#d0c860','#a0d060','#60c890',
          '#50d0c0','#60a8d8','#7080e0','#a868d0','#d860b8',
          '#c04040','#c06020','#a89010','#60a020','#208050',
          '#10a080','#1070a0','#3040b0',
        ];
        const W = cols * PIXEL, H = rows * PIXEL;
        return (
          <canvas
            width={W} height={H}
            style={{ position:'absolute', top:0, left:0, pointerEvents:'none', imageRendering:'pixelated', opacity:0.35 }}
            ref={el => {
              if (!el) return;
              const ctx = el.getContext('2d');
              ctx.clearRect(0,0,W,H);
              for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                  const rid = regions.regionOf[y * cols + x];
                  if (rid < 0) continue;
                  ctx.fillStyle = REGION_COLORS[rid % REGION_COLORS.length];
                  ctx.fillRect(x*PIXEL, y*PIXEL, PIXEL, PIXEL);
                }
              }
            }}
          />
        );
      })()}
      {debugRegions === 1 && hydroRegions && (() => {
        const HYDRO_COLORS = [
          '#2080e0','#20c0e0','#2060c0','#40a0d0','#6080e0',
          '#20e0c0','#4060d0','#80c0e0','#20a0c0','#6040e0',
        ];
        const W = cols * PIXEL, H = rows * PIXEL;
        return (
          <canvas
            width={W} height={H}
            style={{ position:'absolute', top:0, left:0, pointerEvents:'none', imageRendering:'pixelated', opacity:0.45 }}
            ref={el => {
              if (!el) return;
              const ctx = el.getContext('2d');
              ctx.clearRect(0,0,W,H);
              for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                  const hrid = hydroRegions.hydroOf[y * cols + x];
                  if (hrid < 0) continue;
                  ctx.fillStyle = HYDRO_COLORS[hrid % HYDRO_COLORS.length];
                  ctx.fillRect(x*PIXEL, y*PIXEL, PIXEL, PIXEL);
                }
              }
            }}
          />
        );
      })()}

      {debugRegions === 2 && regions?.macroMembers && (() => {
        const MACRO_COLORS = [
          '#c030f0','#f03060','#f09020','#30c040','#2080f0',
          '#f0c020','#20d0c0','#f05090','#70c020','#4050f0',
        ];
        const W = cols * PIXEL, H = rows * PIXEL;
        return (
          <canvas
            width={W} height={H}
            style={{ position:'absolute', top:0, left:0, pointerEvents:'none', imageRendering:'pixelated', opacity:0.4 }}
            ref={el => {
              if (!el) return;
              const ctx = el.getContext('2d');
              ctx.clearRect(0,0,W,H);
              const ridToMacro = new Int16Array(regions.regionData.length);
              regions.macroMembers.forEach((rids, mid) => rids.forEach(rid => { ridToMacro[rid] = mid; }));
              for (let y = 0; y < rows; y++) {
                for (let x = 0; x < cols; x++) {
                  const rid = regions.regionOf[y * cols + x];
                  if (rid < 0) continue;
                  ctx.fillStyle = MACRO_COLORS[ridToMacro[rid] % MACRO_COLORS.length];
                  ctx.fillRect(x*PIXEL, y*PIXEL, PIXEL, PIXEL);
                }
              }
            }}
          />
        );
      })()}
      {currentTime && (
        <div style={{
          position:"absolute", top:12, right:60,
          background:uiBg, color:uiAccent,
          border:`1px solid ${uiBorder}`,
          fontFamily:'"Courier New",monospace', fontSize:22, fontWeight:"bold",
          padding:"4px 14px", borderRadius:2, letterSpacing:3, pointerEvents:"none",
        }}>{currentTime.str}</div>
      )}
      <button onClick={() => setGenSeed((Math.random()*9999)|0)} style={{
        position:"absolute", top:12, right:12, width:40, height:40,
        background:uiAccent, border:"none", color:uiBg, fontSize:22,
        cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:2,
        paddingBottom:3,
      }} title="NEW SESSION">↻</button>
      {debugVisible && <>
      <button onClick={() => { if (triggerWeatherAlertRef.current) triggerWeatherAlertRef.current(); }} style={{
        position:"absolute", top:60, right:12, width:40, height:24,
        background:'transparent', border:`1px solid ${uiBorder}`, color:uiAccent,
        fontSize:9, letterSpacing:'0.08em', cursor:"pointer", borderRadius:2,
        fontFamily:'"Courier New",monospace',
      }}>⚠ DBG</button>
      <button onClick={() => setDebugCtx(d => !d)} style={{
        position:"absolute", top:90, right:12, width:40, height:24,
        background: debugCtx ? uiAccent : 'transparent',
        border:`1px solid ${uiBorder}`, color: debugCtx ? uiBg : uiAccent,
        fontSize:9, letterSpacing:'0.08em', cursor:"pointer", borderRadius:2,
        fontFamily:'"Courier New",monospace',
      }}>CTX</button>
      <button onClick={() => setDebugAbandon(d => !d)} style={{
        position:"absolute", top:120, right:12, width:40, height:24,
        background: debugAbandon ? '#8a4020' : 'transparent',
        border:`1px solid ${debugAbandon ? '#8a4020' : uiBorder}`,
        color: debugAbandon ? '#f0c080' : uiAccent,
        fontSize:9, letterSpacing:'0.08em', cursor:"pointer", borderRadius:2,
        fontFamily:'"Courier New",monospace',
      }}>ABN</button>
      {abandonedRegions.current.size > 0 && (
        <div style={{
          position:'absolute', top:150, right:12, width:40, height:16,
          fontSize:8, letterSpacing:'0.06em', color:'#b07840',
          fontFamily:'"Courier New",monospace', textAlign:'center', lineHeight:'16px',
        }}>{abandonedRegions.current.size} ABN</div>
      )}
      <button onClick={() => setDebugRegions(d => (d + 1) % 3)} style={{
        position:"absolute", top:172, right:12, width:40, height:24,
        background: debugRegions === 1 ? '#204840' : debugRegions === 2 ? '#402060' : 'transparent',
        border:`1px solid ${debugRegions === 1 ? '#40887a' : debugRegions === 2 ? '#8040b0' : uiBorder}`,
        color: debugRegions === 1 ? '#80c8b8' : debugRegions === 2 ? '#c080f0' : uiAccent,
        fontSize:9, letterSpacing:'0.08em', cursor:"pointer", borderRadius:2,
        fontFamily:'"Courier New",monospace',
      }}>{debugRegions === 2 ? 'MCR' : 'RGN'}</button>
      </>}
      {}
      {regions && roadGridRef.current && Object.entries(toponyms).map(([ridStr, name]) => {
        const rid = Number(ridStr);
        const rd = regions.regionData[rid];
        if (!rd || rd.cells.length === 0) return null;
        const estW = name.length * 6.8;
        const estH = 12;
        const edgeMargin = 8;
        const placedTopoNoFly = Object.entries(labelPositions.current)
          .filter(([r]) => Number(r) !== rid)
          .map(([r, pos]) => {
            const tname = toponyms[r];
            if (!tname) return null;
            const tw = tname.length * 6.8 + 8;
            const th = 14;
            return { x1: pos.x - tw/2 - 4, y1: pos.y - th/2 - 4, x2: pos.x + tw/2 + 4, y2: pos.y + th/2 + 4 };
          }).filter(Boolean);
        const noFly = [
          { x1: dims.w - 165, y1: 0,           x2: dims.w, y2: 58 },
          { x1: dims.w - 80,  y1: dims.h - 80,  x2: dims.w, y2: dims.h },
          { x1: 0,            y1: 0,             x2: dims.w, y2: 26 },
          ...placedTopoNoFly,
        ];
        const candidates = rd.cells.length <= 40
          ? rd.cells
          : rd.cells.filter((_, i) => i % Math.ceil(rd.cells.length / 40) === 0);
        let finalX, finalY;
        if (labelPositions.current[rid] !== undefined) {
          ({ x: finalX, y: finalY } = labelPositions.current[rid]);
        } else {
          let bestCell = null, bestScore = -Infinity;
          for (const cell of candidates) {
            const px = cell.x * PIXEL;
            const py = cell.y * PIXEL;
            const lx1 = px - estW / 2, lx2 = px + estW / 2;
            const ly1 = py - estH / 2, ly2 = py + estH / 2;
            if (lx1 < edgeMargin || lx2 > dims.w - edgeMargin) continue;
            if (ly1 < edgeMargin || ly2 > dims.h - edgeMargin) continue;
            if (noFly.some(z => lx1 < z.x2 && lx2 > z.x1 && ly1 < z.y2 && ly2 > z.y1)) continue;
            let minRoadDist = Infinity;
            const scanR = 6;
            for (let dy = -scanR; dy <= scanR; dy++) {
              for (let dx = -scanR; dx <= scanR; dx++) {
                const nx = cell.x + dx, ny = cell.y + dy;
                if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
                if (roadGridRef.current[ny * cols + nx]) {
                  const d = Math.hypot(dx, dy);
                  if (d < minRoadDist) minRoadDist = d;
                }
              }
            }
            const score = minRoadDist === Infinity ? scanR + 1 : minRoadDist;
            if (score > bestScore) { bestScore = score; bestCell = cell; }
          }
          finalX = bestCell ? bestCell.x * PIXEL : rd.cx * PIXEL;
          finalY = bestCell ? bestCell.y * PIXEL : rd.cy * PIXEL;
          labelPositions.current[rid] = { x: finalX, y: finalY };
        }
        const isAbandoned = abandonedRegions.current.has(rid);
        const suffix = regionSuffixes.current[rid] || '';
        const labelColor = isAbandoned
          ? (pal?.isNight ? '#b07840' : '#8a5028')
          : (pal?.isNight ? '#a0b8a8' : '#2a3c38');
        return (
          <div key={`topo-${rid}`} style={{
            position: 'absolute',
            left: finalX,
            top: finalY,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 5,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}>
            <div style={{
              fontFamily: '"Futura", "Futura PT", "Century Gothic", sans-serif',
              fontWeight: 600,
              fontSize: 9,
              letterSpacing: '0.12em',
              color: labelColor,
              opacity: 0.78,
            }}>{name}</div>
            {isAbandoned && suffix && (
              <div style={{
                fontFamily: '"Futura", "Futura PT", "Century Gothic", sans-serif',
                fontWeight: 600,
                fontSize: 9,
                letterSpacing: '0.12em',
                color: pal?.isNight ? '#8a5828' : '#7a4020',
                opacity: 0.78,
                marginTop: 1,
              }}>{suffix}</div>
            )}
          </div>
        );
      })}
      {}
      {hydroRegions && Object.entries(hydronyms).map(([ridStr, name]) => {
        const hrid = Number(ridStr);
        const hd = hydroRegions.hydroData[hrid];
        if (!hd || hd.size < HYDRO_MIN_CELLS) return null;
        const estW = name.length * 6.2;
        const estH = 11;
        const edgeMargin = 8;
        const toponymNoFly = regions ? Object.entries(labelPositions.current).map(([rid, pos]) => {
          const rd = regions.regionData[Number(rid)];
          const tname = toponyms[rid];
          if (!rd || !tname) return null;
          const tw = tname.length * 6.8 + 8;
          const th = 14;
          return { x1: pos.x - tw/2 - 4, y1: pos.y - th/2 - 4, x2: pos.x + tw/2 + 4, y2: pos.y + th/2 + 4 };
        }).filter(Boolean) : [];
        const noFly = [
          { x1: dims.w - 165, y1: 0,           x2: dims.w, y2: 58 },
          { x1: dims.w - 80,  y1: dims.h - 80,  x2: dims.w, y2: dims.h },
          ...toponymNoFly,
        ];
        const candidates = hd.cells.length <= 40
          ? hd.cells
          : hd.cells.filter((_, i) => i % Math.ceil(hd.cells.length / 40) === 0);
        let bestCell = null, bestScore = -Infinity;
        for (const cell of candidates) {
          const px = cell.x * PIXEL;
          const py = cell.y * PIXEL;
          const lx1 = px - estW / 2, lx2 = px + estW / 2;
          const ly1 = py - estH / 2, ly2 = py + estH / 2;
          if (lx1 < edgeMargin || lx2 > dims.w - edgeMargin) continue;
          if (ly1 < edgeMargin || ly2 > dims.h - edgeMargin) continue;
          if (noFly.some(z => lx1 < z.x2 && lx2 > z.x1 && ly1 < z.y2 && ly2 > z.y1)) continue;
          let minLandDist = Infinity;
          const scanR = 5;
          for (let dy = -scanR; dy <= scanR; dy++) {
            for (let dx = -scanR; dx <= scanR; dx++) {
              const nx = cell.x + dx, ny = cell.y + dy;
              if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
              if (cells[ny][nx] !== 0) {
                const d = Math.hypot(dx, dy);
                if (d < minLandDist) minLandDist = d;
              }
            }
          }
          const score = minLandDist === Infinity ? scanR + 1 : minLandDist;
          if (score > bestScore) { bestScore = score; bestCell = cell; }
        }
        const finalX = bestCell ? bestCell.x * PIXEL : hd.cx * PIXEL;
        const finalY = bestCell ? bestCell.y * PIXEL : hd.cy * PIXEL;
        const labelColor = pal?.isNight ? '#6ab0b8' : '#3a7880';
        return (
          <div key={`hydro-${hrid}`} style={{
            position: 'absolute',
            left: finalX,
            top: finalY,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 5,
            fontFamily: '"Futura", "Futura PT", "Century Gothic", sans-serif',
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: 8,
            letterSpacing: '0.10em',
            color: labelColor,
            opacity: 0.80,
            whiteSpace: 'nowrap',
          }}>
            {name}
          </div>
        );
      })}
      {}
      {reportMarkers.map(m => (
        <div key={m.id}>
          <Crosshair px={cpx(m.gx)} py={cpy(m.gy)} pal={pal} fading={m.fading} isReport/>
          <Card px={cpx(m.gx)} py={cpy(m.gy)} gx={m.gx} gy={m.gy} text={m.fragment} loading={m.loading} error={null}
            W={dims.w} H={dims.h} pal={pal} fading={m.fading} isReport reportLabel={m.eventLabel}/>
        </div>
      ))}
      {markers.map(m => (
        <div key={m.id}>
          <Crosshair px={cpx(m.gx)} py={cpy(m.gy)} pal={pal} fading={m.fading} loading={m.loading}/>
          <Card px={cpx(m.gx)} py={cpy(m.gy)} gx={m.gx} gy={m.gy} text={m.fragment} loading={m.loading} error={m.error} dataLoss={m.dataLoss} W={dims.w} H={dims.h} pal={pal} fading={m.fading} spatialCtx={m.spatialCtx} debugCtx={debugCtx}/>
        </div>
      ))}
      {route && (() => {
        const CARD_H_EST = 120;
        const PAD = 8;
        const pts = [route.start, route.end];
        const positions = pts.map((pt, i) => {
          const other = pts[1 - i];
          const px = cpx(pt.x), py = cpy(pt.y);
          const opx = cpx(other.x);
          const left = Math.max(4, Math.min(dims.w - CARD_W - 4,
            px > opx ? px - CARD_W - 14 : px + 14));
          const top = Math.max(8, Math.min(py - 18, dims.h - CARD_H_EST - 8));
          return { px, py, left, top };
        });
        const a = positions[0], b = positions[1];
        const horizOverlap = a.left < b.left + CARD_W + PAD && b.left < a.left + CARD_W + PAD;
        const vertOverlap  = a.top  < b.top  + CARD_H_EST + PAD && b.top  < a.top  + CARD_H_EST + PAD;
        if (horizOverlap && vertOverlap) {
          const newTop = a.top + CARD_H_EST + PAD;
          if (newTop + CARD_H_EST < dims.h) {
            positions[1].top = newTop;
          } else {
            positions[1].top = Math.max(8, a.top - CARD_H_EST - PAD);
          }
        }
        return pts.map((pt, i) => {
          const { px, py, left, top } = positions[i];
          return (
            <div key={i}>
              <Crosshair px={px} py={py} pal={pal} fading={routeFading} isRoute/>
              <Card px={px} py={py} gx={pt.x} gy={pt.y} text={pt.fragment} loading={false} error={null} W={dims.w} H={dims.h} pal={pal} fading={routeFading} isRoute forcedLeft={left} forcedTop={top}/>
            </div>
          );
        });
      })()}
      <div onClick={() => !routeLoading && setModal(true)} style={{
        position:"absolute", bottom:20, right:20, width:50, height:50,
        background:routeLoading ? uiBorder : uiAccent,
        transform:"rotate(45deg)", borderRadius:4,
        cursor:routeLoading?"wait":"pointer",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:20,
      }}>
        {routeLoading
          ? <span style={{ transform:"rotate(-45deg)", color:uiBg, fontSize:20, lineHeight:1 }}>…</span>
          : <svg width="18" height="18" viewBox="0 0 18 18" style={{ transform:"rotate(-45deg)" }} fill="none">
              <line x1="3" y1="15" x2="15" y2="3" stroke={uiBg} strokeWidth="2" strokeLinecap="round"/>
              <polyline points="7,3 15,3 15,11" stroke={uiBg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
        }
      </div>
      {}
      {weatherAlert && weatherAlert.status === 'toast' && (
        <div onClick={() => {
          setWeatherToastVisible(false);
          setWeatherAlert(a => ({ ...a, status: 'open' }));
          weatherPaused.current = true;
          try { startWeatherMelody(audioRoot.current ?? mapRootNote(genSeed), isNight); } catch(e) {}
        }} style={{
          position: 'absolute', top: weatherToastVisible ? 12 : -80,
          left: '50%', transform: 'translateX(-50%)',
          transition: 'top 0.5s cubic-bezier(0.22,1,0.36,1)',
          background: isNight ? '#1e2a1e' : '#eaf0ea',
          border: `2px solid ${pal?.road || ROAD_COLOR}`,
          padding: '8px 18px', cursor: 'pointer', zIndex: 60,
          fontFamily: '"Courier New",Courier,monospace', fontSize: 11,
          color: pal?.road || ROAD_COLOR, letterSpacing: '0.1em',
          whiteSpace: 'nowrap', userSelect: 'none',
        }}>
          ⚠ REGIONAL ALERT — tap to open
        </div>
      )}
      {}
      {weatherAlert && weatherAlert.status === 'open' && weatherAlert.alertText && weatherAlert.alertText.length > 0 && (() => {
        const amber = isNight ? '#b87820' : '#c8860a';
        const dismissAlert = () => {
          const ctaWord = weatherAlert.cta;
          Object.keys(regionPins.current).forEach(rid => {
            if (!regionPins.current[rid]) regionPins.current[rid] = [];
            regionPins.current[rid].unshift(ctaWord);
          });
          Object.keys(hydroPins.current).forEach(hid => {
            if (!hydroPins.current[hid]) hydroPins.current[hid] = [];
            hydroPins.current[hid].unshift(ctaWord);
          });
          setTimeout(() => {
            Object.keys(regionPins.current).forEach(rid => {
              regionPins.current[rid] = (regionPins.current[rid] || []).filter(p => p !== ctaWord);
            });
            Object.keys(hydroPins.current).forEach(hid => {
              hydroPins.current[hid] = (hydroPins.current[hid] || []).filter(p => p !== ctaWord);
            });
          }, 100000);
          weatherPaused.current = false;
          stopWeatherMelody();
          setWeatherAlert(null);
        };
        return (
          <WeatherModalPanel
            key={weatherAlert.alertText}
            alertText={weatherAlert.alertText}
            cta={weatherAlert.cta}
            timeStr={currentTime?.str}
            amber={amber}
            isNight={isNight}
            licenseHex={LICENSE_HEX}
            onDismiss={dismissAlert}
          />
        );
      })()}
      {}
      <div onClick={() => { setReportModal(true); setReportStep('select'); setReportEvent(null); setReportChoices({}); setReportNotes(''); }}
        style={{
          position:"absolute", bottom:90, right:21, width:48, height:48,
          background: reportStep === 'placing' ? uiAccent : uiBg,
          border:`2px solid ${uiAccent}`,
          borderRadius:4, cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:20,
        }}>
        <span style={{ color: reportStep === 'placing' ? uiBg : uiAccent, fontSize:22, lineHeight:1, fontFamily:'monospace' }}>!</span>
      </div>
      {}
      {reportStep === 'placing' && (
        <div style={{
          position:"absolute", bottom:140, right:12,
          fontFamily:'"Courier New",monospace', fontSize:10,
          color:uiAccent, opacity:0.8, pointerEvents:"none",
          whiteSpace:"nowrap", letterSpacing:1,
        }}>SELECT COORDINATES FOR REPORT SUBMISSION</div>
      )}
      {modal && (
        <div style={{
          position:"absolute", inset:0,
          background: isNight ? "rgba(14,20,30,0.78)" : "rgba(40,28,18,0.55)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:50,
        }} onClick={e => { if(e.target===e.currentTarget) setModal(false); }}>
          <div style={{
            background:uiBg, border:`2px solid ${uiAccent}`,
            padding:24, width:320,
            fontFamily:'"Courier New",Courier,monospace',
          }}>
            <div style={{ fontSize:18, marginBottom:20, color:uiFg, letterSpacing:'0.08em', borderBottom:`1px solid ${uiBorder}`, paddingBottom:14 }}>
              TRAVERSE PERMIT
            </div>
            <div style={{ fontSize:9, color:uiAccent, letterSpacing:'0.15em', marginBottom:6 }}>DECLARED DESTINATION</div>
            <input autoFocus value={dest}
              onChange={e => setDest(e.target.value)}
              onKeyDown={e => e.key==="Enter" && handleFindRoute()}
              placeholder=""
              style={{
                width:"100%", padding:8, boxSizing:"border-box",
                fontFamily:'"Courier New",Courier,monospace', fontSize:14,
                border:`1px solid ${uiBorder}`,
                background: isNight ? "#162030" : "#dce8dc",
                color:uiFg, marginBottom:20, outline:"none", letterSpacing:'0.05em',
              }}
            />
            <button onClick={handleFindRoute} style={{
              width:"100%", padding:11, background:uiAccent,
              border:"none", color:uiBg,
              fontFamily:'"Courier New",Courier,monospace', fontSize:12,
              cursor:"pointer", marginBottom:8, letterSpacing:'0.18em',
            }}>FILE ROUTE QUERY</button>
            <button onClick={() => setModal(false)} style={{
              width:"100%", padding:9,
              background:"transparent", border:`1px solid ${uiBorder}`,
              color:uiFg,
              fontFamily:'"Courier New",Courier,monospace', fontSize:11, cursor:"pointer", letterSpacing:'0.15em',
            }}>CANCEL</button>
          </div>
        </div>
      )}
      {}
      {reportModal && (
        <div style={{
          position:"absolute", inset:0,
          background: isNight ? "rgba(14,20,14,0.82)" : "rgba(28,40,28,0.58)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:50,
        }} onClick={e => { if(e.target===e.currentTarget) setReportModal(false); }}>
          <div style={{
            background:uiBg, border:`2px solid ${uiAccent}`,
            padding:"20px 20px 16px", width:320,
            fontFamily:'"Courier New",Courier,monospace',
          }}>
            {}
            {reportStep === 'select' && (<>
              <div style={{ fontSize:10, color:uiAccent, letterSpacing:"0.15em", marginBottom:14, opacity:0.9 }}>
                FIELD OBSERVATION CATEGORY
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
                {REPORT_EVENTS.map(ev => (
                  <button key={ev.id}
                    onClick={() => { setReportEvent(ev); setReportChoices({}); setReportNotes(''); setReportStep('form'); }}
                    style={{
                      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                      gap:5, padding:"10px 4px",
                      background:"transparent", border:`1px solid ${uiBorder}`,
                      color:uiFg, cursor:"pointer",
                      transition:"border-color 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor=uiAccent}
                    onMouseLeave={e => e.currentTarget.style.borderColor=uiBorder}
                  >
                    <span style={{ fontSize:20, lineHeight:1, fontFamily:'"Courier New","Lucida Console","Consolas",monospace', fontStyle:"normal" }}>{ev.icon}</span>
                    <span style={{ fontSize:8, letterSpacing:"0.08em", textAlign:"center", lineHeight:1.3, opacity:0.8 }}>
                      {ev.label.toUpperCase()}
                    </span>
                  </button>
                ))}
              </div>
              <button onClick={() => setReportModal(false)} style={{
                width:"100%", padding:"7px 0",
                background:"transparent", border:`1px solid ${uiBorder}`,
                color:uiFg, fontFamily:'"Courier New",Courier,monospace',
                fontSize:11, cursor:"pointer", letterSpacing:"0.1em",
              }}>CANCEL</button>
            </>)}
            {}
            {reportStep === 'form' && reportEvent && (<>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                <span style={{ fontSize:22, fontFamily:'"Courier New","Lucida Console","Consolas",monospace', fontStyle:"normal", lineHeight:1 }}>{reportEvent.icon}</span>
                <div>
                  <div style={{ fontSize:9, color:uiAccent, letterSpacing:"0.15em", marginBottom:2 }}>OBSERVATION TYPE</div>
                  <div style={{ fontSize:12, color:uiFg, letterSpacing:"0.05em" }}>{reportEvent.label.toUpperCase()}</div>
                </div>
              </div>
              {}
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:9, color:uiAccent, letterSpacing:"0.12em", marginBottom:6 }}>
                  {reportEvent.q1.prompt.toUpperCase()}
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  {reportEvent.q1.options.map(opt => (
                    <button key={opt} onClick={() => setReportChoices(p => ({ ...p, q1: opt }))}
                      style={{
                        flex:1, padding:"6px 4px", fontSize:10, letterSpacing:"0.08em",
                        background: reportChoices.q1===opt ? uiAccent : "transparent",
                        border:`1px solid ${reportChoices.q1===opt ? uiAccent : uiBorder}`,
                        color: reportChoices.q1===opt ? uiBg : uiFg,
                        fontFamily:'"Courier New",Courier,monospace', cursor:"pointer",
                      }}>{opt.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              {}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:9, color:uiAccent, letterSpacing:"0.12em", marginBottom:6 }}>
                  {reportEvent.q2.prompt.toUpperCase()}
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                  {reportEvent.q2.options.map(opt => (
                    <button key={opt} onClick={() => setReportChoices(p => ({ ...p, q2: opt }))}
                      style={{
                        padding:"5px 8px", fontSize:10, letterSpacing:"0.08em",
                        background: reportChoices.q2===opt ? uiAccent : "transparent",
                        border:`1px solid ${reportChoices.q2===opt ? uiAccent : uiBorder}`,
                        color: reportChoices.q2===opt ? uiBg : uiFg,
                        fontFamily:'"Courier New",Courier,monospace', cursor:"pointer",
                      }}>{opt.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              {}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:9, color:uiAccent, letterSpacing:"0.12em", marginBottom:6 }}>NOTES</div>
                <input autoFocus value={reportNotes}
                  onChange={e => setReportNotes(e.target.value)}
                  onKeyDown={e => { if(e.key==='Enter') { setReportModal(false); setReportStep('placing'); }}}
                  placeholder="describe what you observed..."
                  style={{
                    width:"100%", padding:"7px 8px", boxSizing:"border-box",
                    fontFamily:'"Courier New",Courier,monospace', fontSize:12,
                    border:`1px solid ${uiBorder}`,
                    background: isNight ? "#162030" : "#dce8dc",
                    color:uiFg, outline:"none",
                  }}
                />
              </div>
              <button onClick={() => { setReportModal(false); setReportStep('placing'); }} style={{
                width:"100%", padding:"9px 0", background:uiAccent,
                border:"none", color:uiBg,
                fontFamily:'"Courier New",Courier,monospace', fontSize:11,
                cursor:"pointer", marginBottom:7, letterSpacing:"0.15em",
              }}>FILE REPORT ›</button>
              <button onClick={() => setReportStep('select')} style={{
                width:"100%", padding:"7px 0",
                background:"transparent", border:`1px solid ${uiBorder}`,
                color:uiFg, fontFamily:'"Courier New",Courier,monospace',
                fontSize:11, cursor:"pointer", letterSpacing:"0.1em",
              }}>← BACK</button>
            </>)}
          </div>
        </div>
      )}
      {}
      <div style={{
        position: 'absolute', bottom: 8, left: 10,
        fontFamily: '"Courier New",Courier,monospace',
        fontSize: 7, letterSpacing: '0.14em',
        color: uiAccent, opacity: 0.5,
        zIndex: 20, pointerEvents: 'none',
      }}>
        SESSION {sessionHex} · LICENSE {LICENSE_HEX} · SURVEY ACTIVE
      </div>
      </div>
    </div>
  );
}
